import * as i0 from '@angular/core';
import { Directive, Inject, Input, Injectable, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, ViewChild, PLATFORM_ID, Output, NgModule } from '@angular/core';
import { throwError, lastValueFrom, Subject, BehaviorSubject, interval, takeUntil } from 'rxjs';
import * as i1$1 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogConfig, MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS, MatDialogActions } from '@angular/material/dialog';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatProgressBar, MatProgressBarModule } from '@angular/material/progress-bar';
import { catchError, timeout, takeUntil as takeUntil$1 } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i1$2 from '@angular/material/bottom-sheet';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import * as i1$3 from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import * as i1$4 from '@angular/router';
import { DecimalPipe, isPlatformBrowser, NgClass, SlicePipe, NgTemplateOutlet, CommonModule } from '@angular/common';
import * as i3 from '@angular/forms';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinner, MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormField, MatLabel, MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatRadioButton, MatRadioModule } from '@angular/material/radio';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionModule } from '@angular/material/expansion';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';

class DeferImgLoadDirective {
    _element;
    platformService;
    appConfig;
    environment;
    imageSrc;
    defaultImage;
    _intersectionObserver;
    _isDeferImageLoaded;
    constructor(_element, platformService, appConfig, environment) {
        this._element = _element;
        this.platformService = platformService;
        this.appConfig = appConfig;
        this.environment = environment;
    }
    ngOnChanges(changes) {
        const newImageSrc = changes.imageSrc?.currentValue;
        if (newImageSrc) {
            if (this._isDeferImageLoaded) {
                this._element.nativeElement.src = newImageSrc;
            }
            else {
                this.imageSrc = newImageSrc;
            }
        }
    }
    ngAfterViewInit() {
        this._element.nativeElement.src = this.appConfig.static_text.preloadImage;
        if (this.platformService.isBrowser()) {
            if (!('IntersectionObserver' in window)) {
                this._element.nativeElement.src = this.imageSrc;
            }
            else if (this.imageSrc) {
                this._intersectionObserver = new IntersectionObserver(this.checkForIntersection.bind(this), {
                    rootMargin: '25px',
                    threshold: 0.01
                });
                this._intersectionObserver.POLL_INTERVAL = 100; // required for unsupported browsers to work with the polyfill
                this._intersectionObserver.observe(this._element.nativeElement);
            }
        }
    }
    checkForIntersection(entries) {
        entries.forEach((entry) => {
            if (this.checkIfIntersecting(entry)) {
                this._element.nativeElement.src = this.imageSrc;
                this._isDeferImageLoaded = true;
                this._intersectionObserver.unobserve(this._element.nativeElement);
                this._intersectionObserver.disconnect();
            }
        });
    }
    checkIfIntersecting(entry) {
        return entry.isIntersecting && entry.target === this._element.nativeElement;
    }
    loadDefaultImage() {
        if (this.defaultImage) {
            this._element.nativeElement.src = this.environment.DEPLOY_URL + this.defaultImage;
        }
        else {
            this._element.nativeElement.src = this.environment.DEPLOY_URL + '/assets/images/missing.png';
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeferImgLoadDirective, deps: [{ token: i0.ElementRef }, { token: "app-platform-service" }, { token: "app-config" }, { token: 'app-environment' }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: DeferImgLoadDirective, isStandalone: true, selector: "[libDeferImgLoad]", inputs: { imageSrc: "imageSrc", defaultImage: "defaultImage" }, host: { listeners: { "error": "loadDefaultImage()" } }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DeferImgLoadDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[libDeferImgLoad]',
                    host: {
                        '(error)': 'loadDefaultImage()'
                    },
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-platform-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-config"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-environment']
                }] }], propDecorators: { imageSrc: [{
                type: Input
            }], defaultImage: [{
                type: Input
            }] } });

class ApiService {
    environment;
    storageService;
    gtmService;
    http;
    baseUrl;
    paymentUrl;
    paymentAccountUrl;
    constructor(environment, storageService, gtmService, http) {
        this.environment = environment;
        this.storageService = storageService;
        this.gtmService = gtmService;
        this.http = http;
        this.baseUrl = this.environment.c3pServiceUrl;
        this.paymentUrl = "/payment/client";
        this.paymentAccountUrl = "/payment_accounts/client";
    }
    getHeaders(headers = {}) {
        const defaultHeaders = {
            Authorization: "Bearer " + this.storageService.get("session-id")
        };
        return { ...defaultHeaders, ...headers };
    }
    getParams(params = {}) {
        const defaultParams = {
            channel: "web"
        };
        return { ...defaultParams, ...params };
    }
    getBody(body = {}) {
        const defaultBody = {
            channel: "web"
        };
        return { ...defaultBody, ...body };
    }
    formatErrors(error) {
        return throwError(error);
    }
    failureBranchEvent(error, gateway) {
        this.gtmService.logPaymentEvent("payment_timeout", gateway);
        return this.formatErrors(error);
    }
    get(path, params = {}, headers = {}) {
        return this.http
            .get(this.baseUrl + path, {
            params: this.getParams(params),
            headers: this.getHeaders(headers),
        })
            .pipe(catchError(this.formatErrors));
    }
    put(path, body = {}, headers = {}) {
        return this.http
            .put(this.baseUrl + path, this.getBody(body), {
            headers: this.getHeaders(headers),
        })
            .pipe(catchError(this.formatErrors));
    }
    post(path, body = {}, headers = {}) {
        return this.http
            .post(this.baseUrl + path, this.getBody(body), {
            headers: this.getHeaders(headers),
        })
            .pipe(catchError(this.formatErrors));
    }
    paytmPost(path, body = {}) {
        const timeoutDuration = 10000; // 10 seconds
        return this.http
            .post(path, this.getBody(body), {
            headers: {
                "Content-Type": "application/json",
            },
        })
            .pipe(timeout(timeoutDuration), catchError(this.formatErrors));
    }
    newPost(urlType, gateway, requestType, body = {}, headers = {}, version = "") {
        const path = urlType === "account" ? this.paymentAccountUrl : this.paymentUrl;
        const fullUrl = `${this.baseUrl}${version ? `/${version}` : ""}${path}`;
        const requestBody = {
            gateway,
            request_type: requestType,
            payload: this.getBody(body)
        };
        const requestHeaders = this.getHeaders(headers);
        return this.http
            .post(fullUrl, requestBody, { headers: requestHeaders })
            .pipe(catchError((error) => this.failureBranchEvent(error, gateway)));
    }
    delete(path, headers = {}) {
        return this.http
            .delete(this.baseUrl + path, {
            headers: this.getHeaders(headers),
        })
            .pipe(catchError(this.formatErrors));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ApiService, deps: [{ token: "app-environment" }, { token: "app-storage-service" }, { token: "app-gtm-service" }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ApiService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: i1.HttpClient }] });

class PaymentPendingComponent {
    dialogRef;
    api;
    handlePaymentService;
    data;
    environment;
    option;
    method;
    merchandOrderId;
    transactionTTL;
    remainingTime;
    lastApiResponse = {};
    lottieUrl;
    constructor(dialogRef, api, handlePaymentService, data, environment) {
        this.dialogRef = dialogRef;
        this.api = api;
        this.handlePaymentService = handlePaymentService;
        this.data = data;
        this.environment = environment;
        this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/pending`;
    }
    ngOnInit() {
        const { merchant_id, option, method, payment_pending_ttl } = this.data;
        this.option = option;
        this.method = method;
        this.merchandOrderId = merchant_id;
        this.remainingTime = payment_pending_ttl;
    }
    ngAfterViewInit() {
        this.startInterval();
        this.transactionTTL = setInterval(() => {
            if (this.remainingTime > 0) {
                this.initTransactionStatus();
            }
            else {
                this.cancelTimer();
            }
        }, 5000);
    }
    startInterval() {
        const interval = setInterval(() => {
            this.remainingTime -= 1;
            if (this.remainingTime === 0) {
                clearInterval(interval);
                this.cancelTimer();
            }
        }, 1000);
    }
    initTransactionStatus() {
        lastValueFrom(this.api.get("/payment/transaction_status/" + this.merchandOrderId))
            .then((response) => {
            this.lastApiResponse = response.data;
            const { tracking_id, merchant_order_id, order_confirmation_window } = response;
            if (response.status === "SUCCESS") {
                this.handlePaymentService.handlePaymentSuccess(this.option, {
                    tracking_id,
                    merchant_order_id,
                    order_confirmation_window
                });
                this.cancelTimer();
            }
            else if (response.status === "FAILED") {
                this.handlePaymentService.handlePaymentError(this.option, response.message);
                this.cancelTimer();
            }
        }).catch((e) => {
            console.log(e);
        });
    }
    cancelTimer() {
        clearInterval(this.transactionTTL);
        this.close();
    }
    close() {
        this.dialogRef.close({ context: this.lastApiResponse });
    }
    ngOnDestroy() {
        clearInterval(this.transactionTTL);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentPendingComponent, deps: [{ token: i1$1.MatDialogRef }, { token: ApiService }, { token: HandlePaymentService }, { token: MAT_DIALOG_DATA }, { token: "app-environment" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: PaymentPendingComponent, isStandalone: true, selector: "lib-payment-pending", ngImport: i0, template: "<div class=\"wrapper\">\n    <dotlottie-player\n            autoplay=true\n            loop=true\n            class=\"confetti-container\"\n            src=\"{{lottieUrl}}\"\n        >\n        </dotlottie-player>\n    <span class=\"processing-payment\">Processing Payment...</span>\n    <span class=\"info\">THIS WON\u2019T TAKE MORE THAN\n        <span class=\"timer\">{{ remainingTime }}s</span>\n    </span>\n</div>\n", styles: [".wrapper{position:relative;display:flex;align-items:center;flex-direction:column;gap:10px;background-repeat:no-repeat;background-size:100% 100%;padding:50px 0;background-image:url(/assets/images/payment_pending_desktop.png)}.confetti-container{height:200px;width:200px}.processing-payment{font-weight:800;font-size:24px;line-height:30px;letter-spacing:.02em}.info{font-weight:300;font-size:16px;line-height:20px;letter-spacing:.02em}.timer{color:var(--primary-color);font-weight:800;font-size:16px;line-height:22px}@media screen and (max-width: 991px){.wrapper{height:100vh;padding:150px 0;background-image:url(/assets/images/payment_pending.png)}.processing-payment{font-weight:700;font-size:20px;line-height:25px}.info{font-size:12px;line-height:15px}}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentPendingComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-payment-pending", standalone: true, schemas: [CUSTOM_ELEMENTS_SCHEMA], template: "<div class=\"wrapper\">\n    <dotlottie-player\n            autoplay=true\n            loop=true\n            class=\"confetti-container\"\n            src=\"{{lottieUrl}}\"\n        >\n        </dotlottie-player>\n    <span class=\"processing-payment\">Processing Payment...</span>\n    <span class=\"info\">THIS WON\u2019T TAKE MORE THAN\n        <span class=\"timer\">{{ remainingTime }}s</span>\n    </span>\n</div>\n", styles: [".wrapper{position:relative;display:flex;align-items:center;flex-direction:column;gap:10px;background-repeat:no-repeat;background-size:100% 100%;padding:50px 0;background-image:url(/assets/images/payment_pending_desktop.png)}.confetti-container{height:200px;width:200px}.processing-payment{font-weight:800;font-size:24px;line-height:30px;letter-spacing:.02em}.info{font-weight:300;font-size:16px;line-height:20px;letter-spacing:.02em}.timer{color:var(--primary-color);font-weight:800;font-size:16px;line-height:22px}@media screen and (max-width: 991px){.wrapper{height:100vh;padding:150px 0;background-image:url(/assets/images/payment_pending.png)}.processing-payment{font-weight:700;font-size:20px;line-height:25px}.info{font-size:12px;line-height:15px}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.MatDialogRef }, { type: ApiService }, { type: HandlePaymentService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }] });

class TransactionInterruptedBottomSheetComponent {
    paymentService;
    bottomSheetRef;
    injector;
    data;
    deviceService;
    environment;
    gtmService;
    context;
    dialogRef;
    isMobile;
    actionButtons;
    constructor(paymentService, bottomSheetRef, injector, data, deviceService, environment, gtmService) {
        this.paymentService = paymentService;
        this.bottomSheetRef = bottomSheetRef;
        this.injector = injector;
        this.data = data;
        this.deviceService = deviceService;
        this.environment = environment;
        this.gtmService = gtmService;
        this.isMobile = this.deviceService.isMobile();
        if (!this.isMobile) {
            this.dialogRef = this.injector.get(MatDialogRef, null);
            this.data = this.injector.get(MAT_DIALOG_DATA, null);
        }
    }
    ngOnInit() {
        this.context = this.data.context;
        this.actionButtons = this.data.context.action_buttons;
    }
    handleClick(type) {
        if (type === "retry") {
            this.gtmService.logEvent("payment_pending", {
                event_label: "Retry",
                event_value: this.data.option.code
            });
            this.paymentService.pay(this.data.method, this.data.option);
        }
        else {
            this.gtmService.logEvent("payment_pending", {
                event_label: "Change Method",
                event_value: this.data.option.code
            });
        }
        this.close();
    }
    close() {
        if (!this.isMobile) {
            this.dialogRef.close();
        }
        else {
            this.bottomSheetRef.dismiss();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TransactionInterruptedBottomSheetComponent, deps: [{ token: PaymentService }, { token: i1$2.MatBottomSheetRef }, { token: i0.Injector }, { token: MAT_BOTTOM_SHEET_DATA }, { token: "app-device-service" }, { token: "app-environment" }, { token: "app-gtm-service" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: TransactionInterruptedBottomSheetComponent, isStandalone: true, selector: "lib-transaction-interrupted-bottom-sheet", ngImport: i0, template: "<div class=\"wrapper\">\n  <img src=\"{{ environment.DEPLOY_URL }}/assets/images/payment-interupt.svg\" style=\"max-height: 100px; width: 100%\" />\n  <span class=\"trans-interrupt\">Transaction Interrupted</span>\n  <span class=\"sub-text\">{{ context.sub_text }}</span>\n\n  <button class=\"try-again-btn\" (click)=\"handleClick(actionButtons[0].type)\" >\n    {{ actionButtons[0].text }}\n  </button>\n\n  @if (actionButtons[1]) {\n    <button class=\"change-method-btn\" (click)=\"handleClick(actionButtons[1].type)\" >\n      {{ actionButtons[1].text }}\n    </button>\n  }\n</div>\n", styles: [".wrapper{padding:30px 70px;display:flex;flex-direction:column;height:max-content;justify-content:center;align-items:center;gap:15px}.trans-interrupt{font-weight:700;font-size:24px;line-height:30px;text-align:center;color:#282c3f}.try-again-btn{display:flex;flex-direction:row;justify-content:center;align-items:center;background:var(--primary-color);border-radius:4px;width:343px;font-size:16px;font-weight:700;line-height:20px;height:48px;color:#fff}.sub-text{font-weight:500;font-size:16px;line-height:125%;text-align:center;letter-spacing:.02em;width:100%;color:#7c8495}.change-method-btn{font-weight:800;font-size:16px;letter-spacing:.02em;color:#7c8495;height:21px}@media screen and (max-width: 991px){.wrapper{padding:45px 16px 24px;display:flex;flex-direction:column;height:max-content;justify-content:center;gap:10px}.trans-interrupt{font-size:20px;line-height:25px}.try-again-btn{width:calc(100vw - 32px)}.sub-text{font-size:14px}.change-method-btn{width:calc(100vw - 32px)}}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TransactionInterruptedBottomSheetComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-transaction-interrupted-bottom-sheet", standalone: true, template: "<div class=\"wrapper\">\n  <img src=\"{{ environment.DEPLOY_URL }}/assets/images/payment-interupt.svg\" style=\"max-height: 100px; width: 100%\" />\n  <span class=\"trans-interrupt\">Transaction Interrupted</span>\n  <span class=\"sub-text\">{{ context.sub_text }}</span>\n\n  <button class=\"try-again-btn\" (click)=\"handleClick(actionButtons[0].type)\" >\n    {{ actionButtons[0].text }}\n  </button>\n\n  @if (actionButtons[1]) {\n    <button class=\"change-method-btn\" (click)=\"handleClick(actionButtons[1].type)\" >\n      {{ actionButtons[1].text }}\n    </button>\n  }\n</div>\n", styles: [".wrapper{padding:30px 70px;display:flex;flex-direction:column;height:max-content;justify-content:center;align-items:center;gap:15px}.trans-interrupt{font-weight:700;font-size:24px;line-height:30px;text-align:center;color:#282c3f}.try-again-btn{display:flex;flex-direction:row;justify-content:center;align-items:center;background:var(--primary-color);border-radius:4px;width:343px;font-size:16px;font-weight:700;line-height:20px;height:48px;color:#fff}.sub-text{font-weight:500;font-size:16px;line-height:125%;text-align:center;letter-spacing:.02em;width:100%;color:#7c8495}.change-method-btn{font-weight:800;font-size:16px;letter-spacing:.02em;color:#7c8495;height:21px}@media screen and (max-width: 991px){.wrapper{padding:45px 16px 24px;display:flex;flex-direction:column;height:max-content;justify-content:center;gap:10px}.trans-interrupt{font-size:20px;line-height:25px}.try-again-btn{width:calc(100vw - 32px)}.sub-text{font-size:14px}.change-method-btn{width:calc(100vw - 32px)}}\n"] }]
        }], ctorParameters: () => [{ type: PaymentService }, { type: i1$2.MatBottomSheetRef }, { type: i0.Injector }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_BOTTOM_SHEET_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }] });

class MakePaymentButtonLoaderService {
    inProgress = new EventEmitter();
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MakePaymentButtonLoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MakePaymentButtonLoaderService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MakePaymentButtonLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class HeaderNotificationService {
    notifyMessage = new EventEmitter();
    constructor() { }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderNotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderNotificationService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderNotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: () => [] });

class HandlePaymentService {
    dialog;
    bottomSheet;
    apiService;
    buttonLoader;
    headerNotificationService;
    storageService;
    deviceService;
    gtmService;
    isMobile = false;
    redirectedToConfirmPage = false;
    paymentPendingTtl;
    paymentPendingTtl$ = new Subject();
    resetSelection$ = new EventEmitter();
    constructor(dialog, bottomSheet, apiService, buttonLoader, headerNotificationService, storageService, deviceService, gtmService) {
        this.dialog = dialog;
        this.bottomSheet = bottomSheet;
        this.apiService = apiService;
        this.buttonLoader = buttonLoader;
        this.headerNotificationService = headerNotificationService;
        this.storageService = storageService;
        this.deviceService = deviceService;
        this.gtmService = gtmService;
        this.isMobile = this.deviceService.isMobile();
        this.paymentPendingTtl$.subscribe((ttl) => {
            this.paymentPendingTtl = ttl;
        });
    }
    openPaymentPendingModal(merchant_id, option, method) {
        this.gtmService.logEvent("payment_pending", {
            event_label: "Open Payment Pending",
            event_value: option.code
        });
        this.dialog
            .open(PaymentPendingComponent, {
            disableClose: true,
            data: {
                merchant_id,
                option,
                method,
                payment_pending_ttl: this.paymentPendingTtl
            },
            panelClass: "payment-pending-dialog"
        })
            .afterClosed()
            .subscribe((response) => {
            if (response?.context && Object.keys(response.context).length) {
                this.gtmService.logEvent("payment_pending", {
                    event_label: "Open Transaction Interrupt Bottom Sheet",
                    event_value: option.code
                });
                const data = {
                    context: response.context,
                    option,
                    method
                };
                if (this.isMobile) {
                    this.bottomSheet.open(TransactionInterruptedBottomSheetComponent, {
                        panelClass: "bottom-sheet-wrapper",
                        data
                    });
                }
                else {
                    this.dialog.open(TransactionInterruptedBottomSheetComponent, {
                        panelClass: "desktop-transaction-interrupt-dialog",
                        data
                    });
                }
            }
            else {
                this.headerNotificationService.notifyMessage.emit("Something went wrong! Please try again.");
            }
            this.buttonLoader.inProgress.emit(false);
        });
    }
    handlePaymentSuccess(option, queryParams) {
        this.redirectToConfirmPage(queryParams);
        this.gtmService.logPaymentEvent("payment_success", option.code);
    }
    handlePaymentError(option, errorMessage = "Payment Failed") {
        this.headerNotificationService.notifyMessage.emit(errorMessage);
        this.gtmService.logPaymentEvent("payment_failure", option.code);
    }
    redirectToConfirmPage(queryParams) {
        // loader needed, since window.location.href takes few seconds, before navigation
        this.buttonLoader.inProgress.emit(true);
        const { tracking_id, merchant_order_id, order_confirmation_window } = queryParams;
        if (!tracking_id || !merchant_order_id || !order_confirmation_window) {
            this.gtmService.logPaymentEvent("popup_data", `Source: redirect, Tracking ID: ${tracking_id}, Merchant Order ID: ${merchant_order_id}, Order Confirmation Window: ${order_confirmation_window}`);
        }
        window.location.href = `/confirm_page?tracking_id=${tracking_id}&merchant_order_id=${merchant_order_id}&order_confirmation_window=${order_confirmation_window}`;
    }
    addMoney(option, mandatoryParams, extraParams = {}, flow_type) {
        extraParams = { ...extraParams, flow_type };
        const params = this._buildParams(mandatoryParams, extraParams);
        this.buttonLoader.inProgress.emit(true);
        return this.apiService.newPost("payment", option.gateway, "add_money", params, {}, "v2");
    }
    initiatePayment(option, mandatoryParams, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        const params = this._buildParams(mandatoryParams, extraParams);
        return this.apiService.newPost("payment", option.gateway, "initiate_payment", params, {}, "v2");
    }
    // for cards
    initiatePaymentV3(option, mandatoryParams, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = {
            ...extraParams,
            flow_type,
            Accept: "application/vnd.phpl.v2"
        };
        const params = this._buildParams(mandatoryParams, extraParams);
        return this.apiService.newPost("payment", option.gateway, "initiate_payment_v3", params, {}, "v2");
    }
    checkLinking(option, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        return this.apiService.newPost("account", option.gateway, "check_linking", extraParams);
    }
    fetchSodexoCards(option, extraParams = {}, flow_type) {
        return this.checkLinking(option, extraParams, flow_type);
    }
    fetchPaytmCards(option, extraParams = {}, flow_type) {
        return this.checkLinkingV2(option, extraParams, flow_type);
    }
    checkLinkingV2(option, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = {
            ...extraParams,
            flow_type,
            Accept: "application/vnd.phpl.v2"
        };
        return this.apiService.newPost("account", option.gateway, "check_linking_v2", extraParams);
    }
    unlink(option, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        return this.apiService.newPost("account", option.gateway, "unlink", extraParams);
    }
    link(option, extraParams = {}, flow_type) {
        extraParams = { ...extraParams, flow_type };
        return this.apiService.newPost("account", option.gateway, "link", extraParams);
    }
    initiateLinking(option, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        const params = {
            ...extraParams,
            session_id: this.storageService.get("session-id")
        };
        return this.apiService.newPost("account", option.gateway, "initiate_linking", params);
    }
    validateLinking(option, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        const params = {
            ...extraParams,
            session_id: this.storageService.get("session-id")
        };
        return this.apiService.newPost("account", option.gateway, "validate_linking", params);
    }
    debit(option, mandatoryParams, extraParams = {}, flow_type) {
        this.buttonLoader.inProgress.emit(true);
        extraParams = { ...extraParams, flow_type };
        const params = this._buildParams(mandatoryParams, extraParams);
        return this.apiService.newPost("payment", option.gateway, "debit", params, {}, "v2");
    }
    validateOffer(option, body) {
        return this.apiService
            .newPost("account", option.gateway, "validate_offer", body)
            .toPromise();
    }
    _buildParams(mandatoryParams, extraParams) {
        const { address_id, currency_redeem, outlet_service_type, order_time, payment_option_id } = mandatoryParams;
        return {
            order_time,
            address_id,
            remark: "NA",
            checksum: mandatoryParams.options_payload.checksum,
            ...mandatoryParams.getCashOrderId(),
            currency_redeem,
            payment_option_id,
            outlet_service_type,
            ...extraParams
        };
    }
    getGateway(paymentOptionsGateways, code) {
        return paymentOptionsGateways[code] || null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HandlePaymentService, deps: [{ token: i1$1.MatDialog }, { token: i1$2.MatBottomSheet }, { token: ApiService }, { token: MakePaymentButtonLoaderService }, { token: HeaderNotificationService }, { token: "app-storage-service" }, { token: "app-device-service" }, { token: "app-gtm-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HandlePaymentService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HandlePaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: i1$1.MatDialog }, { type: i1$2.MatBottomSheet }, { type: ApiService }, { type: MakePaymentButtonLoaderService }, { type: HeaderNotificationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }] });

class UpiPaymentService {
    handlePaymentService;
    api;
    headerNotificationService;
    option;
    customerPhone;
    merchantTxnId;
    remainingTime;
    customerVpa;
    transactionTTL;
    isValid = new BehaviorSubject(false);
    vpaValidationResponse = new BehaviorSubject(null);
    transactionTimer;
    constructor(handlePaymentService, api, headerNotificationService) {
        this.handlePaymentService = handlePaymentService;
        this.api = api;
        this.headerNotificationService = headerNotificationService;
    }
    init(response, option) {
        this.option = option;
        this.merchantTxnId = response.merchant_order_id;
        this.transactionTTL = response.data.ttl;
        this.remainingTime = response.data.ttl;
        this.initTransactionTimer();
        this.schedulePaymentStatusCheck(20 * 1000);
    }
    initTransactionTimer() {
        this.transactionTimer = setInterval(() => {
            if (this.remainingTime > 0) {
                this.remainingTime--;
            }
            else {
                this.cancelTimer();
            }
        }, 1000);
    }
    cancelTimer() {
        this.transactionTimer = 0;
        this.remainingTime = 0;
        clearInterval(this.transactionTimer);
    }
    schedulePaymentStatusCheck(time) {
        if (this.remainingTime > 0) {
            setTimeout(() => {
                this.checkPaymentStatus(this.merchantTxnId);
            }, time);
        }
    }
    checkPaymentStatus(merchant_order_id) {
        lastValueFrom(this.api.newPost("payment", this.option.gateway, "validate_payment", {
            flow_type: "s2s",
            merchant_order_id
        }, {}, "v2")).then((response) => {
            const paymentStatus = response.status.toLowerCase();
            if (response.success) {
                if (paymentStatus === "success") {
                    const { tracking_id, merchant_order_id, order_confirmation_window, } = response;
                    this.handlePaymentService.redirectToConfirmPage({
                        tracking_id,
                        merchant_order_id,
                        order_confirmation_window
                    });
                }
            }
            else {
                if (paymentStatus === "failed") {
                    this.cancelTimer();
                    this.headerNotificationService.notifyMessage.emit(response.message);
                }
                if (paymentStatus === "pending") {
                    this.schedulePaymentStatusCheck(5000);
                }
            }
        }).catch(() => {
            this.cancelTimer();
            this.headerNotificationService.notifyMessage.emit("Something went wrong! Please try again.");
        });
    }
    validateVpa(gateway, customer_vpa) {
        const body = {
            flow_type: "s2s",
            customer_vpa
        };
        this.customerVpa = customer_vpa;
        return this.api.newPost("account", gateway, "check_linking", body);
    }
    deleteSavedVpa(option) {
        const body = {
            upi_id: option.saved_information.saved_vpa,
            channel: "web",
        };
        return this.handlePaymentService.unlink(option, body, "s2s");
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentService, deps: [{ token: HandlePaymentService }, { token: ApiService }, { token: HeaderNotificationService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: HandlePaymentService }, { type: ApiService }, { type: HeaderNotificationService }] });

class UpiPaymentTimerModalComponent {
    data;
    appConfig;
    gtmService;
    upiPayment;
    dialogRef;
    remainingTimeInPercentage = 0;
    transactionTimer;
    constructor(data, appConfig, gtmService, upiPayment, dialogRef) {
        this.data = data;
        this.appConfig = appConfig;
        this.gtmService = gtmService;
        this.upiPayment = upiPayment;
        this.dialogRef = dialogRef;
    }
    ngOnInit() {
        this.checkRemainingTime();
    }
    ngOnDestroy() {
        clearInterval(this.transactionTimer);
        this.upiPayment.cancelTimer();
    }
    checkRemainingTime() {
        this.transactionTimer = setInterval(() => {
            this.remainingTimeInPercentage = ((this.upiPayment.transactionTTL - this.upiPayment.remainingTime) / this.upiPayment.transactionTTL) * 100;
            if (this.remainingTimeInPercentage === 100 || this.upiPayment.transactionTimer === 0) {
                this.cancelTransaction(true);
            }
        }, 1000);
    }
    cancelTransaction(timeFinished = false) {
        if (!timeFinished) {
            this.gtmService.logPaymentEvent("collect_payment_cancelled", this.data.option.code);
        }
        this.upiPayment.cancelTimer();
        this.close();
    }
    close() {
        this.dialogRef.close();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentTimerModalComponent, deps: [{ token: MAT_DIALOG_DATA }, { token: 'app-config' }, { token: "app-gtm-service" }, { token: UpiPaymentService }, { token: i1$1.MatDialogRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: UpiPaymentTimerModalComponent, isStandalone: true, selector: "lib-upi-payment-timer-modal", ngImport: i0, template: "<div class=\"content\">\n    <div class=\"text-center\" style=\"margin: 30px 0;\">\n        <img libDeferImgLoad [imageSrc]=\"'/assets/images/logo.png'\" alt=\"\" style=\"max-height:40px\">\n    </div>\n    <div class=\"clearfix\"></div>\n    <div class=\"text-center\">\n        <h2 class=\"in-progress\">Payment in progress...</h2>\n        <span class=\"warning\">Please don't close this window!</span>\n        <h2 class=\"steps-heading\">Steps to follow:</h2>\n        <ul class=\"steps-to-follow\">\n            <li>1. Accept the collect request of <span>&#8377; {{data.final_price}} </span> from\n                {{ appConfig.static_text.brand_name }} on your {{data.label}} App</li>\n            <li>2. Enter MPIN to authenticate and pay</li>\n            <li>3. Your order will be placed after successful payment</li>\n        </ul>\n    </div>\n    <div class=\"clearfix\"></div>\n    <div class=\"space-50\"></div>\n    <mat-progress-bar mode=\"determinate\" [value]=\"remainingTimeInPercentage\"></mat-progress-bar>\n    <div class=\"space-30\"></div>\n    <div class=\"clearfix\"></div>\n    <div class=\"col-sm-12 text-center\">\n        <button type=\"button\" mat-flat-button color=\"primary\" class=\"radius-3\" (click)=\"cancelTransaction()\">\n            Cancel\n        </button>\n    </div>\n</div>", styles: [".content{max-width:600px;margin:auto;max-height:100%}.in-progress{font-size:18px;margin:0 0 15px}.warning{color:#777;display:block;font-weight:300;font-size:12px}.steps-heading{font-size:20px;font-weight:700;margin-top:80px;margin-bottom:30px;text-align:left;-webkit-font-smoothing:antialiased}.steps-to-follow{margin:0;padding:0;list-style:none;text-align:left}.steps-to-follow li{margin-bottom:12px}.steps-to-follow li span{font-weight:700;padding:0 2px;color:var(--primary-color);-webkit-font-smoothing:antialiased}.steps-to-follow li:last-child{margin-bottom:0}\n"], dependencies: [{ kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }, { kind: "component", type: MatProgressBar, selector: "mat-progress-bar", inputs: ["color", "value", "bufferValue", "mode"], outputs: ["animationEnd"], exportAs: ["matProgressBar"] }, { kind: "component", type: MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentTimerModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-upi-payment-timer-modal', standalone: true, imports: [DeferImgLoadDirective, MatProgressBar, MatButton], template: "<div class=\"content\">\n    <div class=\"text-center\" style=\"margin: 30px 0;\">\n        <img libDeferImgLoad [imageSrc]=\"'/assets/images/logo.png'\" alt=\"\" style=\"max-height:40px\">\n    </div>\n    <div class=\"clearfix\"></div>\n    <div class=\"text-center\">\n        <h2 class=\"in-progress\">Payment in progress...</h2>\n        <span class=\"warning\">Please don't close this window!</span>\n        <h2 class=\"steps-heading\">Steps to follow:</h2>\n        <ul class=\"steps-to-follow\">\n            <li>1. Accept the collect request of <span>&#8377; {{data.final_price}} </span> from\n                {{ appConfig.static_text.brand_name }} on your {{data.label}} App</li>\n            <li>2. Enter MPIN to authenticate and pay</li>\n            <li>3. Your order will be placed after successful payment</li>\n        </ul>\n    </div>\n    <div class=\"clearfix\"></div>\n    <div class=\"space-50\"></div>\n    <mat-progress-bar mode=\"determinate\" [value]=\"remainingTimeInPercentage\"></mat-progress-bar>\n    <div class=\"space-30\"></div>\n    <div class=\"clearfix\"></div>\n    <div class=\"col-sm-12 text-center\">\n        <button type=\"button\" mat-flat-button color=\"primary\" class=\"radius-3\" (click)=\"cancelTransaction()\">\n            Cancel\n        </button>\n    </div>\n</div>", styles: [".content{max-width:600px;margin:auto;max-height:100%}.in-progress{font-size:18px;margin:0 0 15px}.warning{color:#777;display:block;font-weight:300;font-size:12px}.steps-heading{font-size:20px;font-weight:700;margin-top:80px;margin-bottom:30px;text-align:left;-webkit-font-smoothing:antialiased}.steps-to-follow{margin:0;padding:0;list-style:none;text-align:left}.steps-to-follow li{margin-bottom:12px}.steps-to-follow li span{font-weight:700;padding:0 2px;color:var(--primary-color);-webkit-font-smoothing:antialiased}.steps-to-follow li:last-child{margin-bottom:0}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-config']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: UpiPaymentService }, { type: i1$1.MatDialogRef }] });

var PaymentOptionCode;
(function (PaymentOptionCode) {
    PaymentOptionCode["SODEXO"] = "pluxee";
    PaymentOptionCode["AMAZON_PAY"] = "wal106";
    PaymentOptionCode["LAZYPAY"] = "payl104";
    PaymentOptionCode["SIMPL"] = "payl106";
    PaymentOptionCode["PHONE_PAY_UPI"] = "upi101";
    PaymentOptionCode["MOBIKWIK"] = "wal102";
    PaymentOptionCode["UPI102"] = "upi102";
    PaymentOptionCode["GPAY"] = "gpay";
    PaymentOptionCode["PAYTM_APPINVOKE"] = "paytm_appinvoke";
    PaymentOptionCode["CREDPAY"] = "credpay";
    PaymentOptionCode["SAVED_ICICI_UPI"] = "saved_icici_upi";
    PaymentOptionCode["PAYTM_CARD"] = "paytm_card";
    PaymentOptionCode["AMAZON_PAY_LATER"] = "payl108";
})(PaymentOptionCode || (PaymentOptionCode = {}));

var PaymentMethodCode;
(function (PaymentMethodCode) {
    PaymentMethodCode["PREFERRED_PAYMENT"] = "preferred_payment";
    PaymentMethodCode["CARD"] = "card";
    PaymentMethodCode["NETBANK"] = "netbank";
    PaymentMethodCode["CASH"] = "cash";
    PaymentMethodCode["CREDPAY"] = "credpay";
    PaymentMethodCode["UPI"] = "upi";
    PaymentMethodCode["PAYLATER"] = "payl";
})(PaymentMethodCode || (PaymentMethodCode = {}));

var PaymentType;
(function (PaymentType) {
    PaymentType["postOrder"] = "post-order";
    PaymentType["preOrder"] = "pre-order";
})(PaymentType || (PaymentType = {}));

class ToastrService {
    snackBar;
    constructor(snackBar) {
        this.snackBar = snackBar;
    }
    openSnackBar(message, action) {
        this.snackBar.open(message || '', action, { duration: 3000, panelClass: "toast-dialog" });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastrService, deps: [{ token: i1$3.MatSnackBar }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastrService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastrService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$3.MatSnackBar }] });

class UtilityService {
    router;
    toastrService;
    storageService;
    deviceService;
    phoneRegex = /^[6-9]\d{9}$/;
    upiRegex = /^([\\.A-Za-z0-9-])+@([\\.A-Za-z0-9-])+$/;
    isMobile;
    constructor(router, toastrService, storageService, deviceService) {
        this.router = router;
        this.toastrService = toastrService;
        this.storageService = storageService;
        this.deviceService = deviceService;
        this.isMobile = this.deviceService.isMobile();
    }
    setPaymentRedirectedGateway(apiDataModel) {
        const { multi_order_payment } = apiDataModel;
        if (multi_order_payment) {
            this.storageService.set("payment_redirection_gateway", "postOrder");
            return;
        }
        const url = this.router.url;
        let paymentMode = "preOrder";
        if (url.includes("/manage_orders") || url.includes("/live_tracking") || url === "/account") {
            paymentMode = "postOrder";
        }
        this.storageService.set("payment_redirection_gateway", paymentMode);
    }
    handleResponse(response = {}) {
        const message = response.meta ? response.meta.message : (response.message ? response.message : "Something went wrong! Please try again.");
        this.toastrService.openSnackBar(message, "OKAY");
    }
    navigateToManageOrders(urlQueryParams = {}) {
        if (this.isMobile) {
            this.storageService.set("scroll", "true");
            this.router.navigateByUrl('/account');
        }
        else {
            this.router.navigateByUrl('/profile/manage_orders', urlQueryParams);
        }
    }
    allowOnlyNumbers(event) {
        if (event.type === 'keypress') {
            const char = event.key;
            // Allow numbers and Enter -> Enter if user presses enter after entering a number
            if (/^[0-9]$/.test(char) || char === 'Enter') {
                return true;
            }
            // Prevent any other keys
            event.preventDefault();
            return false;
        }
        else if (event.type === 'paste') {
            const pastedData = event.clipboardData.getData('Text');
            // If the pasted data contains any non-numeric character, prevent the paste
            if (/[^0-9]/.test(pastedData)) {
                event.preventDefault();
                return false;
            }
        }
        return true;
    }
    navigateTo(url) {
        this.router.navigateByUrl(url);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UtilityService, deps: [{ token: i1$4.Router }, { token: ToastrService }, { token: 'app-storage-service' }, { token: "app-device-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UtilityService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UtilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: i1$4.Router }, { type: ToastrService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-storage-service']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }] });

const CASH_PAYMENT_MODEL = {
    method: {
        id: "1",
        code: "cash",
        name: "Cash"
    },
    option: {
        id: "52",
        code: "cash",
        name: "Cash"
    }
};
class CashService {
    apiService;
    utilityService;
    handlePaymentService;
    headerNotificationService;
    makePaymentButtonLoaderService;
    constructor(apiService, utilityService, handlePaymentService, headerNotificationService, makePaymentButtonLoaderService) {
        this.apiService = apiService;
        this.utilityService = utilityService;
        this.handlePaymentService = handlePaymentService;
        this.headerNotificationService = headerNotificationService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
    }
    start(apiDataModel) {
        const { address_id, currency_redeem, outlet_service_type, order_time, payment_method, payment_option_id, remark, options_payload } = apiDataModel;
        const body = {
            remark,
            order_time,
            address_id,
            currency_redeem,
            payment_option_id,
            outlet_service_type,
            checksum: options_payload.checksum,
            payment_method: payment_method.name
        };
        lastValueFrom(this.apiService.post('/v2/order/checkout', body))
            .then((response) => {
            if (response?.meta.code === 200) {
                const { tracking_id, merchant_order_id, order_confirmation_window } = response.data;
                this.handlePaymentService.redirectToConfirmPage({
                    tracking_id,
                    merchant_order_id,
                    order_confirmation_window
                });
            }
            else {
                this.headerNotificationService.notifyMessage.emit(response?.meta.message || "Payment Failed");
            }
        }).catch((error) => {
            this.utilityService.handleResponse(error);
        }).finally(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CashService, deps: [{ token: ApiService }, { token: UtilityService }, { token: HandlePaymentService }, { token: HeaderNotificationService }, { token: MakePaymentButtonLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CashService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CashService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: ApiService }, { type: UtilityService }, { type: HandlePaymentService }, { type: HeaderNotificationService }, { type: MakePaymentButtonLoaderService }] });

class PopupServiceService {
    rendererFactory2;
    handlePaymentService;
    buttonLoader;
    gtmService;
    popupBlocked;
    $transactionTimer;
    transaction_id;
    paymentPendingTtl;
    renderer;
    paymentWindow;
    method;
    option;
    redirect_form;
    postMessageResult = new BehaviorSubject(null);
    messageAbortController = null;
    constructor(rendererFactory2, handlePaymentService, buttonLoader, gtmService) {
        this.rendererFactory2 = rendererFactory2;
        this.handlePaymentService = handlePaymentService;
        this.buttonLoader = buttonLoader;
        this.gtmService = gtmService;
        this.renderer = this.rendererFactory2.createRenderer(null, null);
    }
    setupPopup(method, redirect_url, option, merchant_order_id = null, redirect_form = null) {
        this.setupPostMessagePopup(method, redirect_url, option, merchant_order_id, redirect_form);
        this.initiateTransactionTimer();
    }
    initiateTransactionTimer() {
        this.$transactionTimer = interval(1000).subscribe(() => {
            if (this.isPaymentWindowInactive()) {
                this.removeBackdrop();
                this.resetMessageListener();
                this.cancelTransactionTimer();
                this.buttonLoader.inProgress.emit(false);
            }
        });
    }
    cancelTransactionTimer() {
        this.$transactionTimer?.unsubscribe();
    }
    isPaymentWindowInactive() {
        return this.paymentWindow?.closed;
    }
    setupPostMessagePopup(method, redirect_url, option, merchant_order_id, redirect_form) {
        this.transaction_id = merchant_order_id;
        this.method = method;
        this.option = option;
        this.redirect_form = redirect_form;
        this.postMessageResult.next(null);
        this.popupBlocked = false;
        this.removeBackdrop();
        this.createBackdrop("Please complete the process in popup window. If you don't see it, click the link below.", redirect_url, true);
        this.openPaymentWindow(redirect_url);
        this.resetMessageListener();
        this.messageAbortController = new AbortController();
        window.addEventListener("message", (event) => this.messageListener(method, event, option, merchant_order_id), { signal: this.messageAbortController.signal });
    }
    messageListener(method, event, option, merchant_order_id) {
        if (!event.data ||
            event.data.event_referrer !== "poncho") {
            return;
        }
        this.resetMessageListener();
        this.postMessageResult.next(event.data);
        const { status, order_confirmation_window, tracking_id } = event.data;
        this.afterMessageReceivedTasks();
        if (!tracking_id || !merchant_order_id || !order_confirmation_window) {
            this.gtmService.logPaymentEvent("popup_data", `Source: popup, Tracking ID: ${tracking_id}, Merchant Order ID: ${merchant_order_id}, Order Confirmation Window: ${order_confirmation_window}`);
        }
        switch (status) {
            case "SUCCESS":
                this.handlePaymentService.handlePaymentSuccess(option, {
                    tracking_id,
                    merchant_order_id,
                    order_confirmation_window,
                });
                break;
            case "FAILED":
                this.handlePaymentService.handlePaymentError(option, event.data.message);
                break;
            case "PENDING":
                this.handlePaymentService.openPaymentPendingModal(merchant_order_id, option, method);
                break;
        }
    }
    resetMessageListener() {
        this.messageAbortController?.abort();
        this.messageAbortController = null;
    }
    afterMessageReceivedTasks() {
        this.removeBackdrop();
        this.closePaymentWindow();
    }
    closePaymentWindow() {
        window.focus();
        this.paymentWindow?.close();
    }
    removeBackdrop() {
        const element = document.getElementById("payments-backdrop");
        if (element?.parentNode) {
            this.renderer.removeChild(element.parentNode, element);
        }
    }
    createBackdrop(message, redirect_url, shouldCreateLink) {
        const paymentsBackdrop = this.renderer.createElement("div");
        this.renderer.setAttribute(paymentsBackdrop, "id", "payments-backdrop");
        const p = this.renderer.createElement("p");
        const textnode = this.renderer.createText(message);
        this.renderer.appendChild(p, textnode);
        this.renderer.appendChild(paymentsBackdrop, p);
        if (shouldCreateLink) {
            // create link
            const createLink = this.renderer.createElement("a");
            this.renderer.setProperty(createLink, "textContent", "Click to open payment window");
            this.renderer.appendChild(paymentsBackdrop, createLink);
            createLink.addEventListener("click", () => {
                if (!this.paymentWindow) {
                    this.openPaymentWindow(redirect_url);
                }
                else {
                    this.paymentWindow.focus();
                }
            });
        }
        this.renderer.appendChild(document.body, paymentsBackdrop);
    }
    openPaymentWindow(redirect_url) {
        const width = 800;
        const height = 800;
        const pos = {
            x: window.screen.width / 2 - width / 2,
            y: window.screen.height / 2 - height / 2,
        };
        this.paymentWindow = window.open(redirect_url, "paymentWindow", `width=${width},height=${height},left=${pos.x},top=${pos.x}`);
        if (!this.paymentWindow) {
            this.popupBlocked = true;
            this.gtmService.logPaymentEvent("payment_popup", "blocked: " + this.option.code);
        }
        if (this.paymentWindow) {
            if (this.popupBlocked) {
                this.gtmService.logPaymentEvent("payment_popup", "opened: " + this.option.code);
            }
            if (this.option.code === PaymentOptionCode.PAYTM_CARD && this.option.is_saved) {
                this.submitForm(this.option, this.transaction_id, this.redirect_form);
            }
        }
    }
    submitForm(option, transaction_id, redirect_form) {
        try {
            const { actionUrl, content } = redirect_form;
            const formField = [];
            const postData = [];
            for (const key in content) {
                if (content.hasOwnProperty(key)) {
                    formField.push(key.toString());
                    const key_value = decodeURIComponent(content[key]);
                    postData.push(key_value);
                }
            }
            const form = document.createElement("form");
            form.action = actionUrl;
            form.method = "POST";
            for (let j = 0; j < postData.length; j++) {
                const input = document.createElement("input");
                input.type = "hidden";
                input.name = formField[j];
                input.value = postData[j];
                form.appendChild(input);
            }
            this.paymentWindow.document.body.appendChild(form);
            form.submit();
        }
        catch (e) {
            this.afterMessageReceivedTasks();
            this.handlePaymentService.handlePaymentError(option);
            this.gtmService.logPaymentEvent("form_submit_error", transaction_id + ' ' + e);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PopupServiceService, deps: [{ token: i0.RendererFactory2 }, { token: HandlePaymentService }, { token: MakePaymentButtonLoaderService }, { token: "app-gtm-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PopupServiceService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PopupServiceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: i0.RendererFactory2 }, { type: HandlePaymentService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }] });

class AmazonPayService {
    utilityService;
    popupServiceService;
    handlePaymentService;
    makePaymentButtonLoaderService;
    environment;
    gtmService;
    amazonBalance = new BehaviorSubject(null);
    _isSDKLoaded = false;
    isLinked = false;
    showAddandPay = false;
    amazonPayLaterBalance = new BehaviorSubject(null);
    amazonPayLaterEligible$ = new BehaviorSubject(null);
    constructor(utilityService, popupServiceService, handlePaymentService, makePaymentButtonLoaderService, environment, gtmService) {
        this.utilityService = utilityService;
        this.popupServiceService = popupServiceService;
        this.handlePaymentService = handlePaymentService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.environment = environment;
        this.gtmService = gtmService;
    }
    get isSDKLoaded() {
        return this._isSDKLoaded;
    }
    set isSDKLoaded(value) {
        this._isSDKLoaded = value;
    }
    // load amazon sdk
    loadSDK() {
        const _this = this;
        return new Promise((resolve, reject) => {
            let s, r, t;
            r = false;
            s = document.createElement('script');
            s.type = 'text/javascript';
            s.async = true;
            s.id = 'amazon-login-sdk';
            s.src = 'https://assets.loginwithamazon.com/sdk/na/login1.js';
            s.onload = s.onreadystatechange = function () {
                if (!r && (!this.readyState || this.readyState === 'complete')) {
                    r = true;
                    _this.isSDKLoaded = true;
                    resolve(200);
                }
            };
            s.onerror = () => {
                return reject('error in loading amazonpay SDK');
            };
            t = document.getElementsByTagName('script')[0];
            t.parentNode.insertBefore(s, t);
            window.onAmazonLoginReady = () => {
                window.amazon.Login.setClientId(this.environment.payments.amazonClientId);
            };
        });
    }
    loadAmazonSDK() {
        if (this.isSDKLoaded) {
            return;
        }
        const loadWithRetry = (no_of_retries, fn) => {
            fn().catch(() => {
                if (no_of_retries > 0) {
                    --no_of_retries;
                    console.log('about to retry...', no_of_retries);
                    loadWithRetry(no_of_retries, fn);
                }
            });
        };
        loadWithRetry(2, this.loadSDK.bind(this));
    }
    // amazonpay payment
    amazonpayPayment(method, option, apiDataModel) {
        if (this.isLinked) {
            this.addMoney(method, option, apiDataModel);
        }
        else {
            const params = '', options = {
                scope: 'payments::conduct_silentpay',
                response_type: 'code',
                state: params,
                popup: true
            };
            this.makePaymentButtonLoaderService.inProgress.emit(true);
            window.amazon.Login.authorize(options, (data) => {
                if (data.error || !data.code) {
                    this.utilityService.handleResponse({
                        message: data.error_description
                    });
                    this.makePaymentButtonLoaderService.inProgress.emit(false);
                    this.gtmService.logPaymentEvent("amazon_login_rejected", data.error_description || data.error);
                    return;
                }
                const body = {
                    client_id: this.environment.payments.amazonClientId,
                    redirect_uri: this.environment.payments.amazonPayRediretcUri,
                    code: data.code
                };
                this.link(option, body).subscribe(walletData => {
                    if (walletData && walletData.success) {
                        this.isLinked = true;
                        this.gtmService.logPaymentEvent("wallet_linked", option.code);
                        this.addMoney(method, option, apiDataModel);
                        this.getBalance(option);
                    }
                    else {
                        this.utilityService.handleResponse(walletData);
                        this.makePaymentButtonLoaderService.inProgress.emit(false);
                    }
                });
            });
        }
    }
    // amazon pay balance
    getBalance(option) {
        this.checklinking(option).subscribe((res) => {
            this.makePaymentButtonLoaderService.inProgress.next(false);
            this.hydrateAmazonPayInfo(res);
        }, () => {
            this.isLinked = false;
            this.amazonBalance.next(null);
            this.makePaymentButtonLoaderService.inProgress.next(false);
        });
    }
    // amazon pay checklinking info
    hydrateAmazonPayInfo(res) {
        if (res.linked) {
            this.isLinked = true;
            if (res && res.data) {
                this.amazonBalance.next(res.data.balance);
            }
        }
        else {
            this.isLinked = false;
            this.amazonBalance.next(null);
        }
    }
    link(option, extraParams) {
        return this.handlePaymentService.link(option, extraParams, 'sdk');
    }
    unlink(option) {
        return this.handlePaymentService.unlink(option, {}, 'sdk');
    }
    checklinking(option) {
        return this.handlePaymentService.checkLinking(option, {}, 'sdk');
    }
    addMoney(method, option, apiDataModel) {
        lastValueFrom(this.handlePaymentService.addMoney(option, apiDataModel, {}, 'redirection'))
            .then((response) => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            if (response?.success) {
                if (response.data) {
                    if (response.data.url) {
                        this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
                    }
                    else if (response.status === 'PENDING') {
                        this.handlePaymentService.openPaymentPendingModal(response.merchant_order_id, option, method);
                    }
                }
                else {
                    const { tracking_id, merchant_order_id, order_confirmation_window } = response;
                    this.handlePaymentService.redirectToConfirmPage({
                        tracking_id,
                        merchant_order_id,
                        order_confirmation_window
                    });
                }
            }
            else {
                this.makePaymentButtonLoaderService.inProgress.emit(false);
                this.utilityService.handleResponse(response);
            }
        }).catch((error) => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse(error);
        });
    }
    // amazon pay later checklinking info
    hydrateAmazonPayLaterInfo(res) {
        let data = {
            active: true,
            tnc_url: '',
            description: ''
        };
        if (!res.eligible) {
            data.description = res.message;
            data.active = false;
        }
        if (res.linked) {
            this.isLinked = true;
            if (res && res.data) {
                data.tnc_url = res.data.tnc_url;
                this.amazonPayLaterBalance.next(res.data.balance);
            }
        }
        else {
            this.isLinked = false;
            this.amazonPayLaterBalance.next(null);
        }
        this.amazonPayLaterEligible$.next(data);
    }
    // amazon pay later payment
    amazonpayLaterPayment(method, option, apiDataModel) {
        if (this.isLinked) {
            this.directDebit(method, option, apiDataModel);
        }
        else {
            const params = '', options = {
                scope: 'payments::conduct_silentpay',
                response_type: 'code',
                state: params,
                popup: true
            };
            this.makePaymentButtonLoaderService.inProgress.emit(true);
            window.amazon.Login.authorize(options, (data) => {
                const body = {
                    client_id: this.environment.payments.amazonClientId,
                    redirect_uri: this.environment.payments.amazonPayRediretcUri,
                    code: data.code
                };
                this.link(option, body).subscribe(walletData => {
                    if (walletData && walletData.success) {
                        this.isLinked = true;
                        this.directDebit(method, option, apiDataModel);
                    }
                    else {
                        this.utilityService.handleResponse(walletData);
                    }
                    this.utilityService.handleResponse(walletData);
                    this.makePaymentButtonLoaderService.inProgress.emit(false);
                });
            }, (err) => {
                this.utilityService.handleResponse(err);
                this.makePaymentButtonLoaderService.inProgress.emit(false);
            });
        }
    }
    directDebit(method, option, apiDataModel) {
        this.handlePaymentService.debit(option, apiDataModel, {}, 'redirection')
            .subscribe((response) => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            if (response?.success) {
                const { tracking_id, merchant_order_id, order_confirmation_window } = response;
                this.handlePaymentService.redirectToConfirmPage({
                    tracking_id,
                    merchant_order_id,
                    order_confirmation_window
                });
            }
            else {
                switch (response.status) {
                    case 'PENDING': {
                        this.handlePaymentService.openPaymentPendingModal(response.merchant_order_id, option, method);
                        return;
                    }
                    case 'FAILED': {
                        this.makePaymentButtonLoaderService.inProgress.emit(false);
                        this.utilityService.handleResponse(response);
                        return;
                    }
                    default:
                        {
                            this.makePaymentButtonLoaderService.inProgress.emit(false);
                            this.utilityService.handleResponse(response);
                            return;
                        }
                        ;
                }
            }
        }, (error) => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse(error);
        });
    }
    reset() {
        this.isLinked = false;
        this.showAddandPay = false;
        this._isSDKLoaded = false;
        this.amazonBalance.next(0);
        this.amazonPayLaterBalance.next(0);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AmazonPayService, deps: [{ token: UtilityService }, { token: PopupServiceService }, { token: HandlePaymentService }, { token: MakePaymentButtonLoaderService }, { token: 'app-environment' }, { token: "app-gtm-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AmazonPayService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AmazonPayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: UtilityService }, { type: PopupServiceService }, { type: HandlePaymentService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-environment']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }] });

class SimplService {
    toastrService;
    utilityService;
    headerNotificationService;
    makePaymentButtonLoaderService;
    handlePaymentService;
    popupServiceService;
    isLinked = new BehaviorSubject(false);
    postMessageSubscription;
    paymentInProgress = new BehaviorSubject(false);
    isMobile = false;
    constructor(toastrService, utilityService, headerNotificationService, makePaymentButtonLoaderService, handlePaymentService, popupServiceService) {
        this.toastrService = toastrService;
        this.utilityService = utilityService;
        this.headerNotificationService = headerNotificationService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.handlePaymentService = handlePaymentService;
        this.popupServiceService = popupServiceService;
    }
    isSimplLinked(option, apiDataModel) {
        return this.handlePaymentService.checkLinking(option, {
            ...(apiDataModel.getCashOrderId())
        }, 's2s');
    }
    initiatePayment(method, option, apiDataModel) {
        this.isSimplLinked(option, apiDataModel).toPromise().then((res) => {
            if (res.eligible) {
                if (res.linked) {
                    this.isLinked.next(true);
                    this.debit(method, option, apiDataModel, 's2s');
                }
                else {
                    this.isLinked.next(false);
                    this.initiateLinking(method, option, apiDataModel, 'redirection');
                }
            }
            else {
                this.toastrService.openSnackBar('Account not eligible', 'OKAY');
            }
        })
            .catch((error) => {
            console.log(error);
            this.utilityService.handleResponse(error);
        }).finally(() => this.makePaymentButtonLoaderService.inProgress.next(false));
    }
    initiateLinking(method, option, apiDataModel, flow_type) {
        this.handlePaymentService.initiateLinking(option, {}, flow_type)
            .toPromise()
            .then((response) => {
            if (response && response.success) {
                if (response.data && response.data.url) {
                    this.popupServiceService.setupPopup(method, response.data.url, option);
                    this.postMessageSubscription = this.popupServiceService.postMessageResult.subscribe(res => {
                        if (res) {
                            this.postMessageSubscription.unsubscribe();
                            if (res.success) {
                                this.isLinked.next(true);
                                this.debit(method, option, { ...apiDataModel, link_pending: true }, 's2s');
                            }
                            else {
                                this.isLinked.next(false);
                                this.utilityService.handleResponse(response);
                            }
                        }
                    });
                }
            }
            else {
                this.utilityService.handleResponse(response);
            }
        }).catch((error) => {
            console.log(error);
            this.utilityService.handleResponse(error);
        }).finally(() => this.makePaymentButtonLoaderService.inProgress.next(false));
    }
    debit(method, option, apiDataModel, flow_type) {
        this.handlePaymentService.debit(option, apiDataModel, {}, flow_type)
            .toPromise()
            .then((response) => {
            this.processResult(response, option, method);
        })
            .catch((error) => {
            this.utilityService.handleResponse();
            console.log(error);
        })
            .finally(() => this.makePaymentButtonLoaderService.inProgress.next(false));
    }
    processResult(response, option, method) {
        if (response.status === 'SUCCESS') {
            const { tracking_id, merchant_order_id, order_confirmation_window } = response;
            this.handlePaymentService.redirectToConfirmPage({
                tracking_id,
                merchant_order_id,
                order_confirmation_window
            });
        }
        else if (response.status === 'PENDING') {
            this.handlePaymentService.openPaymentPendingModal(response.merchant_order_id, option, method);
        }
        else {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.headerNotificationService.notifyMessage.emit(response.message);
        }
    }
    unlink(option) {
        return this.handlePaymentService.unlink(option, {}, 's2s');
    }
    reset() {
        this.isLinked.next(false);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SimplService, deps: [{ token: ToastrService }, { token: UtilityService }, { token: HeaderNotificationService }, { token: MakePaymentButtonLoaderService }, { token: HandlePaymentService }, { token: PopupServiceService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SimplService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SimplService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: ToastrService }, { type: UtilityService }, { type: HeaderNotificationService }, { type: MakePaymentButtonLoaderService }, { type: HandlePaymentService }, { type: PopupServiceService }] });

class PaytmPgNetbankService {
    environment;
    utilityService;
    popupServiceService;
    handlePaymentService;
    makePaymentButtonLoaderService;
    constructor(environment, utilityService, popupServiceService, handlePaymentService, makePaymentButtonLoaderService) {
        this.environment = environment;
        this.utilityService = utilityService;
        this.popupServiceService = popupServiceService;
        this.handlePaymentService = handlePaymentService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
    }
    netBankPayment(method, option, apiDataModel) {
        const body = {
            CHANNEL_ID: this.environment.payments.CHANNEL_ID,
            THEME: this.environment.payments.THEME,
            WEBSITE: this.environment.payments.WEBSITE,
            AUTH_MODE: 'USRPWD',
            PAYMENT_TYPE_ID: 'NB',
            BANK_CODE: option.name
        };
        lastValueFrom(this.handlePaymentService.initiatePayment(option, apiDataModel, body, 'redirection'))
            .then((response) => {
            if (response && response.success) {
                this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
            }
            else {
                this.handlePaymentService.handlePaymentError(option, response.message);
                this.utilityService.handleResponse(response);
            }
        }).catch(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse();
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgNetbankService, deps: [{ token: 'app-environment' }, { token: UtilityService }, { token: PopupServiceService }, { token: HandlePaymentService }, { token: MakePaymentButtonLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgNetbankService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgNetbankService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-environment']
                }] }, { type: UtilityService }, { type: PopupServiceService }, { type: HandlePaymentService }, { type: MakePaymentButtonLoaderService }] });

class CardValidatorService {
    cvvRegex = /^[0-9]{3,4}$/;
    cardDateExpiryRegex = /^(0[1-9]|1[0-2])(1[7-9]|[2-9][0-9])$/;
    constructor() { }
    humanize(str) {
        str = str.replace(/_/g, " ");
        return str.toLowerCase();
    }
    errorMsg(key) {
        return {
            error: true,
            message: "Please enter valid " + this.humanize(key),
        };
    }
    validateNumber(value) {
        // accept only digits or spaces
        if (/[^0-9\s]+/.test(value)) {
            return false;
        }
        // The Luhn Algorithm.
        let nCheck = 0, nDigit = 0, bEven = false;
        value = value.replace(/\D/g, "");
        for (let n = value.length - 1; n >= 0; n--) {
            const cDigit = value.charAt(n);
            nDigit = parseInt(cDigit, 10);
            if (bEven) {
                if ((nDigit *= 2) > 9)
                    nDigit -= 9;
            }
            nCheck += nDigit;
            bEven = !bEven;
        }
        return nCheck % 10 === 0;
    }
    isNumberValid(cardType, number) {
        if (!number) {
            return false;
        }
        if (cardType === "INVALID CARD" || !this.validateNumber(number)) {
            return false;
        }
        return true;
    }
    isExpiryValid(cardExpiry) {
        const expiry = cardExpiry ? cardExpiry.replace(/\D/g, "") : null;
        if (!expiry || !this.cardDateExpiryRegex.test(expiry)) {
            return false;
        }
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const expiryMonth = parseInt(expiry.substring(0, 2));
        const expiryYear = parseInt(expiry.substring(2, 4));
        const fullExpiryYear = 2000 + expiryYear;
        if (fullExpiryYear < currentYear || (fullExpiryYear === currentYear && expiryMonth < currentMonth)) {
            return false;
        }
        return true;
    }
    isCvvValid(card_type, cvv) {
        if (card_type !== "maestro") {
            if (!cvv || !this.cvvRegex.test(cvv)) {
                return false;
            }
        }
        return true;
    }
    validateCardDetails(cardDetails, channelCode) {
        const card_type = channelCode.toLowerCase();
        for (const key of Object.keys(cardDetails)) {
            if (key === "card_number") {
                if (!this.isNumberValid(card_type, cardDetails[key])) {
                    return this.errorMsg(key);
                }
            }
            if (key === "card_expiry_date") {
                if (!this.isExpiryValid(cardDetails[key])) {
                    return this.errorMsg(key);
                }
            }
            if (key === "cvv" && card_type !== "maestro") {
                if (!this.isCvvValid(card_type, cardDetails[key])) {
                    return this.errorMsg(key);
                }
            }
            if (key === "name") {
                if (!cardDetails[key]) {
                    return this.errorMsg(key);
                }
            }
        }
        return { error: false, message: "" };
    }
    sanitizeNumber(number) {
        return number.replace(/\D/g, "");
    }
    sanitizeExpiryMonth(expiryDate) {
        const expiry_date = expiryDate.replace(/\D/g, "");
        const expiry_month = expiry_date.slice(0, 2);
        return expiry_month;
    }
    sanitizeExpiryYear(expiryDate) {
        const expiry_date = expiryDate.replace(/\D/g, "");
        const expiry_year = expiry_date.slice(-2);
        return "20" + expiry_year;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardValidatorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardValidatorService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardValidatorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [] });

class PgCardService {
    environment;
    storageService;
    deviceService;
    gtmService;
    toastr;
    makePaymentButtonLoaderService;
    handlePaymentService;
    cardValidatorService;
    utilityService;
    sanitizedNewCardDetais;
    selectedSavedCardDetails;
    constructor(environment, storageService, deviceService, gtmService, toastr, makePaymentButtonLoaderService, handlePaymentService, cardValidatorService, utilityService) {
        this.environment = environment;
        this.storageService = storageService;
        this.deviceService = deviceService;
        this.gtmService = gtmService;
        this.toastr = toastr;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.handlePaymentService = handlePaymentService;
        this.cardValidatorService = cardValidatorService;
        this.utilityService = utilityService;
    }
    async fetch(gateway) {
        try {
            const option = { gateway };
            const response = await lastValueFrom(this.handlePaymentService.fetchPaytmCards(option, {}, 's2s'));
            if (response.data?.cards_info?.length) {
                return response.data.cards_info;
            }
            return [];
        }
        catch (error) {
            throw error;
        }
    }
    async delete(gateway, savedCardId) {
        const option = { gateway };
        const body = { id: savedCardId };
        try {
            const response = await lastValueFrom(this.handlePaymentService.unlink(option, body, "redirection"));
            this.utilityService.handleResponse(response);
            return response;
        }
        catch (err) {
            console.log(err);
            this.utilityService.handleResponse(err);
        }
    }
    getCardImagePath(card = null) {
        if (card?.binDetail) {
            const icon_url = card.binDetail.channelCode.toLowerCase() + '_logo.png';
            return `https://assets.box8.co.in/payments/cards/${icon_url}`;
        }
        return this.environment.DEPLOY_URL + '/assets/images/dummy.png';
    }
    _fetchChecksum(option) {
        return this.handlePaymentService.checkLinking(option, {}, "redirection");
    }
    getsanitizedCardDetails(cardDetails) {
        const { card_number, card_expiry_date } = cardDetails;
        return {
            ...cardDetails,
            card_number: this.cardValidatorService.sanitizeNumber(card_number),
            card_expiry_month: this.cardValidatorService.sanitizeExpiryMonth(card_expiry_date),
            card_expiry_year: this.cardValidatorService.sanitizeExpiryYear(card_expiry_date)
        };
    }
    isCardValidated(isCardDetailsValid) {
        if (isCardDetailsValid.error) {
            this.toastr.openSnackBar(isCardDetailsValid.message, "OKAY");
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            return false;
        }
        return true;
    }
    savedCardValidator() {
        if (!this.selectedSavedCardDetails) {
            this.toastr.openSnackBar("No card selected", "OKAY");
            this.gtmService.logPaymentEvent("no_card_selected");
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            return false;
        }
        else if (this.selectedSavedCardDetails.cvv_required && (this.selectedSavedCardDetails.cvv?.length !== this.selectedSavedCardDetails.cvv_length)) {
            this.toastr.openSnackBar("Please enter valid CVV", "OKAY");
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            return false;
        }
        return true;
    }
    getFormattedCardNumberLength(cardLength) {
        const numGroups = Math.ceil(cardLength / 4);
        const lastGroupLength = cardLength % 4 || 4;
        const spacesInBetween = numGroups - 1;
        const totalLength = numGroups * 4 + spacesInBetween - (4 - lastGroupLength);
        return totalLength;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PgCardService, deps: [{ token: "app-environment" }, { token: "app-storage-service" }, { token: "app-device-service" }, { token: "app-gtm-service" }, { token: ToastrService }, { token: MakePaymentButtonLoaderService }, { token: HandlePaymentService }, { token: CardValidatorService }, { token: UtilityService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PgCardService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PgCardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root",
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: ToastrService }, { type: MakePaymentButtonLoaderService }, { type: HandlePaymentService }, { type: CardValidatorService }, { type: UtilityService }] });

class PaymentGatewayService {
    gatewayInfo = new Map();
    getGatewayUrl(paymentOption) {
        return this.gatewayInfo.get(paymentOption)?.gateway_url;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentGatewayService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentGatewayService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentGatewayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }] });

class PaytmPgService {
    apiService;
    pgCardService;
    utilityService;
    paymentGatewayService;
    handlePaymentService;
    popupServiceService;
    makePaymentButtonLoaderService;
    environment;
    gtmService;
    storageService;
    constructor(apiService, pgCardService, utilityService, paymentGatewayService, handlePaymentService, popupServiceService, makePaymentButtonLoaderService, environment, gtmService, storageService) {
        this.apiService = apiService;
        this.pgCardService = pgCardService;
        this.utilityService = utilityService;
        this.paymentGatewayService = paymentGatewayService;
        this.handlePaymentService = handlePaymentService;
        this.popupServiceService = popupServiceService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.environment = environment;
        this.gtmService = gtmService;
        this.storageService = storageService;
    }
    async initiateCardPayment(method, option, apiDataModel) {
        const { is_saved } = option;
        const cardDetails = is_saved ? this.pgCardService.selectedSavedCardDetails : { payment_type: "", saved_card_id: "", cardIssuer: "" };
        const { payment_type, saved_card_id, cardIssuer } = cardDetails;
        let body = {
            cardIssuer,
            saved_card_id,
            channel: "web",
            payment_option_id: option.id,
            payment_offers_applied: apiDataModel?.options_payload?.payment_offers_applied || null,
            PAYMENT_TYPE_ID: payment_type,
            IS_SAVED_CARD: is_saved ? 1 : 0
        };
        try {
            const response = await lastValueFrom(this.handlePaymentService.initiatePaymentV3(option, apiDataModel, body, "redirection"));
            if (response.success) {
                if (is_saved) {
                    this.processSavedCardPayment(cardDetails, method, option, response);
                }
                else {
                    this.processNewCardPayment(method, option, response);
                }
            }
            else {
                this.makePaymentButtonLoaderService.inProgress.emit(false);
                this.utilityService.handleResponse(response);
            }
        }
        catch (error) {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse();
        }
    }
    processNewCardPayment(method, option, response) {
        this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
        this.handlePaymentService.resetSelection$.emit(method);
    }
    processSavedCardPayment(cardDetails, method, option, response) {
        this.processTransaction(cardDetails, method, option, response);
    }
    async processTransaction(cardDetails, method, option, response) {
        const { payment_type, card_info, user_consent } = cardDetails;
        const { merchant_order_id, data: { token } } = response;
        const { paytmMID: mid, paytmUrl } = this.environment.payments;
        const gatewayUrl = this.paymentGatewayService.getGatewayUrl('paytm') || paytmUrl;
        const url = `${gatewayUrl}theia/api/v1/processTransaction?mid=${mid}&orderId=${merchant_order_id}`;
        const body = {
            head: {
                txnToken: token
            },
            body: {
                mid,
                authMode: "otp",
                requestType: "NATIVE",
                orderId: merchant_order_id,
                paymentMode: payment_type,
                cardInfo: card_info,
                coftConsent: {
                    userConsent: user_consent,
                    createdAt: new Date()
                }
            }
        };
        try {
            const response = await lastValueFrom(this.apiService.paytmPost(url, body));
            if (response?.body?.resultInfo?.resultStatus === "S") {
                this.popupServiceService.setupPopup(method, "", option, merchant_order_id, response.body.bankForm.redirectForm);
            }
            else {
                this.utilityService.handleResponse();
                this.makePaymentButtonLoaderService.inProgress.emit(false);
                this.gtmService.logPaymentEvent("process_transaction_api", 'Failed ' + merchant_order_id + ' ' + JSON.stringify(response.body.resultInfo));
            }
        }
        catch (error) {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse(error);
            // For logging timeout and other errors
            this.gtmService.logPaymentEvent("process_transaction_api", 'Error ' + merchant_order_id + ' ' + JSON.stringify(error?.message));
        }
        finally {
            this.handlePaymentService.resetSelection$.emit(method);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgService, deps: [{ token: ApiService }, { token: PgCardService }, { token: UtilityService }, { token: PaymentGatewayService }, { token: HandlePaymentService }, { token: PopupServiceService }, { token: MakePaymentButtonLoaderService }, { token: "app-environment" }, { token: "app-gtm-service" }, { token: "app-storage-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaytmPgService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: ApiService }, { type: PgCardService }, { type: UtilityService }, { type: PaymentGatewayService }, { type: HandlePaymentService }, { type: PopupServiceService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }] });

class SodexoCardService {
    gtmService;
    utilityService;
    handlePaymentService;
    popupServiceService;
    makePaymentButtonLoaderService;
    use_saved_card = true;
    cardSourceId = new BehaviorSubject(null);
    constructor(gtmService, utilityService, handlePaymentService, popupServiceService, makePaymentButtonLoaderService) {
        this.gtmService = gtmService;
        this.utilityService = utilityService;
        this.handlePaymentService = handlePaymentService;
        this.popupServiceService = popupServiceService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
    }
    async fetch(gateway) {
        try {
            const option = { gateway };
            const response = await lastValueFrom(this.handlePaymentService.fetchSodexoCards(option, {}, "s2s"));
            if (response?.linked && response.eligible && response.data?.length) {
                const savedCards = response.data.map((card) => ({
                    ...card,
                    ...card.cardSourceDetails
                }));
                return savedCards;
            }
            return [];
        }
        catch (error) {
            throw error;
        }
    }
    async delete(gateway, sourceId) {
        const option = { gateway };
        const body = {
            remove_saved_card: true,
            source_id: sourceId ? sourceId : null
        };
        try {
            const response = await lastValueFrom(this.handlePaymentService.unlink(option, body, "s2s"));
            this.utilityService.handleResponse(response);
            return response;
        }
        catch (err) {
            console.log(err);
            this.utilityService.handleResponse(err);
        }
    }
    start(method, option, apiDataModel) {
        const body = {
            source_id: (this.cardSourceId.value && this.use_saved_card) ? this.cardSourceId.value : null,
            use_saved_card: Boolean(this.cardSourceId.value) && this.use_saved_card,
        };
        lastValueFrom(this.handlePaymentService.initiatePayment(option, apiDataModel, body, 'redirection'))
            .then((response) => {
            this.handlePaymentService.resetSelection$.emit(method);
            if (response.success) {
                if (response.data && response.data.url) {
                    if (!body.use_saved_card) {
                        this.gtmService.logPaymentEvent("card_linked", option.code);
                    }
                    this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
                }
            }
            else {
                this.handlePaymentService.handlePaymentError(option, response.message);
            }
        }).catch(() => {
            this.handlePaymentService.resetSelection$.emit(method);
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse();
        });
        this.use_saved_card = true;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoCardService, deps: [{ token: "app-gtm-service" }, { token: UtilityService }, { token: HandlePaymentService }, { token: PopupServiceService }, { token: MakePaymentButtonLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoCardService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoCardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: UtilityService }, { type: HandlePaymentService }, { type: PopupServiceService }, { type: MakePaymentButtonLoaderService }] });

class Time {
    hours;
    minutes;
    seconds;
}

class TimerComponent {
    timeInSeconds;
    message;
    time = new Time();
    timer;
    timerInterval;
    constructor() { }
    ngOnInit() {
        if (!this.timeInSeconds) {
            return;
        }
        this.startTimer();
    }
    updateTime() {
        this.time.minutes = Math.floor(this.timer / 60);
        this.time.seconds = (this.timer - (this.time.minutes * 60));
    }
    startTimer() {
        this.timer = this.timeInSeconds;
        clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
            if (this.timer > 0) {
                this.timer--;
                this.updateTime();
            }
            else {
                clearInterval(this.timerInterval);
            }
        }, 1000);
    }
    ngOnDestroy() {
        clearInterval(this.timerInterval);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TimerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: TimerComponent, isStandalone: true, selector: "lib-timer", inputs: { timeInSeconds: "timeInSeconds", message: "message" }, ngImport: i0, template: "@if (time.seconds > 0) {\n  <div class=\"timer\">\n    <span>{{ message }}</span>&nbsp;\n    <span>{{ time.minutes | number:'2.0-0' }} : {{ time.seconds | number:'2.0-0' }}</span>\n  </div>\n}\n@if (time.seconds <= 0) {\n  <ng-content></ng-content>\n}\n", styles: [".timer span{color:#6b6b6b}\n"], dependencies: [{ kind: "pipe", type: DecimalPipe, name: "number" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TimerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-timer', standalone: true, imports: [DecimalPipe], template: "@if (time.seconds > 0) {\n  <div class=\"timer\">\n    <span>{{ message }}</span>&nbsp;\n    <span>{{ time.minutes | number:'2.0-0' }} : {{ time.seconds | number:'2.0-0' }}</span>\n  </div>\n}\n@if (time.seconds <= 0) {\n  <ng-content></ng-content>\n}\n", styles: [".timer span{color:#6b6b6b}\n"] }]
        }], ctorParameters: () => [], propDecorators: { timeInSeconds: [{
                type: Input
            }], message: [{
                type: Input
            }] } });

class LazypayPaymentComponent {
    data;
    appConfig;
    storageService;
    utilityService;
    dialogRef;
    lazyPayS2S;
    handlePaymentService;
    otpTimer;
    email;
    lazypayData;
    apiDataModel;
    accTransactionId;
    phoneNumber;
    finalPrice;
    option;
    method;
    lazypayOtp;
    otpSent = false;
    requestOTPInProgress = false;
    validateOtpInProgress = false;
    debitInProgress = false;
    constructor(data, appConfig, storageService, utilityService, dialogRef, lazyPayS2S, handlePaymentService) {
        this.data = data;
        this.appConfig = appConfig;
        this.storageService = storageService;
        this.utilityService = utilityService;
        this.dialogRef = dialogRef;
        this.lazyPayS2S = lazyPayS2S;
        this.handlePaymentService = handlePaymentService;
        this.option = data.option;
        this.method = data.method;
        this.lazypayData = data.lazypayData;
        this.apiDataModel = data.apiDataModel;
        this.phoneNumber = this.storageService.get('phone_no');
        this.email = this.storageService.get('email');
        this.finalPrice = this.data.apiDataModel.final_price;
    }
    ngOnInit() {
        this.lazypayOtp = new FormGroup({
            otp: new FormControl('', Validators.compose([
                Validators.required,
                Validators.pattern(/^([\\.A-Za-z0-9-])+@([\\.A-Za-z0-9-])+$/)
            ]))
        });
    }
    requestOTP() {
        this.requestOTPInProgress = true;
        this.lazyPayS2S.requestOTP(this.option).subscribe((data) => {
            this.requestOTPInProgress = false;
            if (data && data.success) {
                this.otpSent = true;
                this.accTransactionId = data.data.account_transaction_id;
                this.utilityService.handleResponse(data);
            }
        }, () => {
            this.requestOTPInProgress = false;
        });
    }
    resendOtp() {
        this.lazyPayS2S.requestOTP(this.option).subscribe(data => {
            if (data && data.success) {
                this.otpTimer.startTimer();
                this.otpSent = true;
                this.utilityService.handleResponse(data);
            }
        });
    }
    validateOtp() {
        this.validateOtpInProgress = true;
        const body = {
            account_transaction_id: this.accTransactionId,
            otp: Number(this.lazypayOtp.value.otp)
        };
        this.lazyPayS2S.validateOTP(this.option, body).subscribe((data) => {
            if (data.success) {
                this.debit();
            }
            else {
                this.validateOtpInProgress = false;
                this.utilityService.handleResponse(data);
            }
        }, () => {
            this.validateOtpInProgress = false;
        });
    }
    debit() {
        this.debitInProgress = true;
        lastValueFrom(this.lazyPayS2S.debit(this.option, this.apiDataModel))
            .then((response) => {
            if (response.status === 'SUCCESS') {
                const { tracking_id, merchant_order_id, order_confirmation_window } = response;
                this.handlePaymentService.redirectToConfirmPage({
                    tracking_id,
                    merchant_order_id,
                    order_confirmation_window
                });
            }
            else if (response.status === 'PENDING') {
                this.handlePaymentService.openPaymentPendingModal(response.merchant_order_id, this.option, this.method);
            }
            else {
                this.debitInProgress = false;
                this.utilityService.handleResponse(response);
            }
        }).catch(() => {
            this.debitInProgress = false;
            this.utilityService.handleResponse();
        });
    }
    close() {
        this.dialogRef.close();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LazypayPaymentComponent, deps: [{ token: MAT_DIALOG_DATA }, { token: 'app-config' }, { token: 'app-storage-service' }, { token: UtilityService }, { token: i1$1.MatDialogRef }, { token: LazypayS2SService }, { token: HandlePaymentService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: LazypayPaymentComponent, isStandalone: true, selector: "lib-lazypay-payment", viewQueries: [{ propertyName: "otpTimer", first: true, predicate: ["otpTimer"], descendants: true }], ngImport: i0, template: "<div mat-dialog-title class=\"text-center\">\n    <img libDeferImgLoad [imageSrc]=\"'/assets/images/lazypay_logo.png'\" />\n    <div class=\"pull-right close\" (click)=\"close()\">&#10005;</div>\n</div>\n\n<div mat-dialog-content class=\"content\">\n    <div class=\"clearfix\"></div>\n    @if (!data?.lazypayData.isUserAccountLinked && !otpSent) {\n        <div class=\"lazypays2s-initial\">\n            <h4>Use LazyPay Credit</h4>\n            <h2>&#8377; {{ finalPrice }}</h2>\n            <h5>Top Features of LazyPay Credit</h5>\n            <ul>\n                <li>Zero Cost Credit (0% interest)</li>\n                <li>No sign-up needed</li>\n                <li>Place your order with just an OTP</li>\n                <li>Pay us back within the due date - we will remind you</li>\n            </ul>\n            <div class=\"customer-info\">\n                <p><span>Mobile:&nbsp;</span>{{ phoneNumber }}</p>\n                <p><span>Email:&nbsp;</span>{{ email }}</p>\n            </div>\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width\"\n                (click)=\"requestOTP()\"\n                [disabled]=\"requestOTPInProgress\">\n                @if (!requestOTPInProgress) {\n                    <span>SEND OTP</span>\n                }\n                @if (requestOTPInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n        </div>\n    }\n\n    @if (otpSent) {\n        <p class=\"lazypay-info\">LazyPay has sent a verification code to {{ phoneNumber }} via SMS. Please enter it below.</p>\n        <div class=\"clearfix\"></div>\n        <form name=\"lazypayOtp\" [formGroup]=\"lazypayOtp\" class=\"form-fields-theme\">\n            <div class=\"input-height\">\n                <mat-form-field appearance=\"outline\" class=\"lazypay-input form-fields-theme\">\n                    <mat-label>ENTER VERIFICATION CODE</mat-label>\n                    <input matInput formControlName=\"otp\" type=\"text\" inputmode=\"numeric\" placeholder=\"OTP\" />\n                </mat-form-field>\n            </div>\n        </form>\n        <div class=\"clearfix\"></div>\n        <p class=\"lazypay-info\">\n            <mat-icon class=\"info-icon\" style=\"vertical-align: middle\">info</mat-icon>\n            I agree to LazyPay\u2019s\n            <a href=\"https://lazypay.in/tnc.html\" target=\"_blank\" style=\"cursor: pointer\">Terms & Conditions</a>\n            and agree to pay &#8377;&nbsp;{{ finalPrice }} by {{ data.lazypayData.payableDate }}.\n        </p>\n        <div class=\"clearfix\"></div>\n        <div class=\"full-width text-center\">\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width lazypay-btn\"\n                (click)=\"validateOtp()\"\n                [disabled]=\"validateOtpInProgress\">\n                @if (!validateOtpInProgress) {\n                    <span>COMPLETE PAYMENT</span>\n                }\n                @if (validateOtpInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n            <div class=\"space-20\"></div>\n            <div class=\"clearfix\"></div>\n            <div class=\"text-center\">\n                <lib-timer #otpTimer timeInSeconds=\"30\" message=\"You will receive message within\">\n                    <div style=\"font-size: 12px; color: #6b6b6b\">\n                        <span style=\"font-style: italic\">Didn't get OTP?</span>&nbsp;\n                        <a href=\"JavaScript:void(0);\" (click)=\"resendOtp()\" style=\"color: #2493da\">Resend OTP</a>\n                    </div>\n                </lib-timer>\n            </div>\n        </div>\n        <div class=\"clearfix\"></div>\n    }\n\n    @if (data.lazypayData.isUserAccountLinked && !otpSent) {\n        <div class=\"lazypays2s-initial\">\n            <h4>Use LazyPay Credit</h4>\n            <h2>&#8377; {{ finalPrice }}</h2>\n            <p class=\"completed-info\">\n                Pay us back &#8377;&nbsp;{{ finalPrice }} online by\n                <span>{{ data.lazypayData.payableDate }}</span>\n            </p>\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width lazypay-btn\"\n                (click)=\"debit()\"\n                [disabled]=\"debitInProgress\">\n                @if (!debitInProgress) {\n                    <span>COMPLETE PAYMENT</span>\n                }\n                @if (debitInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n            <span class=\"info\">\n                <mat-icon class=\"info-icon\" style=\"vertical-align: middle\">info</mat-icon>\n                OTP not required because your LazyPay credit account is linked with {{ appConfig.static_text.brand_name }}.\n            </span>\n        </div>\n    }\n</div>\n", styles: [".lazypays2s-initial h4{font-size:14px;margin:10px 0;text-align:center;color:#323232}.lazypays2s-initial .customer-info{margin-bottom:24px}.lazypays2s-initial h2{margin:5px 0 0;font-size:32px;font-weight:600;text-align:center;color:#ff0057;-webkit-font-smoothing:antialiased}.lazypays2s-initial h5{font-size:16px;font-weight:600;margin-top:15px;margin-bottom:14px;padding-left:0;color:#555}.lazypays2s-initial ul{margin-bottom:24px;list-style-type:circle;color:#5a5a5a;padding-left:20px}.lazypays2s-initial ul li{font-size:13px;margin-bottom:10px}.lazypays2s-initial p{text-align:left;color:#323232;margin:8px 0}.lazypays2s-initial p span{font-weight:600;color:#8c8c8c}.lazypays2s-initial span.info{font-size:12px;display:block;margin-top:12px;text-align:center;color:#888}.lazypays2s-initial p.completed-info{font-size:16px;margin-top:20px;text-align:center;color:#8c8c8c}.lazypays2s p.completed-info span{font-weight:600;color:#555;-webkit-font-smoothing:antialiased}.lazypay-info{font-size:13px;color:#6b6b6b}.lazypay-info a{color:var(--primary-color);text-decoration:none}.lazypay-btn{margin-top:20px}.info-icon{font-size:20px;width:20px;height:20px}\n"], dependencies: [{ kind: "directive", type: MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }, { kind: "directive", type: MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { kind: "component", type: MatButton, selector: "    button[mat-button], button[mat-raised-button], button[mat-flat-button],    button[mat-stroked-button]  ", exportAs: ["matButton"] }, { kind: "component", type: MatProgressSpinner, selector: "mat-progress-spinner, mat-spinner", inputs: ["color", "mode", "value", "diameter", "strokeWidth"], exportAs: ["matProgressSpinner"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: MatLabel, selector: "mat-label" }, { kind: "directive", type: MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "component", type: MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: TimerComponent, selector: "lib-timer", inputs: ["timeInSeconds", "message"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LazypayPaymentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-lazypay-payment', standalone: true, imports: [MatDialogTitle, DeferImgLoadDirective, MatDialogContent, MatButton, MatProgressSpinner, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatIcon, TimerComponent], template: "<div mat-dialog-title class=\"text-center\">\n    <img libDeferImgLoad [imageSrc]=\"'/assets/images/lazypay_logo.png'\" />\n    <div class=\"pull-right close\" (click)=\"close()\">&#10005;</div>\n</div>\n\n<div mat-dialog-content class=\"content\">\n    <div class=\"clearfix\"></div>\n    @if (!data?.lazypayData.isUserAccountLinked && !otpSent) {\n        <div class=\"lazypays2s-initial\">\n            <h4>Use LazyPay Credit</h4>\n            <h2>&#8377; {{ finalPrice }}</h2>\n            <h5>Top Features of LazyPay Credit</h5>\n            <ul>\n                <li>Zero Cost Credit (0% interest)</li>\n                <li>No sign-up needed</li>\n                <li>Place your order with just an OTP</li>\n                <li>Pay us back within the due date - we will remind you</li>\n            </ul>\n            <div class=\"customer-info\">\n                <p><span>Mobile:&nbsp;</span>{{ phoneNumber }}</p>\n                <p><span>Email:&nbsp;</span>{{ email }}</p>\n            </div>\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width\"\n                (click)=\"requestOTP()\"\n                [disabled]=\"requestOTPInProgress\">\n                @if (!requestOTPInProgress) {\n                    <span>SEND OTP</span>\n                }\n                @if (requestOTPInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n        </div>\n    }\n\n    @if (otpSent) {\n        <p class=\"lazypay-info\">LazyPay has sent a verification code to {{ phoneNumber }} via SMS. Please enter it below.</p>\n        <div class=\"clearfix\"></div>\n        <form name=\"lazypayOtp\" [formGroup]=\"lazypayOtp\" class=\"form-fields-theme\">\n            <div class=\"input-height\">\n                <mat-form-field appearance=\"outline\" class=\"lazypay-input form-fields-theme\">\n                    <mat-label>ENTER VERIFICATION CODE</mat-label>\n                    <input matInput formControlName=\"otp\" type=\"text\" inputmode=\"numeric\" placeholder=\"OTP\" />\n                </mat-form-field>\n            </div>\n        </form>\n        <div class=\"clearfix\"></div>\n        <p class=\"lazypay-info\">\n            <mat-icon class=\"info-icon\" style=\"vertical-align: middle\">info</mat-icon>\n            I agree to LazyPay\u2019s\n            <a href=\"https://lazypay.in/tnc.html\" target=\"_blank\" style=\"cursor: pointer\">Terms & Conditions</a>\n            and agree to pay &#8377;&nbsp;{{ finalPrice }} by {{ data.lazypayData.payableDate }}.\n        </p>\n        <div class=\"clearfix\"></div>\n        <div class=\"full-width text-center\">\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width lazypay-btn\"\n                (click)=\"validateOtp()\"\n                [disabled]=\"validateOtpInProgress\">\n                @if (!validateOtpInProgress) {\n                    <span>COMPLETE PAYMENT</span>\n                }\n                @if (validateOtpInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n            <div class=\"space-20\"></div>\n            <div class=\"clearfix\"></div>\n            <div class=\"text-center\">\n                <lib-timer #otpTimer timeInSeconds=\"30\" message=\"You will receive message within\">\n                    <div style=\"font-size: 12px; color: #6b6b6b\">\n                        <span style=\"font-style: italic\">Didn't get OTP?</span>&nbsp;\n                        <a href=\"JavaScript:void(0);\" (click)=\"resendOtp()\" style=\"color: #2493da\">Resend OTP</a>\n                    </div>\n                </lib-timer>\n            </div>\n        </div>\n        <div class=\"clearfix\"></div>\n    }\n\n    @if (data.lazypayData.isUserAccountLinked && !otpSent) {\n        <div class=\"lazypays2s-initial\">\n            <h4>Use LazyPay Credit</h4>\n            <h2>&#8377; {{ finalPrice }}</h2>\n            <p class=\"completed-info\">\n                Pay us back &#8377;&nbsp;{{ finalPrice }} online by\n                <span>{{ data.lazypayData.payableDate }}</span>\n            </p>\n            <button\n                type=\"button\"\n                mat-flat-button\n                color=\"primary\"\n                class=\"radius-3 full-width lazypay-btn\"\n                (click)=\"debit()\"\n                [disabled]=\"debitInProgress\">\n                @if (!debitInProgress) {\n                    <span>COMPLETE PAYMENT</span>\n                }\n                @if (debitInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n            <span class=\"info\">\n                <mat-icon class=\"info-icon\" style=\"vertical-align: middle\">info</mat-icon>\n                OTP not required because your LazyPay credit account is linked with {{ appConfig.static_text.brand_name }}.\n            </span>\n        </div>\n    }\n</div>\n", styles: [".lazypays2s-initial h4{font-size:14px;margin:10px 0;text-align:center;color:#323232}.lazypays2s-initial .customer-info{margin-bottom:24px}.lazypays2s-initial h2{margin:5px 0 0;font-size:32px;font-weight:600;text-align:center;color:#ff0057;-webkit-font-smoothing:antialiased}.lazypays2s-initial h5{font-size:16px;font-weight:600;margin-top:15px;margin-bottom:14px;padding-left:0;color:#555}.lazypays2s-initial ul{margin-bottom:24px;list-style-type:circle;color:#5a5a5a;padding-left:20px}.lazypays2s-initial ul li{font-size:13px;margin-bottom:10px}.lazypays2s-initial p{text-align:left;color:#323232;margin:8px 0}.lazypays2s-initial p span{font-weight:600;color:#8c8c8c}.lazypays2s-initial span.info{font-size:12px;display:block;margin-top:12px;text-align:center;color:#888}.lazypays2s-initial p.completed-info{font-size:16px;margin-top:20px;text-align:center;color:#8c8c8c}.lazypays2s p.completed-info span{font-weight:600;color:#555;-webkit-font-smoothing:antialiased}.lazypay-info{font-size:13px;color:#6b6b6b}.lazypay-info a{color:var(--primary-color);text-decoration:none}.lazypay-btn{margin-top:20px}.info-icon{font-size:20px;width:20px;height:20px}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-config']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-storage-service']
                }] }, { type: UtilityService }, { type: i1$1.MatDialogRef }, { type: LazypayS2SService }, { type: HandlePaymentService }], propDecorators: { otpTimer: [{
                type: ViewChild,
                args: ['otpTimer']
            }] } });

class LazypayS2SService {
    dialog;
    utilityService;
    handlePaymentService;
    makePaymentButtonLoaderService;
    platformId;
    lazyPayData;
    params = {
        DeviceInfo: null
    };
    constructor(dialog, utilityService, handlePaymentService, makePaymentButtonLoaderService, platformId) {
        this.dialog = dialog;
        this.utilityService = utilityService;
        this.handlePaymentService = handlePaymentService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.platformId = platformId;
        if (isPlatformBrowser(this.platformId)) {
            this.params = {
                DeviceInfo: window.navigator.userAgent
            };
        }
    }
    checkEligibility(option, apiDataModel) {
        return this.handlePaymentService.checkLinking(option, {
            ...(apiDataModel.getCashOrderId()),
            ...this.params
        }, 'sdk');
    }
    requestOTP(option) {
        return this.handlePaymentService.initiateLinking(option, this.params, 'sdk');
    }
    validateOTP(option, body) {
        const extraParams = { ...body, ...this.params };
        return this.handlePaymentService.validateLinking(option, extraParams, 'sdk');
    }
    debit(option, apiDataModel) {
        return this.handlePaymentService.debit(option, apiDataModel, this.params, 'sdk');
    }
    showLazypayS2SModal(method, option, lazypayData, apiDataModel) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = 450;
        dialogConfig.panelClass = 'lazypays2s-modal';
        dialogConfig.data = { method, option, lazypayData, apiDataModel };
        this.dialog.open(LazypayPaymentComponent, dialogConfig)
            .afterClosed()
            .subscribe(() => {
            this.makePaymentButtonLoaderService.inProgress.next(false);
        });
    }
    lazypayS2SPayment(method, option, apiDataModel) {
        this.checkEligibility(option, apiDataModel).subscribe((response) => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            if (response) {
                if (!response.data.eligible) {
                    this.utilityService.handleResponse(response);
                }
                else {
                    if (response.data && !response.data.linked) {
                        this.showLazypayS2SModal(method, option, response.data, apiDataModel);
                    }
                    else if (response.data && response.data.linked) {
                        this.debit(option, apiDataModel).subscribe((response) => {
                            this.handleDebitResponse(response, method, option);
                        }, () => {
                            this.makePaymentButtonLoaderService.inProgress.emit(false);
                        });
                    }
                }
            }
        }, () => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        });
    }
    handleDebitResponse(response, method, option) {
        if (response.status === 'SUCCESS') {
            const { tracking_id, merchant_order_id, order_confirmation_window } = response;
            this.handlePaymentService.redirectToConfirmPage({
                tracking_id,
                merchant_order_id,
                order_confirmation_window
            });
        }
        else if (response.status === 'PENDING') {
            this.handlePaymentService.openPaymentPendingModal(response.merchant_order_id, option, method);
        }
        else {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LazypayS2SService, deps: [{ token: i1$1.MatDialog }, { token: UtilityService }, { token: HandlePaymentService }, { token: MakePaymentButtonLoaderService }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LazypayS2SService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: LazypayS2SService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.MatDialog }, { type: UtilityService }, { type: HandlePaymentService }, { type: MakePaymentButtonLoaderService }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

class CredpayService {
    handlePaymentService;
    makePaymentButtonLoaderService;
    deviceService;
    description = new BehaviorSubject({});
    checkLinkingCalled = false;
    constructor(handlePaymentService, makePaymentButtonLoaderService, deviceService) {
        this.handlePaymentService = handlePaymentService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.deviceService = deviceService;
    }
    checkLinking(option, paymentModel = null) {
        const body = {
            app_present: false,
            os: this.deviceService.getOS(),
            device: this.deviceService.getDevice(),
            ...(this.getCashOrderId(paymentModel))
        };
        return this.handlePaymentService.checkLinking(option, body, 's2s');
    }
    getCashOrderId(paymentModel) {
        if (!paymentModel) {
            return {};
        }
        if (paymentModel.multi_order_payment) {
            return { cash_order_ids: paymentModel.cart.id };
        }
        if (paymentModel.pay_on_delivery && paymentModel.pay_on_delivery.available) {
            return { cash_order_id: paymentModel.cart.id };
        }
        return {};
    }
    async init(paymentOptionsGateways, paymentModel = null) {
        const gateway = this.handlePaymentService.getGateway(paymentOptionsGateways, PaymentOptionCode.CREDPAY);
        await lastValueFrom(this.checkLinking({ gateway }, paymentModel))
            .then((response) => {
            let data = {
                description: '',
                active: true,
                tnc_url: ''
            };
            if (response.eligible) {
                data.description = response.data.offer_subtext;
                data.tnc_url = response.data.tnc_url;
            }
            else {
                data.description = response.message;
                data.active = false;
            }
            this.description.next(data);
        }).catch((err) => {
            console.log(err);
        }).finally(() => {
            this.checkLinkingCalled = true;
            this.makePaymentButtonLoaderService.inProgress.next(false);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CredpayService, deps: [{ token: HandlePaymentService }, { token: MakePaymentButtonLoaderService }, { token: 'app-device-service' }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CredpayService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CredpayService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: HandlePaymentService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-device-service']
                }] }] });

class CheckLinkingService {
    credPayService;
    paymentOptionGateways;
    paymentModel;
    paymentMethods;
    constructor(credPayService) {
        this.credPayService = credPayService;
    }
    isPaymentOptionAllowed(paymentOptionCode) {
        return this.paymentOptionGateways && this.paymentOptionGateways[paymentOptionCode];
    }
    _isOptionPresentInPreferredMethods(optionCode) {
        const preferredMethod = this.paymentMethods.find((method) => method.code === PaymentMethodCode.PREFERRED_PAYMENT);
        if (!preferredMethod)
            return false;
        return preferredMethod.payment_options.some((option) => option.code === optionCode);
    }
    checkPaymentOptionsLinking(paymentOptionGateways, paymentModel, paymentMethods) {
        this.paymentOptionGateways = paymentOptionGateways;
        this.paymentModel = paymentModel;
        this.paymentMethods = paymentMethods;
        this.checkCredpayLinking();
    }
    checkCredpayLinking() {
        if (this.isPaymentOptionAllowed(PaymentOptionCode.CREDPAY) && this._isOptionPresentInPreferredMethods(PaymentOptionCode.CREDPAY)) {
            this.credPayService.init(this.paymentOptionGateways, this.paymentModel);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckLinkingService, deps: [{ token: CredpayService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckLinkingService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CheckLinkingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: CredpayService }] });

class PaymentService {
    apiService;
    toastrService;
    cashService;
    amazonPayService;
    simplService;
    paytmPgNetbankService;
    paytmPgService;
    sodexoCardService;
    upiPayment;
    lazyPayS2S;
    dialog;
    makePaymentButtonLoaderService;
    popupServiceService;
    handlePaymentService;
    checkLinkingService;
    paymentGatewayService;
    utilityService;
    gtmService;
    deviceService;
    storageService;
    availablePaymentMethods = [];
    filteredPayment = [];
    noOfOptionToShow = {
        [PaymentMethodCode.UPI]: 3
    };
    paymentModel = {};
    paymentUtilModel = {};
    isZeroOrderPayment = new BehaviorSubject({
        currency_used: false,
        isZeroOrder: false
    });
    paymentMethods = new BehaviorSubject({
        paymentMethods: [],
        filteredPaymentMethod: {}
    });
    isPaymentMethodsUpdated = new BehaviorSubject(false);
    cartFinalAmount$ = new BehaviorSubject(null);
    couponCode$ = new BehaviorSubject("");
    disableMakePaymentCta$ = new BehaviorSubject(false);
    paymentGateways;
    constructor(apiService, toastrService, cashService, amazonPayService, simplService, paytmPgNetbankService, paytmPgService, sodexoCardService, upiPayment, lazyPayS2S, dialog, makePaymentButtonLoaderService, popupServiceService, handlePaymentService, checkLinkingService, paymentGatewayService, utilityService, gtmService, deviceService, storageService) {
        this.apiService = apiService;
        this.toastrService = toastrService;
        this.cashService = cashService;
        this.amazonPayService = amazonPayService;
        this.simplService = simplService;
        this.paytmPgNetbankService = paytmPgNetbankService;
        this.paytmPgService = paytmPgService;
        this.sodexoCardService = sodexoCardService;
        this.upiPayment = upiPayment;
        this.lazyPayS2S = lazyPayS2S;
        this.dialog = dialog;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.popupServiceService = popupServiceService;
        this.handlePaymentService = handlePaymentService;
        this.checkLinkingService = checkLinkingService;
        this.paymentGatewayService = paymentGatewayService;
        this.utilityService = utilityService;
        this.gtmService = gtmService;
        this.deviceService = deviceService;
        this.storageService = storageService;
    }
    getValidPaymentMethods(payment_methods) {
        return payment_methods.filter((method) => {
            return method.payment_options && method.payment_options.length;
        });
    }
    init(type, cart, payload = {}) {
        this.resetPayments();
        if (!Object.values(cart).length) {
            cart = JSON.parse(sessionStorage.getItem("cart"));
        }
        switch (type) {
            case PaymentType.postOrder:
                this.initPostOrder(cart.order_details, payload);
                break;
            case PaymentType.preOrder:
                this.initPreOrder(cart, payload);
                break;
        }
        const { payment_methods, payment_pending_ttl, payment_options_gateway, payment_gateways_info } = this.paymentUtilModel;
        // init helper vars
        this.availablePaymentMethods = payment_methods || [];
        this.paymentGatewayService.gatewayInfo = new Map(Object.entries(payment_gateways_info));
        //  hydrating all payment methods
        this.paymentMethods.next({
            paymentMethods: this.getValidPaymentMethods(this.availablePaymentMethods),
            filteredPaymentMethod: this.filteredPayment
        });
        this.paymentGateways = payment_options_gateway;
        this.handlePaymentService.paymentPendingTtl$.next(payment_pending_ttl);
        // initiating check linking
        this.checkLinkingService.checkPaymentOptionsLinking(payment_options_gateway, this.paymentModel, this.paymentMethods.value.paymentMethods);
        this.updatePaymentModel({});
    }
    rehydratePaymentMethod(data, payment_option) {
        const updatePaymentOption = (paymentOption) => {
            if (paymentOption.code === payment_option) {
                paymentOption.description = data.description || paymentOption.description;
                paymentOption.remark = data.remark || paymentOption.remark;
                paymentOption.active = data.active && paymentOption.active;
                paymentOption.tnc_url = data.tnc_url || paymentOption.tnc_url;
            }
            return paymentOption;
        };
        // update is done inplace, so changes gets reflected everywhere
        this.paymentMethods.value.paymentMethods.forEach((method) => {
            method.payment_options = method.payment_options.map(updatePaymentOption);
        });
        // Update filtered payment options
        this.paymentMethods.value.filteredPaymentMethod.payment_method?.payment_options?.forEach(updatePaymentOption);
        this.isPaymentMethodsUpdated.next(true);
    }
    initPostOrder(cart, payload) {
        if (!cart) {
            // show error
            return false;
        }
        this.paymentUtilModel = payload.payment_model;
        this.paymentModel = {
            type: PaymentType.postOrder,
            cart,
            outlet_open: null, // 'null' because of post order payment
            address_id: cart.customer_address ? cart.customer_address.id : 0,
            preorder_details: {
                available: null, // 'null' because of post order payment
                is_selected: Boolean(cart.order_time),
                time: cart.order_time
            },
            pay_on_delivery: {
                available: Boolean(cart.total_due),
                cash_order_id: cart.id
            },
            multi_order_payment: payload.multi_order_payment,
            currency_used: false, // currency will never be applicable for pay on delivery
            outlet_service_type: cart.order_service_type
        };
    }
    initPreOrder(cart, payload) {
        const { outlet_open, address_id, preorder_details, payment_model, currency_used, outlet_service_type } = payload;
        if (!cart || outlet_open === null || typeof outlet_open === "undefined" ||
            address_id === null || typeof address_id === "undefined" || payment_model === null ||
            typeof payment_model === "undefined" || outlet_service_type === null || typeof outlet_service_type === "undefined") {
            // show error
            return false;
        }
        this.filteredPayment = payment_model.filtered_payment_method;
        this.paymentUtilModel = payment_model;
        this.couponCode$.next(cart.discount_code);
        this.paymentModel = {
            type: PaymentType.preOrder,
            cart,
            outlet_open,
            address_id,
            preorder_details: preorder_details
                ? preorder_details
                : { available: false, is_selected: false, time: null },
            pay_on_delivery: null,
            currency_used: Boolean(currency_used),
            outlet_service_type
        };
    }
    updateFilteredMethod(method) {
        this.paymentMethods.next({
            ...this.paymentMethods.value,
            filteredPaymentMethod: method
        });
    }
    updateCart(cart) {
        if (!cart || !Object.values(cart).length) {
            return;
        }
        this.paymentModel.cart = cart;
        this.couponCode$.next(cart.discount_code);
        this.updatePaymentModel({});
    }
    updatePaymentModel(options) {
        this.paymentModel = { ...this.paymentModel, ...options };
        const { cart } = this.paymentModel;
        // case: user arrives first time on cart page, and payment page is not opened yet
        if (!cart) {
            return;
        }
        const amount = this.getCartFinalPrice();
        this.cartFinalAmount$.next(amount);
        this.checkTotalPayableEqualsZero(amount);
    }
    checkTotalPayableEqualsZero(amount) {
        const { currency_used } = this.paymentModel;
        this.isZeroOrderPayment.next({
            currency_used,
            isZeroOrder: !amount
        });
    }
    resetPayments() {
        this.availablePaymentMethods = [];
        this.paymentMethods.next({
            paymentMethods: [],
            filteredPaymentMethod: {}
        });
        this.paymentModel = {};
    }
    validParameters(method, option) {
        if (!method || !option) {
            this.gtmService.logPaymentEvent("no_option_selected");
            this.toastrService.openSnackBar("Please select an option for payment", "OKAY");
            return false;
        }
        const { outlet_open, preorder_details, pay_on_delivery } = this.paymentModel;
        // TODO: add check: for card and netbanking parameter validity
        // if pay on delivery is selected, skip further validations and start payment
        if (pay_on_delivery && pay_on_delivery.available) {
            return true;
        }
        // only preorder is available and user didn't selected it
        if (!outlet_open && !preorder_details.is_selected && preorder_details.available) {
            this.toastrService.openSnackBar("We only accept pre booking of your order when outlet is closed", "OKAY");
            return false;
        }
        // To stop the api call when cash is selected with order pre-booking.
        if (preorder_details.is_selected && method.code === PaymentMethodCode.CASH) {
            this.toastrService.openSnackBar("Cash Payment is not available when you are choosing for order pre booking", "OKAY");
            return false;
        }
        return true;
    }
    getRemark(method, option) {
        return method.code === PaymentMethodCode.CASH ? `On delivery, I'll use ${option.name}` : "NA";
    }
    getOutletServiceType() {
        return this.paymentModel.outlet_service_type;
    }
    getOrderTime() {
        const { preorder_details } = this.paymentModel;
        return preorder_details.is_selected ? Number(preorder_details.time / 1000) : 0;
    }
    getCartFinalPrice() {
        const { cart, currency_used, type } = this.paymentModel;
        if (type === PaymentType.postOrder) {
            return cart.total_due;
        }
        return currency_used ? cart.total_payable_with_credit : cart.total_payable;
    }
    getApiDataModel(method, option, extraPayload = {}) {
        const { address_id, cart, pay_on_delivery, currency_used, multi_order_payment } = this.paymentModel;
        return {
            address_id: String((address_id > 0) ? address_id : 0),
            address_locality: this.storageService.get("outlet_locality"),
            currency_redeem: String(currency_used),
            final_price: this.getCartFinalPrice(),
            latitude: this.storageService.get("latitude"),
            longitude: this.storageService.get("longitude"),
            order_time: String(this.getOrderTime()),
            outlet_service_type: this.getOutletServiceType(),
            payment_method: method,
            payment_option: option,
            payment_option_id: String(option.id),
            phone_no: this.storageService.get("phone_no"),
            remark: this.getRemark(method, option),
            session_id: this.storageService.get("session-id"),
            multi_order_payment,
            options_payload: {
                ...extraPayload
            },
            getCashOrderId: function () {
                if (multi_order_payment) {
                    return { cash_order_ids: cart.id };
                }
                else if (pay_on_delivery && pay_on_delivery.available) {
                    return { cash_order_id: cart.id };
                }
            }.bind(this)
        };
    }
    orderValidate(method, option) {
        const apiDataModel = this.getApiDataModel(method, option);
        const { address_id, currency_redeem, outlet_service_type, order_time, payment_method, payment_option_id, latitude: lat, longitude: lon } = apiDataModel;
        const params = {
            address_id,
            order_time,
            currency_redeem,
            payment_option_id,
            outlet_service_type,
            currencyRedeem: currency_redeem,
            payment_method: payment_method.code,
            lat,
            lon
        };
        return this.apiService.get("/order/validate", params);
    }
    pay(method, option, extraPayload = {}) {
        if (method.code === PaymentMethodCode.CASH) {
            this.payByCash(method, option, extraPayload);
        }
        else {
            this.payByOnlineMedium(method, option, extraPayload);
        }
    }
    payByCash(method, option, extraPayload = {}) {
        const apiDataModel = this.getApiDataModel(method, option, extraPayload);
        this.cashService.start(apiDataModel);
    }
    showUpiPaymentTimerModal(option, final_price, label = "UPI") {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = "full-screen";
        dialogConfig.data = { option, final_price, label };
        this.dialog.open(UpiPaymentTimerModalComponent, dialogConfig)
            .afterClosed()
            .subscribe(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        });
    }
    initUpiPayment(method, option, apiDataModel, extraParams = {}, flow_type) {
        lastValueFrom(this.handlePaymentService.initiatePayment(option, apiDataModel, extraParams, flow_type))
            .then((response) => {
            if (response && response.success) {
                if (flow_type === "redirection") {
                    this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
                }
                else {
                    this.showUpiPaymentTimerModal(option, apiDataModel.final_price);
                    this.upiPayment.init(response, option);
                }
                return;
            }
            else {
                this.utilityService.handleResponse(response);
            }
        }).finally(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        });
    }
    initWalletPayment(method, option, apiDataModel, extraParams = {}, flow_type) {
        lastValueFrom(this.handlePaymentService.initiatePayment(option, apiDataModel, extraParams, flow_type))
            .then((response) => {
            if (response && response.success) {
                this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
            }
            else {
                this.utilityService.handleResponse(response);
            }
        }).finally(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
        });
    }
    payByOnlineMedium(method, option, extraPayload = {}) {
        const methodCode = method.code;
        const optionCode = option.code;
        const apiDataModel = this.getApiDataModel(method, option, extraPayload);
        this.utilityService.setPaymentRedirectedGateway(apiDataModel);
        switch (optionCode) {
            case PaymentOptionCode.SODEXO:
                this.sodexoCardService.start(method, option, apiDataModel);
                break;
            case PaymentOptionCode.AMAZON_PAY:
                this.amazonPayService.amazonpayPayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.LAZYPAY:
                this.lazyPayS2S.lazypayS2SPayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.SIMPL:
                this.simplService.initiatePayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.SAVED_ICICI_UPI:
                this.initUpiPayment(method, option, apiDataModel, { upi_flow_type: "collect", customer_vpa: this.upiPayment.customerVpa }, "s2s");
                break;
            case PaymentOptionCode.PHONE_PAY_UPI:
                this.initUpiPayment(method, option, apiDataModel, {}, "redirection");
                break;
            case PaymentOptionCode.MOBIKWIK:
                this.initWalletPayment(method, option, apiDataModel, {}, "redirection");
                break;
            case PaymentOptionCode.UPI102:
                this.initUpiPayment(method, option, apiDataModel, { CHANNEL_ID: "WEB", WEBSITE: "ponchoweb", AUTH_MODE: "USRPWD", THEME: "merchant" }, "redirection");
                break;
            case PaymentOptionCode.GPAY:
                this.gpayPayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.PAYTM_APPINVOKE:
                this.initWalletPayment(method, option, apiDataModel, { paytm_present: false }, "redirection");
                break;
            case PaymentOptionCode.CREDPAY:
                this.initiateCredPayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.AMAZON_PAY_LATER:
                this.amazonPayService.amazonpayLaterPayment(method, option, apiDataModel);
                break;
            case PaymentOptionCode.PAYTM_CARD: {
                this.paytmPgService.initiateCardPayment(method, option, apiDataModel);
                break;
            }
            default: {
                switch (methodCode) {
                    case PaymentMethodCode.NETBANK:
                        this.paytmPgNetbankService.netBankPayment(method, option, apiDataModel);
                        break;
                    default: {
                        this.makePaymentButtonLoaderService.inProgress.emit(false);
                        this.gtmService.logPaymentEvent("invalid_option_selected", "option: " + option?.code + " method: " + method?.code);
                        this.toastrService.openSnackBar("Invalid option selected. Please try again with different option.", "OKAY");
                    }
                }
            }
        }
    }
    initiateCredPayment(method, option, apiDataModel) {
        const body = {
            app_present: false,
            client_phone_number: this.storageService.get("phone_no") || "",
            os: this.deviceService.getOS(),
            device: this.deviceService.getDevice()
        };
        lastValueFrom(this.handlePaymentService.initiatePayment(option, apiDataModel, body, "redirection"))
            .then((response) => {
            if (response.success) {
                if (response.data && response.data.url) {
                    this.popupServiceService.setupPopup(method, response.data.url, option, response.merchant_order_id);
                }
                else if (response.data && response.data.ttl) {
                    this.upiPayment.init(response, option);
                    this.showUpiPaymentTimerModal(option, apiDataModel.final_price, option.label);
                }
            }
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse(response);
        }).catch(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse();
        });
    }
    gpayPayment(method, option, apiDataModel) {
        if (!this.upiPayment.customerPhone) {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.toastrService.openSnackBar("Please enter a valid 10 digit number", "OKAY");
            return;
        }
        this.initUpiPayment(method, option, apiDataModel, { upi_flow_type: "collect", client_phone_number: this.upiPayment.customerPhone }, "s2s");
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentService, deps: [{ token: ApiService }, { token: ToastrService }, { token: CashService }, { token: AmazonPayService }, { token: SimplService }, { token: PaytmPgNetbankService }, { token: PaytmPgService }, { token: SodexoCardService }, { token: UpiPaymentService }, { token: LazypayS2SService }, { token: i1$1.MatDialog }, { token: MakePaymentButtonLoaderService }, { token: PopupServiceService }, { token: HandlePaymentService }, { token: CheckLinkingService }, { token: PaymentGatewayService }, { token: UtilityService }, { token: "app-gtm-service" }, { token: "app-device-service" }, { token: "app-storage-service" }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentService, providedIn: "root" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: "root"
                }]
        }], ctorParameters: () => [{ type: ApiService }, { type: ToastrService }, { type: CashService }, { type: AmazonPayService }, { type: SimplService }, { type: PaytmPgNetbankService }, { type: PaytmPgService }, { type: SodexoCardService }, { type: UpiPaymentService }, { type: LazypayS2SService }, { type: i1$1.MatDialog }, { type: MakePaymentButtonLoaderService }, { type: PopupServiceService }, { type: HandlePaymentService }, { type: CheckLinkingService }, { type: PaymentGatewayService }, { type: UtilityService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }] });

class RemoveCouponComponent {
    data;
    deviceService;
    bottomSheetRef;
    injector;
    isMobile = false;
    dialogRef;
    couponCode;
    info;
    constructor(data, deviceService, bottomSheetRef, injector) {
        this.data = data;
        this.deviceService = deviceService;
        this.bottomSheetRef = bottomSheetRef;
        this.injector = injector;
        this.isMobile = this.deviceService.isMobile();
        if (!this.isMobile) {
            this.dialogRef = this.injector.get(MatDialogRef, null);
            this.data = this.injector.get(MAT_DIALOG_DATA, null);
        }
    }
    ngOnInit() {
        this.couponCode = this.data.couponCode;
        this.hydrateInfo();
    }
    hydrateInfo() {
        if (this.data.type === 'change-code') {
            this.info = "Code is not applicable for other payment methods. If you choose another option, you will have to pay the full amount without any discount.";
        }
        else if (this.data.type === 'new-card') {
            this.info = "Code is not applicable on the selected card. Please try again with a valid card.";
        }
        else {
            this.info = "Code is not applicable on the selected payment mode. If you go ahead with this, coupon will not be applied & you will have to pay the full amount without discount.";
        }
    }
    close(data = '') {
        if (!this.isMobile) {
            this.dialogRef.close(data);
        }
        else {
            this.bottomSheetRef.dismiss(data);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RemoveCouponComponent, deps: [{ token: MAT_BOTTOM_SHEET_DATA }, { token: "app-device-service" }, { token: i1$2.MatBottomSheetRef }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: RemoveCouponComponent, isStandalone: true, selector: "lib-remove-coupon", ngImport: i0, template: "<div class=\"remove-coupon-wrapper\">\n  <div class=\"coupon-wrapper\">\n    <span class=\"coupon-code\"> {{ couponCode }} Code will be removed </span>\n    <mat-icon (click)=\"close()\">close</mat-icon>\n  </div>\n  <span class=\"info\">\n    {{ info }}\n  </span>\n  <div style=\"height: 20px\"></div>\n  <button (click)=\"close('change-code')\" class=\"change-code-btn\">\n    APPLY ANOTHER CODE\n  </button>\n  <button (click)=\"close('proceed-without-code')\" class=\"proceed-btn change-code-btn\">\n    @if (this.data.type === 'new-card') {\n      <span>\n        Choose another payment option\n      </span>\n    }\n    @if (this.data.type !== 'new-card') {\n      <span>\n        Proceed Anyway\n      </span>\n    }\n  </button>\n</div>\n", styles: [".remove-coupon-wrapper{padding:20px;display:flex;flex-direction:column;gap:6px}.coupon-wrapper{display:flex;justify-content:space-between;cursor:pointer;-webkit-tap-highlight-color:transparent}.coupon-code{font-weight:800;font-size:20px;line-height:24px;letter-spacing:.03em;color:#000}.info{font-weight:400;font-size:14px;line-height:18px;color:#474d60}.change-code-btn{height:44px;border-radius:4px;text-align:center;font-weight:700;font-size:16px;line-height:18px;color:#fff;background:var(--primary-color)}.proceed-btn{background:#fff;color:#707786}@media screen and (max-width: 991px){.coupon-code{font-weight:600;font-size:16px;letter-spacing:0}.info{font-weight:300}.change-code-btn{font-size:14px}.proceed-btn{font-weight:600}}\n"], dependencies: [{ kind: "component", type: MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RemoveCouponComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-remove-coupon", standalone: true, imports: [MatIcon], template: "<div class=\"remove-coupon-wrapper\">\n  <div class=\"coupon-wrapper\">\n    <span class=\"coupon-code\"> {{ couponCode }} Code will be removed </span>\n    <mat-icon (click)=\"close()\">close</mat-icon>\n  </div>\n  <span class=\"info\">\n    {{ info }}\n  </span>\n  <div style=\"height: 20px\"></div>\n  <button (click)=\"close('change-code')\" class=\"change-code-btn\">\n    APPLY ANOTHER CODE\n  </button>\n  <button (click)=\"close('proceed-without-code')\" class=\"proceed-btn change-code-btn\">\n    @if (this.data.type === 'new-card') {\n      <span>\n        Choose another payment option\n      </span>\n    }\n    @if (this.data.type !== 'new-card') {\n      <span>\n        Proceed Anyway\n      </span>\n    }\n  </button>\n</div>\n", styles: [".remove-coupon-wrapper{padding:20px;display:flex;flex-direction:column;gap:6px}.coupon-wrapper{display:flex;justify-content:space-between;cursor:pointer;-webkit-tap-highlight-color:transparent}.coupon-code{font-weight:800;font-size:20px;line-height:24px;letter-spacing:.03em;color:#000}.info{font-weight:400;font-size:14px;line-height:18px;color:#474d60}.change-code-btn{height:44px;border-radius:4px;text-align:center;font-weight:700;font-size:16px;line-height:18px;color:#fff;background:var(--primary-color)}.proceed-btn{background:#fff;color:#707786}@media screen and (max-width: 991px){.coupon-code{font-weight:600;font-size:16px;letter-spacing:0}.info{font-weight:300}.change-code-btn{font-size:14px}.proceed-btn{font-weight:600}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_BOTTOM_SHEET_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: i1$2.MatBottomSheetRef }, { type: i0.Injector }] });

class InfoDialogComponent {
    data;
    environment;
    dialogRef;
    DEPLOY_URL;
    message;
    ctas;
    constructor(data, environment, dialogRef) {
        this.data = data;
        this.environment = environment;
        this.dialogRef = dialogRef;
        this.DEPLOY_URL = this.environment.DEPLOY_URL;
    }
    ngOnInit() {
        const { message, ctas } = this.data;
        this.message = message;
        this.ctas = ctas;
    }
    close(message = "") {
        this.dialogRef.close(message);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InfoDialogComponent, deps: [{ token: MAT_DIALOG_DATA }, { token: "app-environment" }, { token: i1$1.MatDialogRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: InfoDialogComponent, isStandalone: true, selector: "lib-info-dialog", ngImport: i0, template: "<div class=\"info-dialog-top\">\n  <span class=\"message\">\n    {{ message }}\n  </span>\n  <img (click)=\"close()\" class=\"icon\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n</div>\n\n<div class=\"info-dialog-bottom\">\n  @for (cta of ctas; track cta) {\n    <button class=\"cta\" (click)=\"close(cta)\">\n      {{ cta }}\n    </button>\n  }\n</div>", styles: [".info-dialog-top{display:flex;gap:2px;margin-bottom:20px;padding:8px}.info-dialog-bottom{display:flex;flex-wrap:wrap}.message{display:block;font-weight:400;font-size:16px;line-height:20px;color:#707786}.cta{margin:8px;flex-basis:calc(50% - 16px);display:flex;justify-content:center;align-items:center;width:100%;height:40px;background:var(--primary-color);color:#fff;border:1px solid #cbced5;border-radius:4px}.icon{top:10px;right:10px;height:20px;cursor:pointer}@media (max-width: 991px){.icon{cursor:none}}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InfoDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-info-dialog', standalone: true, template: "<div class=\"info-dialog-top\">\n  <span class=\"message\">\n    {{ message }}\n  </span>\n  <img (click)=\"close()\" class=\"icon\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n</div>\n\n<div class=\"info-dialog-bottom\">\n  @for (cta of ctas; track cta) {\n    <button class=\"cta\" (click)=\"close(cta)\">\n      {{ cta }}\n    </button>\n  }\n</div>", styles: [".info-dialog-top{display:flex;gap:2px;margin-bottom:20px;padding:8px}.info-dialog-bottom{display:flex;flex-wrap:wrap}.message{display:block;font-weight:400;font-size:16px;line-height:20px;color:#707786}.cta{margin:8px;flex-basis:calc(50% - 16px);display:flex;justify-content:center;align-items:center;width:100%;height:40px;background:var(--primary-color);color:#fff;border:1px solid #cbced5;border-radius:4px}.icon{top:10px;right:10px;height:20px;cursor:pointer}@media (max-width: 991px){.icon{cursor:none}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: i1$1.MatDialogRef }] });

class PaymentIconComponent {
    paymentService;
    amazonPayService;
    credPayService;
    deviceService;
    environment;
    selected;
    option;
    panelExpanded;
    selectedPaymentOption = new EventEmitter();
    isMobile = false;
    checkLinkingInProgress;
    walletBalance = {};
    paymentDescription = {};
    isSelected;
    showBorder;
    destroySubject$ = new Subject();
    constructor(paymentService, amazonPayService, credPayService, deviceService, environment) {
        this.paymentService = paymentService;
        this.amazonPayService = amazonPayService;
        this.credPayService = credPayService;
        this.deviceService = deviceService;
        this.environment = environment;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.amazonPayService.amazonBalance.pipe(takeUntil(this.destroySubject$)).subscribe((balance) => {
            this.walletBalance[PaymentOptionCode.AMAZON_PAY] = balance;
        });
        this.amazonPayService.amazonPayLaterBalance.pipe(takeUntil(this.destroySubject$)).subscribe((balance) => {
            this.walletBalance[PaymentOptionCode.AMAZON_PAY_LATER] = balance;
        });
    }
    ngOnChanges(changes) {
        if (changes) {
            this.isSelected = this.option.identifier === this.selected.option.identifier;
        }
        if (this.shouldInitiateCredPayChecklinking(changes)) {
            this.initiateCredPayChecklinking();
        }
    }
    shouldInitiateCredPayChecklinking(changes) {
        if ((this.option?.code !== PaymentOptionCode.CREDPAY) || this.credPayService.checkLinkingCalled) {
            return;
        }
        const panelExpanded = changes.panelExpanded?.currentValue;
        const paymentMethodCode = changes.selected?.currentValue.method.code;
        if (this.isMobile) {
            return panelExpanded?.value && panelExpanded.methodCode === PaymentMethodCode.CREDPAY;
        }
        else {
            return paymentMethodCode === PaymentMethodCode.CREDPAY;
        }
    }
    async initiateCredPayChecklinking() {
        this.checkLinkingInProgress = true;
        await this.credPayService.init(this.paymentService.paymentGateways, this.paymentService.paymentModel);
        this.checkLinkingInProgress = false;
    }
    select() {
        this.selectedPaymentOption.emit(this.option);
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = '/assets/images/default_payment_option.svg';
    }
    openTnc(e, tnc_url) {
        window.open(tnc_url);
        e.stopPropagation();
    }
    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentIconComponent, deps: [{ token: PaymentService }, { token: AmazonPayService }, { token: CredpayService }, { token: 'app-device-service' }, { token: 'app-environment' }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: PaymentIconComponent, isStandalone: true, selector: "lib-payment-icon", inputs: { selected: "selected", option: "option", panelExpanded: "panelExpanded" }, outputs: { selectedPaymentOption: "selectedPaymentOption" }, usesOnChanges: true, ngImport: i0, template: "<div\n    class=\"option\"\n    (click)=\"option.active && !checkLinkingInProgress && select()\"\n    [style.opacity]=\"option.active ? '1' : '0.4'\"\n    [style.border]=\"(isSelected && option.active && !this.isMobile) ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n\n            <!-- option image -->\n            @if (checkLinkingInProgress) {\n                <div class=\"payment-option-img shine\"></div>\n            } @else {\n                <img class=\"payment-option-img\" [imageSrc]=\"option.image\" alt=\"{{ option.name }}\" libDeferImgLoad/>\n            }\n\n            <div>\n                <!-- option label -->\n                <span class=\"payment-label\">\n                    @if (checkLinkingInProgress) {\n                        <div class=\"shine\"></div>\n                    } @else {\n                        {{ option.label }}\n                        @if (option.remark; as remark) {\n                            <span class=\"low-success-remark\" [style.color]=\"option.active ? '#f37d7d' : '#606060'\">\n                                {{ remark }}\n                            </span>\n                        }\n                    }\n                </span>\n\n                <!-- option description -->\n                <div class=\"payment-option-description\">\n                    @if (checkLinkingInProgress) {\n                        <div class=\"shine\"></div>\n                    } @else {\n                        @if (walletBalance[option.code]; as walletBalance) {\n                            <span class=\"wallet-amount\">\n                                Balance: &#8377;{{ walletBalance }}\n                                @if (option.description || option.tnc_url) {\n                                    <span>|</span>\n                                }\n                            </span>\n                        }\n                        {{ option.description }}\n\n                        @if (option.tnc_url) {\n                            <span (click)=\"openTnc($event, option.tnc_url)\">\n                                @if (option.description) {\n                                    <span>| &nbsp;</span>\n                                }\n                                <span style=\"border-bottom: 1px solid\"> View T&C </span>\n                            </span>\n                        }\n                    }\n                </div>    \n            </div>\n        </div>\n\n        @if (option.active && !checkLinkingInProgress) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n</div>", styles: [".wallet-amount{font-weight:700}.shine{width:400px}\n"], dependencies: [{ kind: "component", type: MatRadioButton, selector: "mat-radio-button", inputs: ["id", "name", "aria-label", "aria-labelledby", "aria-describedby", "disableRipple", "tabIndex", "checked", "value", "labelPosition", "disabled", "required", "color", "disabledInteractive"], outputs: ["change"], exportAs: ["matRadioButton"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-payment-icon', standalone: true, imports: [MatRadioButton, DeferImgLoadDirective], template: "<div\n    class=\"option\"\n    (click)=\"option.active && !checkLinkingInProgress && select()\"\n    [style.opacity]=\"option.active ? '1' : '0.4'\"\n    [style.border]=\"(isSelected && option.active && !this.isMobile) ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n\n            <!-- option image -->\n            @if (checkLinkingInProgress) {\n                <div class=\"payment-option-img shine\"></div>\n            } @else {\n                <img class=\"payment-option-img\" [imageSrc]=\"option.image\" alt=\"{{ option.name }}\" libDeferImgLoad/>\n            }\n\n            <div>\n                <!-- option label -->\n                <span class=\"payment-label\">\n                    @if (checkLinkingInProgress) {\n                        <div class=\"shine\"></div>\n                    } @else {\n                        {{ option.label }}\n                        @if (option.remark; as remark) {\n                            <span class=\"low-success-remark\" [style.color]=\"option.active ? '#f37d7d' : '#606060'\">\n                                {{ remark }}\n                            </span>\n                        }\n                    }\n                </span>\n\n                <!-- option description -->\n                <div class=\"payment-option-description\">\n                    @if (checkLinkingInProgress) {\n                        <div class=\"shine\"></div>\n                    } @else {\n                        @if (walletBalance[option.code]; as walletBalance) {\n                            <span class=\"wallet-amount\">\n                                Balance: &#8377;{{ walletBalance }}\n                                @if (option.description || option.tnc_url) {\n                                    <span>|</span>\n                                }\n                            </span>\n                        }\n                        {{ option.description }}\n\n                        @if (option.tnc_url) {\n                            <span (click)=\"openTnc($event, option.tnc_url)\">\n                                @if (option.description) {\n                                    <span>| &nbsp;</span>\n                                }\n                                <span style=\"border-bottom: 1px solid\"> View T&C </span>\n                            </span>\n                        }\n                    }\n                </div>    \n            </div>\n        </div>\n\n        @if (option.active && !checkLinkingInProgress) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n</div>", styles: [".wallet-amount{font-weight:700}.shine{width:400px}\n"] }]
        }], ctorParameters: () => [{ type: PaymentService }, { type: AmazonPayService }, { type: CredpayService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-device-service']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-environment']
                }] }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], panelExpanded: [{
                type: Input
            }], selectedPaymentOption: [{
                type: Output
            }] } });

class SodexoSavedCardComponent {
    deviceService;
    paymentService;
    selected;
    option;
    finalCartValue;
    isPaymentPage = true;
    // recieved from profile page
    savedCardInfo;
    sodexoPaymentOption;
    selectedCardDetails = new EventEmitter();
    removeCard = new EventEmitter();
    description;
    isMobile = false;
    active = true;
    isSelected;
    showBorder;
    cardDetail;
    destroySubject$ = new Subject();
    constructor(deviceService, paymentService) {
        this.deviceService = deviceService;
        this.paymentService = paymentService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.option = this.option || this.sodexoPaymentOption;
        if (this.isPaymentPage) {
            this.cardDetail = this.option.saved_information.card_info;
        }
        else {
            this.cardDetail = this.savedCardInfo;
        }
    }
    ngOnChanges(changes) {
        if (changes && this.isPaymentPage && this.option.code === PaymentOptionCode.SODEXO) {
            const isLowBalance = this.finalCartValue > this.option.saved_information.card_info.balance;
            this.active = isLowBalance ? false : this.option.active;
            this.description = isLowBalance ? 'Insufficient Balance' : this.option.description;
            this.isSelected = this.option.identifier === this.selected.option.identifier;
            this.showBorder = this.isSelected && !this.isMobile && this.active;
            if (this.isSelected && !this.active) {
                this.paymentService.disableMakePaymentCta$.next(true);
            }
            else if (this.isSelected && this.active) {
                this.paymentService.disableMakePaymentCta$.next(false);
            }
        }
    }
    select() {
        this.selectedCardDetails.emit(this.cardDetail);
    }
    remove() {
        this.removeCard.emit(this.cardDetail.sourceId);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoSavedCardComponent, deps: [{ token: "app-device-service" }, { token: PaymentService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SodexoSavedCardComponent, isStandalone: true, selector: "lib-sodexo-saved-card", inputs: { selected: "selected", option: "option", finalCartValue: "finalCartValue", isPaymentPage: "isPaymentPage", savedCardInfo: "savedCardInfo", sodexoPaymentOption: "sodexoPaymentOption" }, outputs: { selectedCardDetails: "selectedCardDetails", removeCard: "removeCard" }, usesOnChanges: true, ngImport: i0, template: "<div\n    class=\"option\"\n    (click)=\"active && select()\"\n    [style.opacity]=\"active ? '1' : '0.4'\"\n    [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n            <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"option.image\" alt=\"sodexo\" />\n            <div style=\"display: flex; flex-direction: column\">\n                <span class=\"payment-label\">\n                    {{ option.label }}\n\n                    @if (option.remark; as remark) {\n                        <span class=\"low-success-remark\" [style.color]=\"active ? '#f37d7d' : '#606060'\">\n                            {{ remark }}\n                        </span>\n                    }\n                </span>\n                <span class=\"card-number\">\n                    &bull;&bull;&bull;&bull;\n                    {{ cardDetail.maskedPan.slice(-4) }}\n\n                    @if (cardDetail.balance; as balance) {\n                        <div class=\"price\">\n                            | Balance: &nbsp;&#8377;&nbsp;{{ balance }}\n                            @if (description) {\n                                ({{ description }})\n                            }\n                        </div>\n                    }\n                </span>\n            </div>\n        </div>\n\n        <!--delete option-->\n        @if (!isPaymentPage) {\n            <a class=\"delete-container\" href=\"javascript:void(0);\" (click)=\"remove()\" title=\"Delete this saved card\">\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/delete-payment-option.svg'\" alt=\"Delete\" />\n            </a>\n        }\n\n        <!-- radio selection -->\n        @if (isPaymentPage && active) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n</div>\n", styles: [".card-number{color:#7c8495;font-size:12px;font-weight:400;line-height:13px;letter-spacing:.1px;margin-top:2px;display:flex;align-items:center;gap:5px;flex-wrap:wrap}.price{display:flex;align-items:center;font-weight:700}\n"], dependencies: [{ kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }, { kind: "component", type: MatRadioButton, selector: "mat-radio-button", inputs: ["id", "name", "aria-label", "aria-labelledby", "aria-describedby", "disableRipple", "tabIndex", "checked", "value", "labelPosition", "disabled", "required", "color", "disabledInteractive"], outputs: ["change"], exportAs: ["matRadioButton"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoSavedCardComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-sodexo-saved-card", standalone: true, imports: [DeferImgLoadDirective, MatRadioButton], template: "<div\n    class=\"option\"\n    (click)=\"active && select()\"\n    [style.opacity]=\"active ? '1' : '0.4'\"\n    [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n            <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"option.image\" alt=\"sodexo\" />\n            <div style=\"display: flex; flex-direction: column\">\n                <span class=\"payment-label\">\n                    {{ option.label }}\n\n                    @if (option.remark; as remark) {\n                        <span class=\"low-success-remark\" [style.color]=\"active ? '#f37d7d' : '#606060'\">\n                            {{ remark }}\n                        </span>\n                    }\n                </span>\n                <span class=\"card-number\">\n                    &bull;&bull;&bull;&bull;\n                    {{ cardDetail.maskedPan.slice(-4) }}\n\n                    @if (cardDetail.balance; as balance) {\n                        <div class=\"price\">\n                            | Balance: &nbsp;&#8377;&nbsp;{{ balance }}\n                            @if (description) {\n                                ({{ description }})\n                            }\n                        </div>\n                    }\n                </span>\n            </div>\n        </div>\n\n        <!--delete option-->\n        @if (!isPaymentPage) {\n            <a class=\"delete-container\" href=\"javascript:void(0);\" (click)=\"remove()\" title=\"Delete this saved card\">\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/delete-payment-option.svg'\" alt=\"Delete\" />\n            </a>\n        }\n\n        <!-- radio selection -->\n        @if (isPaymentPage && active) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n</div>\n", styles: [".card-number{color:#7c8495;font-size:12px;font-weight:400;line-height:13px;letter-spacing:.1px;margin-top:2px;display:flex;align-items:center;gap:5px;flex-wrap:wrap}.price{display:flex;align-items:center;font-weight:700}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: PaymentService }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], finalCartValue: [{
                type: Input
            }], isPaymentPage: [{
                type: Input
            }], savedCardInfo: [{
                type: Input
            }], sodexoPaymentOption: [{
                type: Input
            }], selectedCardDetails: [{
                type: Output
            }], removeCard: [{
                type: Output
            }] } });

class SodexoCardViewComponent {
    sodexoCardService;
    deviceService;
    selected;
    option;
    finalCartValue;
    onSuccess = new EventEmitter();
    selectedPaymentOption = new EventEmitter();
    inProgress = false;
    isMobile = false;
    showSavedCard;
    constructor(sodexoCardService, deviceService) {
        this.sodexoCardService = sodexoCardService;
        this.deviceService = deviceService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.showSavedCard = this.option.is_saved;
    }
    select(cardDetails) {
        const { sourceId } = cardDetails;
        this.sodexoCardService.cardSourceId.next(sourceId);
        this.selectedPaymentOption.emit(this.option);
    }
    handleMakePayment() {
        this.sodexoCardService.use_saved_card = false;
        this.onSuccess.emit(this.option);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoCardViewComponent, deps: [{ token: SodexoCardService }, { token: 'app-device-service' }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SodexoCardViewComponent, isStandalone: true, selector: "lib-sodexo-card-view", inputs: { selected: "selected", option: "option", finalCartValue: "finalCartValue" }, outputs: { onSuccess: "onSuccess", selectedPaymentOption: "selectedPaymentOption" }, ngImport: i0, template: "@if (showSavedCard) {\n    <lib-sodexo-saved-card\n        [selected]=\"selected\"\n        [option]=\"option\"\n        [finalCartValue]=\"finalCartValue\"\n        (selectedCardDetails)=\"select($event)\">\n    </lib-sodexo-saved-card>\n}\n@if (!showSavedCard) {\n    <div class=\"option\">\n        <div class=\"payment-option-item\" (click)=\"handleMakePayment()\">\n            <div class=\"payment-option-item-content\">\n                <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                <div>\n                    <span class=\"payment-label\" style=\"padding: 0\"> Add Pluxee | Sodexo </span>\n                </div>\n            </div>\n            <span class=\"card-add\"> ADD </span>\n        </div>\n    </div>\n}\n", styles: [".card-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}\n"], dependencies: [{ kind: "component", type: SodexoSavedCardComponent, selector: "lib-sodexo-saved-card", inputs: ["selected", "option", "finalCartValue", "isPaymentPage", "savedCardInfo", "sodexoPaymentOption"], outputs: ["selectedCardDetails", "removeCard"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SodexoCardViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-sodexo-card-view', standalone: true, imports: [SodexoSavedCardComponent, DeferImgLoadDirective], template: "@if (showSavedCard) {\n    <lib-sodexo-saved-card\n        [selected]=\"selected\"\n        [option]=\"option\"\n        [finalCartValue]=\"finalCartValue\"\n        (selectedCardDetails)=\"select($event)\">\n    </lib-sodexo-saved-card>\n}\n@if (!showSavedCard) {\n    <div class=\"option\">\n        <div class=\"payment-option-item\" (click)=\"handleMakePayment()\">\n            <div class=\"payment-option-item-content\">\n                <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                <div>\n                    <span class=\"payment-label\" style=\"padding: 0\"> Add Pluxee | Sodexo </span>\n                </div>\n            </div>\n            <span class=\"card-add\"> ADD </span>\n        </div>\n    </div>\n}\n", styles: [".card-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}\n"] }]
        }], ctorParameters: () => [{ type: SodexoCardService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-device-service']
                }] }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], finalCartValue: [{
                type: Input
            }], onSuccess: [{
                type: Output
            }], selectedPaymentOption: [{
                type: Output
            }] } });

class GooglePayComponent {
    upiPayment;
    utilityService;
    storageService;
    gpayInput;
    inProgress = false;
    constructor(upiPayment, utilityService, storageService) {
        this.upiPayment = upiPayment;
        this.utilityService = utilityService;
        this.storageService = storageService;
    }
    ngOnInit() {
        const phoneNo = this.storageService.get("phone_no") || "";
        const phoneRegex = this.utilityService.phoneRegex;
        this.upiPayment.customerPhone = phoneNo;
        this.gpayInput = new FormGroup({
            googleMobile: new FormControl(phoneNo, Validators.compose([
                Validators.pattern(phoneRegex)
            ]))
        });
        this.gpayInput.controls["googleMobile"].valueChanges.subscribe((phoneNumber) => {
            if (String(phoneNumber).match(phoneRegex)) {
                this.upiPayment.customerPhone = String(phoneNumber);
            }
            else {
                this.upiPayment.customerPhone = null;
            }
        });
    }
    allowOnlyNumbers(event) {
        this.utilityService.allowOnlyNumbers(event);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GooglePayComponent, deps: [{ token: UpiPaymentService }, { token: UtilityService }, { token: "app-storage-service" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: GooglePayComponent, isStandalone: true, selector: "lib-google-pay", ngImport: i0, template: "<form name=\"gpayInput\" [formGroup]=\"gpayInput\">\n  <div class=\"col-md-8 col-xs-12 input-height\">\n    <div class=\"input-wrapper\">\n      <span class=\"input-label\"> MOBILE NUMBER </span>\n      <input\n        (click)=\"$event.stopPropagation()\"\n        (keypress)=\"allowOnlyNumbers($event)\"\n        (paste)=\"allowOnlyNumbers($event)\"\n        matInput\n        class=\"input-field\"\n        required\n        formControlName=\"googleMobile\"\n        type=\"tel\"\n        placeholder=\"MOBILE NUMBER\"\n        />\n    </div>\n    <mat-error>\n      @if (\n        gpayInput.get('googleMobile').hasError('pattern') ||\n        gpayInput.get('googleMobile').hasError('required')\n        ) {\n        <div class=\"mask\">\n          <span\n          class=\"error-img\"\n          >\n          Please enter valid mobile number\n        </span>\n        </div>\n       \n      }\n    </mat-error>\n  </div>\n</form>\n", styles: [".mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain}.mask .error-img{display:block;width:12px;height:12px;background-color:#cf5c63}.input-height{padding:10px 0 0;width:100%}.input-field{width:calc(100% - 120px)}.google-pay-container{padding:0}.cvv-wrapper{display:flex;padding:8px;justify-content:space-between;align-items:center;align-self:stretch;border-radius:8px;background:#f2f3f4}.enter-cvv{color:#0c0d0d;font-size:12px;font-weight:600;line-height:15.6px;letter-spacing:.12px}\n"], dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i3.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "directive", type: MatError, selector: "mat-error, [matError]", inputs: ["id"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GooglePayComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-google-pay", standalone: true, imports: [FormsModule, ReactiveFormsModule, MatInput, MatError], template: "<form name=\"gpayInput\" [formGroup]=\"gpayInput\">\n  <div class=\"col-md-8 col-xs-12 input-height\">\n    <div class=\"input-wrapper\">\n      <span class=\"input-label\"> MOBILE NUMBER </span>\n      <input\n        (click)=\"$event.stopPropagation()\"\n        (keypress)=\"allowOnlyNumbers($event)\"\n        (paste)=\"allowOnlyNumbers($event)\"\n        matInput\n        class=\"input-field\"\n        required\n        formControlName=\"googleMobile\"\n        type=\"tel\"\n        placeholder=\"MOBILE NUMBER\"\n        />\n    </div>\n    <mat-error>\n      @if (\n        gpayInput.get('googleMobile').hasError('pattern') ||\n        gpayInput.get('googleMobile').hasError('required')\n        ) {\n        <div class=\"mask\">\n          <span\n          class=\"error-img\"\n          >\n          Please enter valid mobile number\n        </span>\n        </div>\n       \n      }\n    </mat-error>\n  </div>\n</form>\n", styles: [".mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain}.mask .error-img{display:block;width:12px;height:12px;background-color:#cf5c63}.input-height{padding:10px 0 0;width:100%}.input-field{width:calc(100% - 120px)}.google-pay-container{padding:0}.cvv-wrapper{display:flex;padding:8px;justify-content:space-between;align-items:center;align-self:stretch;border-radius:8px;background:#f2f3f4}.enter-cvv{color:#0c0d0d;font-size:12px;font-weight:600;line-height:15.6px;letter-spacing:.12px}\n"] }]
        }], ctorParameters: () => [{ type: UpiPaymentService }, { type: UtilityService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }] });

class GpayComponent {
    deviceService;
    selected;
    option;
    selectedPaymentOption = new EventEmitter();
    isSelected;
    showBorder;
    isMobile = false;
    constructor(deviceService) {
        this.deviceService = deviceService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnChanges(changes) {
        if (changes) {
            this.isSelected = this.option.identifier === this.selected.option.identifier;
            this.showBorder = this.isSelected && !this.isMobile && this.option.active;
        }
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = "/assets/images/default_payment_option.svg";
    }
    select(event) {
        this.selectedPaymentOption.emit(this.option);
        event.stopPropagation();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GpayComponent, deps: [{ token: "app-device-service" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: GpayComponent, isStandalone: true, selector: "lib-gpay", inputs: { selected: "selected", option: "option" }, outputs: { selectedPaymentOption: "selectedPaymentOption" }, usesOnChanges: true, ngImport: i0, template: "<div\n    class=\"option\"\n    (click)=\"option.active && select($event)\"\n    [style.opacity]=\"option.active ? '1' : '0.4'\"\n    [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n            <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"option.image\" alt=\"{{ option.name }}\" (error)=\"setDefaultPic($event)\" />\n            <div>\n                <span class=\"payment-label\">\n                    {{ option.label }}\n                    @if (option.remark; as remark) {\n                        <span class=\"low-success-remark\" [style.color]=\"option.active ? '#f37d7d' : '#606060'\">\n                            {{ remark }}\n                        </span>\n                    }\n                </span>\n\n                <div class=\"payment-option-description\">\n                    {{ option.description }}\n                </div>\n            </div>\n        </div>\n        @if (option.active) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n\n    @if (isSelected && option.active) {\n        <lib-google-pay style=\"width: 100%; display: inline-block\"> </lib-google-pay>\n    }\n</div>\n", styles: [""], dependencies: [{ kind: "component", type: MatRadioButton, selector: "mat-radio-button", inputs: ["id", "name", "aria-label", "aria-labelledby", "aria-describedby", "disableRipple", "tabIndex", "checked", "value", "labelPosition", "disabled", "required", "color", "disabledInteractive"], outputs: ["change"], exportAs: ["matRadioButton"] }, { kind: "component", type: GooglePayComponent, selector: "lib-google-pay" }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: GpayComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-gpay", standalone: true, imports: [MatRadioButton, GooglePayComponent, DeferImgLoadDirective], template: "<div\n    class=\"option\"\n    (click)=\"option.active && select($event)\"\n    [style.opacity]=\"option.active ? '1' : '0.4'\"\n    [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\">\n    <div class=\"payment-option-item\">\n        <div class=\"payment-option-item-content\">\n            <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"option.image\" alt=\"{{ option.name }}\" (error)=\"setDefaultPic($event)\" />\n            <div>\n                <span class=\"payment-label\">\n                    {{ option.label }}\n                    @if (option.remark; as remark) {\n                        <span class=\"low-success-remark\" [style.color]=\"option.active ? '#f37d7d' : '#606060'\">\n                            {{ remark }}\n                        </span>\n                    }\n                </span>\n\n                <div class=\"payment-option-description\">\n                    {{ option.description }}\n                </div>\n            </div>\n        </div>\n        @if (option.active) {\n            <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isSelected\"> </mat-radio-button>\n        }\n    </div>\n\n    @if (isSelected && option.active) {\n        <lib-google-pay style=\"width: 100%; display: inline-block\"> </lib-google-pay>\n    }\n</div>\n" }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], selectedPaymentOption: [{
                type: Output
            }] } });

class UpiPaymentComponent {
    upiPaymentService;
    utilitySerice;
    toastrService;
    gtmService;
    storageService;
    environment;
    upiInput;
    destroySubject$ = new Subject();
    isError = false;
    validationMessage = "This UPI ID will be saved after the transaction is completed successfully.";
    constructor(upiPaymentService, utilitySerice, toastrService, gtmService, storageService, environment) {
        this.upiPaymentService = upiPaymentService;
        this.utilitySerice = utilitySerice;
        this.toastrService = toastrService;
        this.gtmService = gtmService;
        this.storageService = storageService;
        this.environment = environment;
    }
    ngOnInit() {
        const regex = this.utilitySerice.upiRegex;
        this.upiInput = new FormGroup({
            vpa: new FormControl("", Validators.compose([
                Validators.required,
                Validators.pattern(regex)
            ]))
        });
        this.upiInput.controls["vpa"].valueChanges.subscribe((vpa) => {
            if (!vpa)
                return;
            if (!vpa.match(regex)) {
                this.setVPA(null);
                return;
            }
            this.setVPA(vpa);
        });
        this.upiPaymentService.vpaValidationResponse
            .pipe(takeUntil$1(this.destroySubject$))
            .subscribe((response) => {
            if (response) {
                this.validationMessage = response.message;
                this.isError = response.isError;
            }
            if (this.isError) {
                this.gtmService.logPaymentEvent("custom_upi", "Incorrect Upi Id");
                this.toastrService.openSnackBar(response.message, "OKAY!");
            }
        });
    }
    setVPA(vpa) {
        this.upiPaymentService.customerVpa = vpa;
    }
    resetValue() {
        this.validationMessage = "";
        this.isError = false;
    }
    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
        this.upiPaymentService.vpaValidationResponse.next(null);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentComponent, deps: [{ token: UpiPaymentService }, { token: UtilityService }, { token: ToastrService }, { token: "app-gtm-service" }, { token: "app-storage-service" }, { token: "app-environment" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: UpiPaymentComponent, isStandalone: true, selector: "lib-upi-payment", ngImport: i0, template: "<form name=\"upiInput\" [formGroup]=\"upiInput\" (keydown)=\"resetValue()\">\n    <div class=\"col-md-8 col-xs-12 input-height\">\n        <div class=\"input-wrapper\">\n            <span class=\"input-label\">\n                UPI ID\n            </span>\n            <input matInput class=\"input-field\" type=\"text\" formControlName=\"vpa\" />\n        </div>\n        @if (validationMessage) {\n          <span class=\"upi-info-msg\" [ngClass]=\"isError ? 'upi-error' : 'upi-success'\">\n            @if (isError) {\n              <div class=\"mask\">\n                <span class=\"error-img\">\n              </span>\n            </div>\n            }\n            {{ validationMessage }}\n          </span>\n        }\n    </div>    \n</form>\n", styles: [".upi-info-msg{font-weight:400;position:relative;display:flex;text-align:left;margin-top:10px}.upi-error{color:#cf5c63;font-size:12px;line-height:15.6px}.mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain}.mask .error-img{display:block;width:12px;height:12px;background-color:#cf5c63}.upi-success{line-height:12.5px;color:#707786;font-size:10px;margin-top:10px}.input-height{padding:10px 0 0;width:100%}.input-field{width:calc(100% - 50px)}.mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain;width:14px;margin-right:6px}.mask .error-img{width:14px;height:14px;background-color:#cf5c63}\n"], dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UpiPaymentComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-upi-payment", standalone: true, imports: [FormsModule, ReactiveFormsModule, MatInput, NgClass], template: "<form name=\"upiInput\" [formGroup]=\"upiInput\" (keydown)=\"resetValue()\">\n    <div class=\"col-md-8 col-xs-12 input-height\">\n        <div class=\"input-wrapper\">\n            <span class=\"input-label\">\n                UPI ID\n            </span>\n            <input matInput class=\"input-field\" type=\"text\" formControlName=\"vpa\" />\n        </div>\n        @if (validationMessage) {\n          <span class=\"upi-info-msg\" [ngClass]=\"isError ? 'upi-error' : 'upi-success'\">\n            @if (isError) {\n              <div class=\"mask\">\n                <span class=\"error-img\">\n              </span>\n            </div>\n            }\n            {{ validationMessage }}\n          </span>\n        }\n    </div>    \n</form>\n", styles: [".upi-info-msg{font-weight:400;position:relative;display:flex;text-align:left;margin-top:10px}.upi-error{color:#cf5c63;font-size:12px;line-height:15.6px}.mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain}.mask .error-img{display:block;width:12px;height:12px;background-color:#cf5c63}.upi-success{line-height:12.5px;color:#707786;font-size:10px;margin-top:10px}.input-height{padding:10px 0 0;width:100%}.input-field{width:calc(100% - 50px)}.mask{mask:url(/assets/images/alert.svg) no-repeat center/contain;-webkit-mask:url(/assets/images/alert.svg) no-repeat center/contain;width:14px;margin-right:6px}.mask .error-img{width:14px;height:14px;background-color:#cf5c63}\n"] }]
        }], ctorParameters: () => [{ type: UpiPaymentService }, { type: UtilityService }, { type: ToastrService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-storage-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }] });

class SavedUpiComponent {
    environment;
    gtmService;
    deviceService;
    upiService;
    selected;
    option;
    selectedPaymentOption = new EventEmitter();
    showSavedUpi;
    isSelected;
    showBorder;
    isMobile = false;
    constructor(environment, gtmService, deviceService, upiService) {
        this.environment = environment;
        this.gtmService = gtmService;
        this.deviceService = deviceService;
        this.upiService = upiService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.showSavedUpi = this.option.is_saved;
    }
    ngOnChanges(changes) {
        if (changes) {
            this.isSelected = this.option.identifier === this.selected.option.identifier;
            this.showBorder = this.isSelected && !this.isMobile && this.option.active;
        }
    }
    select(e) {
        const { is_saved, saved_information } = this.option;
        if (is_saved) {
            const { saved_vpa } = saved_information;
            this.upiService.customerVpa = saved_vpa;
            this.upiService.isValid.next(true);
        }
        else {
            this.upiService.customerVpa = "";
            this.upiService.isValid.next(false);
            this.gtmService.logPaymentEvent("custom_upi", "Add New Upi");
        }
        this.selectedPaymentOption.emit(this.option);
        e.stopPropagation();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SavedUpiComponent, deps: [{ token: "app-environment" }, { token: "app-gtm-service" }, { token: "app-device-service" }, { token: UpiPaymentService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SavedUpiComponent, isStandalone: true, selector: "lib-saved-upi", inputs: { selected: "selected", option: "option" }, outputs: { selectedPaymentOption: "selectedPaymentOption" }, usesOnChanges: true, ngImport: i0, template: "<div (click)=\"option.active && select($event)\">\n    @if (!showSavedUpi) {\n    <div\n        class=\"option\"\n        (click)=\"option.active && select($event)\"\n        [style.opacity]=\"option.active ? '1' : '0.4'\"\n        [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\"\n    >\n        <div class=\"payment-option-item\">\n            <div class=\"payment-option-item-content\">\n                <img libDeferImgLoad class=\"payment-option-img\" [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                <div>\n                    <span class=\"payment-label\" style=\"padding: 0\">\n                        Add New UPI ID\n                    </span>\n                </div>\n            </div>\n            @if (isSelected) {\n            <mat-radio-button\n                style=\"width: 8%\"\n                color=\"primary\"\n                type=\"radio\"\n                [checked]=\"isSelected\"\n            ></mat-radio-button>\n            } @else {\n            <span class=\"upi-add\"> ADD </span>\n            }\n        </div>\n        @if (isSelected && option.active) {\n        <lib-upi-payment style=\"width: 100%; display: inline-block\">\n        </lib-upi-payment>\n        }\n    </div>\n    } @else {\n    <lib-payment-icon\n        [selected]=\"selected\"\n        [option]=\"option\"\n    ></lib-payment-icon>\n    }\n</div>\n", styles: [".upi-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}\n"], dependencies: [{ kind: "component", type: MatRadioButton, selector: "mat-radio-button", inputs: ["id", "name", "aria-label", "aria-labelledby", "aria-describedby", "disableRipple", "tabIndex", "checked", "value", "labelPosition", "disabled", "required", "color", "disabledInteractive"], outputs: ["change"], exportAs: ["matRadioButton"] }, { kind: "component", type: UpiPaymentComponent, selector: "lib-upi-payment" }, { kind: "component", type: PaymentIconComponent, selector: "lib-payment-icon", inputs: ["selected", "option", "panelExpanded"], outputs: ["selectedPaymentOption"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SavedUpiComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-saved-upi", standalone: true, imports: [MatRadioButton, UpiPaymentComponent, PaymentIconComponent, DeferImgLoadDirective], template: "<div (click)=\"option.active && select($event)\">\n    @if (!showSavedUpi) {\n    <div\n        class=\"option\"\n        (click)=\"option.active && select($event)\"\n        [style.opacity]=\"option.active ? '1' : '0.4'\"\n        [style.border]=\"showBorder ? '2px solid var(--primary-color)' : ''\"\n    >\n        <div class=\"payment-option-item\">\n            <div class=\"payment-option-item-content\">\n                <img libDeferImgLoad class=\"payment-option-img\" [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                <div>\n                    <span class=\"payment-label\" style=\"padding: 0\">\n                        Add New UPI ID\n                    </span>\n                </div>\n            </div>\n            @if (isSelected) {\n            <mat-radio-button\n                style=\"width: 8%\"\n                color=\"primary\"\n                type=\"radio\"\n                [checked]=\"isSelected\"\n            ></mat-radio-button>\n            } @else {\n            <span class=\"upi-add\"> ADD </span>\n            }\n        </div>\n        @if (isSelected && option.active) {\n        <lib-upi-payment style=\"width: 100%; display: inline-block\">\n        </lib-upi-payment>\n        }\n    </div>\n    } @else {\n    <lib-payment-icon\n        [selected]=\"selected\"\n        [option]=\"option\"\n    ></lib-payment-icon>\n    }\n</div>\n", styles: [".upi-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: UpiPaymentService }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], selectedPaymentOption: [{
                type: Output
            }] } });

class SecureCardBottomSheetComponent {
    _bottomSheetRef;
    environment;
    data;
    DEPLOY_URL;
    isOutdated = false;
    isBottomSheet = true;
    constructor(_bottomSheetRef, environment, data) {
        this._bottomSheetRef = _bottomSheetRef;
        this.environment = environment;
        this.data = data;
        this.DEPLOY_URL = this.environment.DEPLOY_URL;
    }
    ngOnInit() {
        this.isOutdated = this.data.isOutdated;
    }
    close(close) {
        this._bottomSheetRef.dismiss(close);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SecureCardBottomSheetComponent, deps: [{ token: i1$2.MatBottomSheetRef }, { token: 'app-environment' }, { token: MAT_BOTTOM_SHEET_DATA }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SecureCardBottomSheetComponent, isStandalone: true, selector: "lib-secure-card-bottom-sheet", ngImport: i0, template: "@if (!isBottomSheet) {\n  <div class=\"dialog-box\">\n    <div class=\"close\" (click)=\"close()\">\n      <img  style=\"height: 25px;\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n    </div>\n    @if (!isOutdated) {\n      <div>\n        <div>\n          <div>\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"img-container\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_dialog.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div class=\"content-container\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n\n@if (isBottomSheet) {\n  <div class=\"bottom-sheet\">\n    @if (!isOutdated) {\n      <div>\n        <div class=\"close-button\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div class=\"container\">\n          <div class=\"title-container box\">\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"box stack-top\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_card.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div class=\"close-button\" style=\"margin: 0 15px\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div style=\"margin: 15px; font-size: 16px;\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n", styles: ["@charset \"UTF-8\";.dialog-box .close{float:right;cursor:pointer}.dialog-box .img-container{display:flex;align-items:center;justify-content:center;padding-top:15px}.dialog-box .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.dialog-box .button-container button{height:40px;border-radius:5px;margin-top:10px}.dialog-box .button-container .button-background{color:#fff}.dialog-box .content-container{margin:15px;font-size:16px;color:#555656}ul{font-size:16px;margin:30px}li{margin:5px}.title{font-size:20px;margin:20px 10px;font-weight:900;line-height:1.2;width:80%}ul li:before{content:\"\\2022\";color:var(--feedback-theme);font-weight:700;display:inline-block;width:.8em;margin-left:-.8em;font-size:25px;vertical-align:sub}.bottom-sheet .title-container{height:200px;background-color:var(--bottom-sheet-background)}.bottom-sheet .close-button{margin:15px;position:absolute;right:0;z-index:10;background-color:var(--bottom-sheet-background)}.bottom-sheet .container{height:300px;position:relative}.bottom-sheet .box{position:absolute;top:0;left:0;opacity:.8}.bottom-sheet .stack-top{z-index:9;top:100px}.bottom-sheet .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.bottom-sheet .button-container button{height:40px;border-radius:5px;margin-top:10px}.bottom-sheet .button-container .button-background{color:#fff}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SecureCardBottomSheetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-secure-card-bottom-sheet', standalone: true, template: "@if (!isBottomSheet) {\n  <div class=\"dialog-box\">\n    <div class=\"close\" (click)=\"close()\">\n      <img  style=\"height: 25px;\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n    </div>\n    @if (!isOutdated) {\n      <div>\n        <div>\n          <div>\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"img-container\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_dialog.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div class=\"content-container\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n\n@if (isBottomSheet) {\n  <div class=\"bottom-sheet\">\n    @if (!isOutdated) {\n      <div>\n        <div class=\"close-button\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div class=\"container\">\n          <div class=\"title-container box\">\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"box stack-top\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_card.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div class=\"close-button\" style=\"margin: 0 15px\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div style=\"margin: 15px; font-size: 16px;\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n", styles: ["@charset \"UTF-8\";.dialog-box .close{float:right;cursor:pointer}.dialog-box .img-container{display:flex;align-items:center;justify-content:center;padding-top:15px}.dialog-box .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.dialog-box .button-container button{height:40px;border-radius:5px;margin-top:10px}.dialog-box .button-container .button-background{color:#fff}.dialog-box .content-container{margin:15px;font-size:16px;color:#555656}ul{font-size:16px;margin:30px}li{margin:5px}.title{font-size:20px;margin:20px 10px;font-weight:900;line-height:1.2;width:80%}ul li:before{content:\"\\2022\";color:var(--feedback-theme);font-weight:700;display:inline-block;width:.8em;margin-left:-.8em;font-size:25px;vertical-align:sub}.bottom-sheet .title-container{height:200px;background-color:var(--bottom-sheet-background)}.bottom-sheet .close-button{margin:15px;position:absolute;right:0;z-index:10;background-color:var(--bottom-sheet-background)}.bottom-sheet .container{height:300px;position:relative}.bottom-sheet .box{position:absolute;top:0;left:0;opacity:.8}.bottom-sheet .stack-top{z-index:9;top:100px}.bottom-sheet .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.bottom-sheet .button-container button{height:40px;border-radius:5px;margin-top:10px}.bottom-sheet .button-container .button-background{color:#fff}\n"] }]
        }], ctorParameters: () => [{ type: i1$2.MatBottomSheetRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-environment']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_BOTTOM_SHEET_DATA]
                }] }] });

class SecureCardDialogComponent {
    dialogRef;
    data;
    environment;
    constructor(dialogRef, data, environment) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.environment = environment;
        this.DEPLOY_URL = this.environment.DEPLOY_URL;
    }
    DEPLOY_URL;
    isOutdated = false;
    isBottomSheet = false;
    ngOnInit() {
        this.isOutdated = this.data.isOutdated;
    }
    close(close) {
        this.dialogRef.close(close);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SecureCardDialogComponent, deps: [{ token: i1$1.MatDialogRef }, { token: MAT_DIALOG_DATA }, { token: "app-environment" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SecureCardDialogComponent, isStandalone: true, selector: "lib-secure-card-dialog", ngImport: i0, template: "@if (!isBottomSheet) {\n  <div class=\"dialog-box\">\n    <div class=\"close\" (click)=\"close()\">\n      <img  style=\"height: 25px;\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n    </div>\n    @if (!isOutdated) {\n      <div>\n        <div>\n          <div>\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"img-container\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_dialog.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div class=\"content-container\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n\n@if (isBottomSheet) {\n  <div class=\"bottom-sheet\">\n    @if (!isOutdated) {\n      <div>\n        <div class=\"close-button\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div class=\"container\">\n          <div class=\"title-container box\">\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"box stack-top\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_card.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div class=\"close-button\" style=\"margin: 0 15px\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div style=\"margin: 15px; font-size: 16px;\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n", styles: ["@charset \"UTF-8\";.dialog-box .close{float:right;cursor:pointer}.dialog-box .img-container{display:flex;align-items:center;justify-content:center;padding-top:15px}.dialog-box .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.dialog-box .button-container button{height:40px;border-radius:5px;margin-top:10px}.dialog-box .button-container .button-background{color:#fff}.dialog-box .content-container{margin:15px;font-size:16px;color:#555656}ul{font-size:16px;margin:30px}li{margin:5px}.title{font-size:20px;margin:20px 10px;font-weight:900;line-height:1.2;width:80%}ul li:before{content:\"\\2022\";color:var(--feedback-theme);font-weight:700;display:inline-block;width:.8em;margin-left:-.8em;font-size:25px;vertical-align:sub}.bottom-sheet .title-container{height:200px;background-color:var(--bottom-sheet-background)}.bottom-sheet .close-button{margin:15px;position:absolute;right:0;z-index:10;background-color:var(--bottom-sheet-background)}.bottom-sheet .container{height:300px;position:relative}.bottom-sheet .box{position:absolute;top:0;left:0;opacity:.8}.bottom-sheet .stack-top{z-index:9;top:100px}.bottom-sheet .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.bottom-sheet .button-container button{height:40px;border-radius:5px;margin-top:10px}.bottom-sheet .button-container .button-background{color:#fff}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SecureCardDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-secure-card-dialog", standalone: true, template: "@if (!isBottomSheet) {\n  <div class=\"dialog-box\">\n    <div class=\"close\" (click)=\"close()\">\n      <img  style=\"height: 25px;\" src=\"{{DEPLOY_URL}}/assets/images/cross.svg\">\n    </div>\n    @if (!isOutdated) {\n      <div>\n        <div>\n          <div>\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"img-container\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_dialog.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div class=\"content-container\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n\n@if (isBottomSheet) {\n  <div class=\"bottom-sheet\">\n    @if (!isOutdated) {\n      <div>\n        <div class=\"close-button\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div class=\"container\">\n          <div class=\"title-container box\">\n            <p class=\"title\">\n              As per RBI Guidelines, your card will now be saved with your payment network\n            </p>\n          </div>\n          <div class=\"box stack-top\">\n            <img src=\"{{DEPLOY_URL}}/assets/images/illustration_secure_card.svg\">\n          </div>\n        </div>\n        <div>\n          <ul>\n            <li>Upgraded Payment Security with payment network (Visa/Mastercard/Rupay)</li>\n            <li>No need to re-enter your card details everytime</li>\n          </ul>\n        </div>\n      </div>\n    }\n    @if (isOutdated) {\n      <div>\n        <div class=\"close-button\" style=\"margin: 0 15px\" (click)=\"close()\">\n          <img  style=\"height: 15px;\" src=\"{{DEPLOY_URL}}/assets/images/close.png\">\n        </div>\n        <div>\n          <p class=\"title\">\n            This card cannot be saved\n          </p>\n        </div>\n        <div style=\"margin: 15px; font-size: 16px;\">\n          <p>\n            In accordance with the new RBI guidelines, your card cannot be saved as your bank has not yet implemented the regulations necessary for tokenization.\n          </p>\n          <p>\n            Until it's done, you may have to manually enter the details every time you transact with this card or you can use another card.\n          </p>\n        </div>\n        <div class=\"button-container\">\n          <button btn-primary class=\"button-background\" style=\"background-color: var(--cart-button-color);\" size=\"full\" (click)=\"close('continue')\">CONTINUE</button>\n          <button btn-primary (click)=\"close('close')\">\n            Use Another Card\n          </button>\n        </div>\n      </div>\n    }\n  </div>\n}\n", styles: ["@charset \"UTF-8\";.dialog-box .close{float:right;cursor:pointer}.dialog-box .img-container{display:flex;align-items:center;justify-content:center;padding-top:15px}.dialog-box .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.dialog-box .button-container button{height:40px;border-radius:5px;margin-top:10px}.dialog-box .button-container .button-background{color:#fff}.dialog-box .content-container{margin:15px;font-size:16px;color:#555656}ul{font-size:16px;margin:30px}li{margin:5px}.title{font-size:20px;margin:20px 10px;font-weight:900;line-height:1.2;width:80%}ul li:before{content:\"\\2022\";color:var(--feedback-theme);font-weight:700;display:inline-block;width:.8em;margin-left:-.8em;font-size:25px;vertical-align:sub}.bottom-sheet .title-container{height:200px;background-color:var(--bottom-sheet-background)}.bottom-sheet .close-button{margin:15px;position:absolute;right:0;z-index:10;background-color:var(--bottom-sheet-background)}.bottom-sheet .container{height:300px;position:relative}.bottom-sheet .box{position:absolute;top:0;left:0;opacity:.8}.bottom-sheet .stack-top{z-index:9;top:100px}.bottom-sheet .button-container{display:flex;flex-direction:column;margin:10px;font-size:16px;font-weight:800}.bottom-sheet .button-container button{height:40px;border-radius:5px;margin-top:10px}.bottom-sheet .button-container .button-background{color:#fff}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }] });

class SavedCardComponent {
    deviceService;
    environment;
    bottomSheet;
    dialog;
    option;
    selected;
    // sent from profile page
    isPaymentPage = true;
    savedCardInfo;
    selectedCardDetails = new EventEmitter();
    removeCard = new EventEmitter();
    isMobile;
    cardDetail = {
        icon_url: "",
        saved_card_id: "",
        payment_type: "",
        expiry_date: "",
        last_four_digit: "",
        cvv_length: 0,
        cvv_required: false,
        is_card_coft: false,
        is_eligible_for_coft: false,
        bank_code: "",
        display_name: "",
        cvv: "",
        card_info: "",
        user_consent: 0,
        cardIssuer: ""
    };
    isCardSelected;
    showCoftInfo;
    DEPLOY_URL;
    constructor(deviceService, environment, bottomSheet, dialog) {
        this.deviceService = deviceService;
        this.environment = environment;
        this.bottomSheet = bottomSheet;
        this.dialog = dialog;
        this.DEPLOY_URL = this.environment.DEPLOY_URL;
    }
    ngOnInit() {
        this.isMobile = this.deviceService.isMobile();
        this.initializeCardDetails();
    }
    ngOnChanges(changes) {
        if (changes && this.isPaymentPage) {
            this.isCardSelected = this.option.identifier === this.selected.option.identifier;
        }
    }
    initializeCardDetails() {
        if (this.isPaymentPage) {
            const { card_info } = this.option.saved_information;
            const isSavedCard = card_info && Object.keys(card_info).length;
            if (!isSavedCard) {
                return;
            }
            this.cardDetail = {
                ...this.cardDetail,
                ...card_info
            };
            const { is_card_coft, is_eligible_for_coft } = this.cardDetail;
            this.showCoftInfo = !is_card_coft && is_eligible_for_coft;
            this.isCardSelected = this.option.identifier === this.selected.option.identifier;
        }
        else {
            this.cardDetail = this.savedCardInfo;
        }
    }
    selectCardConsent(event) {
        this.cardDetail.user_consent = event ? 1 : 0;
        this.select();
    }
    select() {
        const card = this.cardDetail;
        const { saved_card_id, cvv, is_card_coft, user_consent } = card;
        this.selectedCardDetails.emit({
            ...card,
            card_info: `${saved_card_id}||${cvv}|`,
            user_consent: is_card_coft ? 1 : user_consent
        });
    }
    remove() {
        this.removeCard.emit(this.cardDetail.saved_card_id);
    }
    openSecureCard() {
        if (this.isMobile) {
            this.bottomSheet.open(SecureCardBottomSheetComponent, {
                panelClass: "secure-card-sheet"
            });
        }
        else {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.panelClass = "secure-card-dialog";
            dialogConfig.data = {};
            dialogConfig.autoFocus = false;
            this.dialog.open(SecureCardDialogComponent, dialogConfig);
        }
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = "/assets/images/dummy.png";
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SavedCardComponent, deps: [{ token: "app-device-service" }, { token: "app-environment" }, { token: i1$2.MatBottomSheet }, { token: i1$1.MatDialog }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: SavedCardComponent, isStandalone: true, selector: "lib-saved-card", inputs: { option: "option", selected: "selected", isPaymentPage: "isPaymentPage", savedCardInfo: "savedCardInfo" }, outputs: { selectedCardDetails: "selectedCardDetails", removeCard: "removeCard" }, usesOnChanges: true, ngImport: i0, template: "@if (cardDetail) {\n    <div class=\"option\" (click)=\"select()\" [style.border]=\"!isMobile && isCardSelected ? '2px solid var(--primary-color)' : ''\">\n        <div class=\"payment-option-item\">\n            <div class=\"payment-option-item-content\">\n                <img\n                    class=\"payment-option-img\"\n                    libDeferImgLoad\n                    [imageSrc]=\"'https://assets.box8.co.in/payments/cards/' + cardDetail.icon_url\"\n                    (error)=\"setDefaultPic($event)\" />\n                <div style=\"display: flex; flex-direction: column\">\n                    <span class=\"payment-label\">\n                        {{ cardDetail.display_name }}\n                        @if (cardDetail.is_card_coft) {\n                            <img libDeferImgLoad [imageSrc]=\"'/assets/images/icon_shield-blue.svg'\" class=\"secured-img\" />\n                        }\n                    </span>\n                    <span class=\"card_number\">\n                        &bull;&bull;&bull;&bull;\n                        {{ cardDetail.last_four_digit }}\n                    </span>\n                </div>\n            </div>\n            @if (!isPaymentPage) {\n                <a class=\"delete-container\" href=\"javascript:void(0);\" (click)=\"remove()\" title=\"Delete this saved card\">\n                    <img libDeferImgLoad [imageSrc]=\"'assets/images/delete-payment-option.svg'\" alt=\"Delete\" />\n                </a>\n            }\n            @if (isPaymentPage) {\n                <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isCardSelected\"> </mat-radio-button>\n            }\n        </div>\n        @if (isPaymentPage && isCardSelected) {\n            @if (!cardDetail.cvv_required) {\n                <span class=\"payment-option-description cvv-less\">\n                    CVV is not required as your card is secured according to RBI guidelines.\n                </span>\n            }\n            <div class=\"clearfix\"></div>\n            @if (cardDetail.cvv_required) {\n                <div class=\"cvv-container\">\n                    <div class=\"input-wrapper\">\n                        <span class=\"input-label\"> Please enter your CVV </span>\n                        <input\n                            matInput\n                            placeholder=\"CVV\"\n                            type=\"password\"\n                            inputmode=\"numeric\"\n                            autocomplete=\"new-password\"\n                            [(ngModel)]=\"cardDetail.cvv\"\n                            maxlength=\"{{ cardDetail.cvv_length }}\"\n                            (ngModelChange)=\"select()\"\n                            class=\"input-field\" />\n                    </div>\n                    @if (showCoftInfo) {\n                        <div class=\"secure-card-container\">\n                            <mat-checkbox\n                                color=\"primary\"\n                                class=\"checkbox-padding\"\n                                [(ngModel)]=\"cardDetail.user_consent\"\n                                (ngModelChange)=\"selectCardConsent($event)\">\n                            </mat-checkbox>\n                            <div class=\"checkbox-label\">\n                                <span class=\"title\">Secure your Saved card</span><br />\n                                <span class=\"desc\">\n                                    The complete card number will only be known to your payment network and your bank.\n                                    <span class=\"underline-text\" (click)=\"openSecureCard()\"> Know more </span>\n                                </span>\n                            </div>\n                        </div>\n                    }\n                </div>\n            }\n        }\n    </div>\n}\n", styles: [".checkbox-label{white-space:normal;line-height:1.2;text-align:justify;padding-left:10px}.checkbox-label .title{font-size:15px;color:#7c8495;font-weight:800}.checkbox-label .desc{padding:5px 0;display:block;font-weight:400;color:#7c8495}.secure-card-container{width:auto;padding:10px 0;display:flex;margin:10px 0}.underline-text{text-decoration:underline;font-weight:700;color:#f47d38}.cvv-container{margin-top:12px}.input-field{width:17%}.card_number{color:#7c8495;font-size:12px;font-weight:400;line-height:13px;letter-spacing:.1px;margin-top:2px}.secured-img{height:16px;width:16px;margin-left:5px}.cvv-less{margin-top:12px;display:block;font-weight:300}@media (max-width: 991px){.secure-card-container{border:2px solid #fff9f5;background-color:#fff9f5;border-radius:10px;padding:10px}}\n"], dependencies: [{ kind: "component", type: MatRadioButton, selector: "mat-radio-button", inputs: ["id", "name", "aria-label", "aria-labelledby", "aria-describedby", "disableRipple", "tabIndex", "checked", "value", "labelPosition", "disabled", "required", "color", "disabledInteractive"], outputs: ["change"], exportAs: ["matRadioButton"] }, { kind: "directive", type: MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "directive", type: i3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: MatCheckbox, selector: "mat-checkbox", inputs: ["aria-label", "aria-labelledby", "aria-describedby", "id", "required", "labelPosition", "name", "value", "disableRipple", "tabIndex", "color", "disabledInteractive", "checked", "disabled", "indeterminate"], outputs: ["change", "indeterminateChange"], exportAs: ["matCheckbox"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SavedCardComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-saved-card", standalone: true, imports: [MatRadioButton, MatInput, FormsModule, MatCheckbox, DeferImgLoadDirective], template: "@if (cardDetail) {\n    <div class=\"option\" (click)=\"select()\" [style.border]=\"!isMobile && isCardSelected ? '2px solid var(--primary-color)' : ''\">\n        <div class=\"payment-option-item\">\n            <div class=\"payment-option-item-content\">\n                <img\n                    class=\"payment-option-img\"\n                    libDeferImgLoad\n                    [imageSrc]=\"'https://assets.box8.co.in/payments/cards/' + cardDetail.icon_url\"\n                    (error)=\"setDefaultPic($event)\" />\n                <div style=\"display: flex; flex-direction: column\">\n                    <span class=\"payment-label\">\n                        {{ cardDetail.display_name }}\n                        @if (cardDetail.is_card_coft) {\n                            <img libDeferImgLoad [imageSrc]=\"'/assets/images/icon_shield-blue.svg'\" class=\"secured-img\" />\n                        }\n                    </span>\n                    <span class=\"card_number\">\n                        &bull;&bull;&bull;&bull;\n                        {{ cardDetail.last_four_digit }}\n                    </span>\n                </div>\n            </div>\n            @if (!isPaymentPage) {\n                <a class=\"delete-container\" href=\"javascript:void(0);\" (click)=\"remove()\" title=\"Delete this saved card\">\n                    <img libDeferImgLoad [imageSrc]=\"'assets/images/delete-payment-option.svg'\" alt=\"Delete\" />\n                </a>\n            }\n            @if (isPaymentPage) {\n                <mat-radio-button style=\"width: 8%\" color=\"primary\" type=\"radio\" [checked]=\"isCardSelected\"> </mat-radio-button>\n            }\n        </div>\n        @if (isPaymentPage && isCardSelected) {\n            @if (!cardDetail.cvv_required) {\n                <span class=\"payment-option-description cvv-less\">\n                    CVV is not required as your card is secured according to RBI guidelines.\n                </span>\n            }\n            <div class=\"clearfix\"></div>\n            @if (cardDetail.cvv_required) {\n                <div class=\"cvv-container\">\n                    <div class=\"input-wrapper\">\n                        <span class=\"input-label\"> Please enter your CVV </span>\n                        <input\n                            matInput\n                            placeholder=\"CVV\"\n                            type=\"password\"\n                            inputmode=\"numeric\"\n                            autocomplete=\"new-password\"\n                            [(ngModel)]=\"cardDetail.cvv\"\n                            maxlength=\"{{ cardDetail.cvv_length }}\"\n                            (ngModelChange)=\"select()\"\n                            class=\"input-field\" />\n                    </div>\n                    @if (showCoftInfo) {\n                        <div class=\"secure-card-container\">\n                            <mat-checkbox\n                                color=\"primary\"\n                                class=\"checkbox-padding\"\n                                [(ngModel)]=\"cardDetail.user_consent\"\n                                (ngModelChange)=\"selectCardConsent($event)\">\n                            </mat-checkbox>\n                            <div class=\"checkbox-label\">\n                                <span class=\"title\">Secure your Saved card</span><br />\n                                <span class=\"desc\">\n                                    The complete card number will only be known to your payment network and your bank.\n                                    <span class=\"underline-text\" (click)=\"openSecureCard()\"> Know more </span>\n                                </span>\n                            </div>\n                        </div>\n                    }\n                </div>\n            }\n        }\n    </div>\n}\n", styles: [".checkbox-label{white-space:normal;line-height:1.2;text-align:justify;padding-left:10px}.checkbox-label .title{font-size:15px;color:#7c8495;font-weight:800}.checkbox-label .desc{padding:5px 0;display:block;font-weight:400;color:#7c8495}.secure-card-container{width:auto;padding:10px 0;display:flex;margin:10px 0}.underline-text{text-decoration:underline;font-weight:700;color:#f47d38}.cvv-container{margin-top:12px}.input-field{width:17%}.card_number{color:#7c8495;font-size:12px;font-weight:400;line-height:13px;letter-spacing:.1px;margin-top:2px}.secured-img{height:16px;width:16px;margin-left:5px}.cvv-less{margin-top:12px;display:block;font-weight:300}@media (max-width: 991px){.secure-card-container{border:2px solid #fff9f5;background-color:#fff9f5;border-radius:10px;padding:10px}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: i1$2.MatBottomSheet }, { type: i1$1.MatDialog }], propDecorators: { option: [{
                type: Input
            }], selected: [{
                type: Input
            }], isPaymentPage: [{
                type: Input
            }], savedCardInfo: [{
                type: Input
            }], selectedCardDetails: [{
                type: Output
            }], removeCard: [{
                type: Output
            }] } });

class CardViewComponent {
    pgCardService;
    deviceService;
    selected;
    option;
    cardFiltered = false;
    onPaymentClick = new EventEmitter();
    selectedPaymentOption = new EventEmitter();
    isMobile;
    showSavedCard;
    constructor(pgCardService, deviceService) {
        this.pgCardService = pgCardService;
        this.deviceService = deviceService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.showSavedCard = this.option.is_saved;
    }
    ngOnChanges(changes) {
        if (changes) {
            this.showSavedCard = this.option.is_saved;
        }
    }
    saveCardInfo(savedCard) {
        this.pgCardService.selectedSavedCardDetails = savedCard;
    }
    select(savedCard) {
        this.saveCardInfo(savedCard);
        this.selectedPaymentOption.emit(this.option);
    }
    newCardPayment() {
        this.selectedPaymentOption.emit(this.option);
        this.onPaymentClick.emit(this.option);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardViewComponent, deps: [{ token: PgCardService }, { token: 'app-device-service' }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: CardViewComponent, isStandalone: true, selector: "lib-card-view", inputs: { selected: "selected", option: "option", cardFiltered: "cardFiltered" }, outputs: { onPaymentClick: "onPaymentClick", selectedPaymentOption: "selectedPaymentOption" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"card-view-wrapper\">\n    @if (showSavedCard) {\n        <lib-saved-card [option]=\"option\" [selected]=\"selected\" (selectedCardDetails)=\"select($event)\"> </lib-saved-card>\n    }\n    @if (!showSavedCard) {\n        <div class=\"option\" (click)=\"newCardPayment()\">\n            <div class=\"payment-option-item\">\n                <div class=\"payment-option-item-content\">\n                    <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                    <div>\n                        <span class=\"payment-label\" style=\"padding: 0\"> Add New Debit/Credit Card </span>\n                    </div>\n                </div>\n                <span class=\"card-add\"> ADD </span>\n            </div>\n        </div>\n        @if (cardFiltered) {\n            <div class=\"filtered-card-info\">\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/filter-info.svg'\" style=\"margin-top: -4px\" />\n                Some of your saved cards might not be visible here as they are not valid for the applied coupon code.\n            </div>\n        }\n    }\n</div>\n", styles: [".filtered-card-info{padding:12px;height:max-content;border:1px solid #d8dadf;font-weight:600;font-size:12px;line-height:16px;letter-spacing:.01em;color:#3d3d3d;border-radius:4px;background:#f2f3f4}.card-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}@media screen and (max-width: 991px){.filtered-card-info{margin:20px 16px}}\n"], dependencies: [{ kind: "component", type: SavedCardComponent, selector: "lib-saved-card", inputs: ["option", "selected", "isPaymentPage", "savedCardInfo"], outputs: ["selectedCardDetails", "removeCard"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardViewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-card-view', standalone: true, imports: [SavedCardComponent, DeferImgLoadDirective], template: "<div class=\"card-view-wrapper\">\n    @if (showSavedCard) {\n        <lib-saved-card [option]=\"option\" [selected]=\"selected\" (selectedCardDetails)=\"select($event)\"> </lib-saved-card>\n    }\n    @if (!showSavedCard) {\n        <div class=\"option\" (click)=\"newCardPayment()\">\n            <div class=\"payment-option-item\">\n                <div class=\"payment-option-item-content\">\n                    <img class=\"payment-option-img\" libDeferImgLoad [imageSrc]=\"'/assets/images/plus_filled.svg'\" />\n                    <div>\n                        <span class=\"payment-label\" style=\"padding: 0\"> Add New Debit/Credit Card </span>\n                    </div>\n                </div>\n                <span class=\"card-add\"> ADD </span>\n            </div>\n        </div>\n        @if (cardFiltered) {\n            <div class=\"filtered-card-info\">\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/filter-info.svg'\" style=\"margin-top: -4px\" />\n                Some of your saved cards might not be visible here as they are not valid for the applied coupon code.\n            </div>\n        }\n    }\n</div>\n", styles: [".filtered-card-info{padding:12px;height:max-content;border:1px solid #d8dadf;font-weight:600;font-size:12px;line-height:16px;letter-spacing:.01em;color:#3d3d3d;border-radius:4px;background:#f2f3f4}.card-add{color:#1daaaa;font-size:14px;font-weight:700;line-height:18.2px}@media screen and (max-width: 991px){.filtered-card-info{margin:20px 16px}}\n"] }]
        }], ctorParameters: () => [{ type: PgCardService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-device-service']
                }] }], propDecorators: { selected: [{
                type: Input
            }], option: [{
                type: Input
            }], cardFiltered: [{
                type: Input
            }], onPaymentClick: [{
                type: Output
            }], selectedPaymentOption: [{
                type: Output
            }] } });

class PaymentButtonComponent {
    upiPaymentService;
    amazonPayService;
    utilityService;
    makePaymentButtonLoaderService;
    gtmService;
    deviceService;
    selectedOption;
    gateway;
    isZeroOrderPayment = false;
    finalCartValue;
    onSuccess = new EventEmitter();
    walletCodes = [PaymentOptionCode.AMAZON_PAY, PaymentOptionCode.AMAZON_PAY_LATER];
    upiCodes = [PaymentOptionCode.SAVED_ICICI_UPI];
    otherPaymentOptionCodes = [];
    selectedCode;
    handleMakePaymentInProgress = false;
    isMobile = false;
    paymentType = {
        verifyAndPay: false,
        linkAndPay: false,
        addAndPay: false,
        makePayment: false
    };
    paymentOptionCode;
    cartFinalAmount;
    destroySubject$ = new Subject();
    constructor(upiPaymentService, amazonPayService, utilityService, makePaymentButtonLoaderService, gtmService, deviceService) {
        this.upiPaymentService = upiPaymentService;
        this.amazonPayService = amazonPayService;
        this.utilityService = utilityService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.gtmService = gtmService;
        this.deviceService = deviceService;
        this.paymentOptionCode = PaymentOptionCode;
        this.otherPaymentOptionCodes = this.otherPaymentOptionCodes.concat(this.walletCodes, this.upiCodes);
    }
    ngOnInit() {
        this.makePaymentButtonLoaderService.inProgress
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((res) => {
            if (this.handleMakePaymentInProgress !== res) {
                this.checkPaymentOptions();
            }
            this.handleMakePaymentInProgress = res;
        });
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnChanges() {
        this.selectedCode = this.selectedOption.code;
        this.checkPaymentOptions();
        this.makePaymentButtonLoaderService.inProgress.next(false);
    }
    checkPaymentOptions() {
        this.setPaymentButton('');
        if (this.otherPaymentOptionCodes.indexOf(this.selectedCode) === -1) {
            this.setPaymentButton('makePayment');
        }
        else {
            this.checkOptionCodeType(this.selectedCode);
        }
    }
    checkOptionCodeType(optionCode) {
        if (this.upiCodes.indexOf(optionCode) >= 0) {
            this.handleUpiPayment();
        }
        else if (this.walletCodes.indexOf(optionCode) >= 0) {
            this.handleWalletPayment(optionCode);
        }
        else {
            this.setPaymentButton('');
        }
    }
    handleUpiPayment() {
        if (this.upiPaymentService.isValid.value) {
            this.setPaymentButton('makePayment');
        }
        else {
            // custom upi payment
            this.gtmService.logPaymentEvent("custom_upi", "Verify And Pay Upi");
            this.setPaymentButton('verifyAndPay');
        }
    }
    handleWalletPayment(optionCode) {
        switch (optionCode.toLowerCase()) {
            case PaymentOptionCode.AMAZON_PAY:
            case PaymentOptionCode.AMAZON_PAY_LATER:
                this.selectButtonType(this.amazonPayService);
                break;
            default:
                this.setPaymentButton('');
                break;
        }
    }
    selectButtonType(paymentType) {
        if (paymentType) {
            if (paymentType.showAddandPay) {
                this.setPaymentButton('addAndPay');
            }
            else if (!paymentType.isLinked) {
                this.setPaymentButton('linkAndPay');
            }
            else {
                this.setPaymentButton('makePayment');
            }
        }
    }
    setPaymentButton(buttonValue) {
        Object.keys(this.paymentType).map((key) => {
            if (key === buttonValue) {
                this.paymentType[key] = true;
            }
            else {
                this.paymentType[key] = false;
            }
        });
    }
    validateVpa() {
        const upiInput = this.upiPaymentService.customerVpa;
        if (!upiInput) {
            this.upiPaymentService.vpaValidationResponse.next({
                message: "Please enter a valid UPI ID",
                isError: true
            });
        }
        else {
            this.makePaymentButtonLoaderService.inProgress.emit(true);
            lastValueFrom(this.upiPaymentService.validateVpa(this.gateway, upiInput))
                .then((response) => {
                if (response && response.eligible) {
                    this.upiPaymentService.vpaValidationResponse.next({
                        message: 'VPA verified',
                        isError: false
                    });
                    this.handleMakePayment();
                }
                else {
                    this.makePaymentButtonLoaderService.inProgress.emit(false);
                    this.upiPaymentService.vpaValidationResponse.next({
                        message: response.message,
                        isError: true
                    });
                }
            }).catch((error) => {
                console.log(error);
                this.utilityService.handleResponse();
            }).finally(() => {
                this.makePaymentButtonLoaderService.inProgress.emit(false);
            });
        }
    }
    handleMakePayment() {
        this.onSuccess.emit();
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = "/assets/images/default_payment_option.svg";
    }
    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentButtonComponent, deps: [{ token: UpiPaymentService }, { token: AmazonPayService }, { token: UtilityService }, { token: MakePaymentButtonLoaderService }, { token: "app-gtm-service" }, { token: 'app-device-service' }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: PaymentButtonComponent, isStandalone: true, selector: "lib-payment-button", inputs: { selectedOption: "selectedOption", gateway: "gateway", isZeroOrderPayment: "isZeroOrderPayment", finalCartValue: "finalCartValue" }, outputs: { onSuccess: "onSuccess" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"space-50\"></div>\n<div class=\"clearfix\"></div>\n<div\n    class=\"col-sm-12 text-right button-wrapper\"\n    [ngClass]=\"{ 'button-container-mb': isMobile, 'button-container-web': !isMobile }\">\n    @if (isMobile && !isZeroOrderPayment) {\n        <img\n            libDeferImgLoad\n            [imageSrc]=\"\n                selectedOption.code == paymentOptionCode.PAYTM_CARD && selectedOption.is_saved\n                    ? 'https://assets.box8.co.in/payments/cards/' + selectedOption.saved_information.card_info.icon_url\n                    : selectedOption.image\n            \"\n            alt=\"{{ selectedOption.name }}\"\n            (error)=\"setDefaultPic($event)\"\n            class=\"img\" />\n        <div class=\"pay-via-wrapper\">\n            <div class=\"pay-via\">Pay via</div>\n            <div class=\"label\">{{ selectedOption.label }}</div>\n        </div>\n    }\n    <div [style.flex-grow]=\"isMobile ? 2 : null\">\n        @if (isZeroOrderPayment) {\n            <button\n                color=\"primary\"\n                (click)=\"handleMakePayment()\"\n                class=\"payment-button radius-3\"\n                [disabled]=\"handleMakePaymentInProgress\"\n                data-test=\"confirm-order\">\n                @if (!handleMakePaymentInProgress) {\n                    <span>Confirm Order</span>\n                }\n                @if (handleMakePaymentInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n        } @else {\n            @if (paymentType.makePayment) {\n                <button\n                    color=\"primary\"\n                    (click)=\"handleMakePayment()\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.verifyAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    (click)=\"validateVpa()\"\n                    [disabled]=\"handleMakePaymentInProgress\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Verify &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.addAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\"\n                    (click)=\"handleMakePayment()\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Add &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.linkAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\"\n                    (click)=\"handleMakePayment()\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Link &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n        }\n    </div>\n</div>\n", styles: [".text-right{text-align:right}.payment-button{min-width:141px;background-color:var(--primary-color);color:#fff;padding:12px}.button-container-mb{display:flex;justify-content:space-between;align-items:center}.button-container-web{position:absolute;bottom:0;background:#fff;box-shadow:0 -2px 4px #0000001a;padding:15px 30px 15px 60px}.img{width:30px;height:30px;margin-right:10px;object-fit:contain}.pay-via-wrapper{flex:1;text-align:left;width:30%;margin-right:12px}.pay-via{font-size:12px;font-weight:700;color:#5c5c5c;margin-bottom:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}@media screen and (max-width: 991px){.button-wrapper{position:fixed;bottom:0;left:0;background:#fff;padding:20px;width:100%;z-index:5;box-shadow:0 -8px 12px #0000000d}.payment-button{width:100%;max-height:40px;background-color:var(--cart-button-color)}.label{font-size:14px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: MatProgressSpinner, selector: "mat-progress-spinner, mat-spinner", inputs: ["color", "mode", "value", "diameter", "strokeWidth"], exportAs: ["matProgressSpinner"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-payment-button', standalone: true, imports: [NgClass, MatProgressSpinner, DeferImgLoadDirective], template: "<div class=\"space-50\"></div>\n<div class=\"clearfix\"></div>\n<div\n    class=\"col-sm-12 text-right button-wrapper\"\n    [ngClass]=\"{ 'button-container-mb': isMobile, 'button-container-web': !isMobile }\">\n    @if (isMobile && !isZeroOrderPayment) {\n        <img\n            libDeferImgLoad\n            [imageSrc]=\"\n                selectedOption.code == paymentOptionCode.PAYTM_CARD && selectedOption.is_saved\n                    ? 'https://assets.box8.co.in/payments/cards/' + selectedOption.saved_information.card_info.icon_url\n                    : selectedOption.image\n            \"\n            alt=\"{{ selectedOption.name }}\"\n            (error)=\"setDefaultPic($event)\"\n            class=\"img\" />\n        <div class=\"pay-via-wrapper\">\n            <div class=\"pay-via\">Pay via</div>\n            <div class=\"label\">{{ selectedOption.label }}</div>\n        </div>\n    }\n    <div [style.flex-grow]=\"isMobile ? 2 : null\">\n        @if (isZeroOrderPayment) {\n            <button\n                color=\"primary\"\n                (click)=\"handleMakePayment()\"\n                class=\"payment-button radius-3\"\n                [disabled]=\"handleMakePaymentInProgress\"\n                data-test=\"confirm-order\">\n                @if (!handleMakePaymentInProgress) {\n                    <span>Confirm Order</span>\n                }\n                @if (handleMakePaymentInProgress) {\n                    <mat-spinner class=\"white-spinner\"></mat-spinner>\n                }\n            </button>\n        } @else {\n            @if (paymentType.makePayment) {\n                <button\n                    color=\"primary\"\n                    (click)=\"handleMakePayment()\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.verifyAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    (click)=\"validateVpa()\"\n                    [disabled]=\"handleMakePaymentInProgress\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Verify &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.addAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\"\n                    (click)=\"handleMakePayment()\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Add &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n            @if (paymentType.linkAndPay) {\n                <button\n                    color=\"primary\"\n                    class=\"payment-button radius-3\"\n                    [disabled]=\"handleMakePaymentInProgress\"\n                    (click)=\"handleMakePayment()\">\n                    @if (!handleMakePaymentInProgress) {\n                        <span>Link &amp; Pay \u20B9{{ finalCartValue }}</span>\n                    }\n                    @if (handleMakePaymentInProgress) {\n                        <mat-spinner class=\"white-spinner\"></mat-spinner>\n                    }\n                </button>\n            }\n        }\n    </div>\n</div>\n", styles: [".text-right{text-align:right}.payment-button{min-width:141px;background-color:var(--primary-color);color:#fff;padding:12px}.button-container-mb{display:flex;justify-content:space-between;align-items:center}.button-container-web{position:absolute;bottom:0;background:#fff;box-shadow:0 -2px 4px #0000001a;padding:15px 30px 15px 60px}.img{width:30px;height:30px;margin-right:10px;object-fit:contain}.pay-via-wrapper{flex:1;text-align:left;width:30%;margin-right:12px}.pay-via{font-size:12px;font-weight:700;color:#5c5c5c;margin-bottom:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}@media screen and (max-width: 991px){.button-wrapper{position:fixed;bottom:0;left:0;background:#fff;padding:20px;width:100%;z-index:5;box-shadow:0 -8px 12px #0000000d}.payment-button{width:100%;max-height:40px;background-color:var(--cart-button-color)}.label{font-size:14px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}}\n"] }]
        }], ctorParameters: () => [{ type: UpiPaymentService }, { type: AmazonPayService }, { type: UtilityService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['app-device-service']
                }] }], propDecorators: { selectedOption: [{
                type: Input
            }], gateway: [{
                type: Input
            }], isZeroOrderPayment: [{
                type: Input
            }], finalCartValue: [{
                type: Input
            }], onSuccess: [{
                type: Output
            }] } });

class MobileNetbankViewComponent {
    dialogRef;
    data;
    allBanks = [];
    displayedBanks = [];
    onSuccess = new EventEmitter();
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
        this.allBanks = data.banks;
        this.displayedBanks = this.allBanks;
    }
    close() {
        this.dialogRef.close(false);
    }
    keyUpData(searchText = "", event) {
        this.displayedBanks = this.allBanks.filter((bank) => {
            return (bank.label.toLowerCase().includes(searchText.toLowerCase()) ||
                bank.name.toLowerCase().includes(searchText.toLowerCase()));
        });
        event.stopPropagation();
    }
    selectBank(bank, event) {
        event.stopPropagation();
        this.dialogRef.close(bank);
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = "/assets/images/default_payment_icon.png";
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MobileNetbankViewComponent, deps: [{ token: i1$1.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: MobileNetbankViewComponent, isStandalone: true, selector: "lib-mobile-netbank-view", outputs: { onSuccess: "onSuccess" }, ngImport: i0, template: "<div class=\"header-container\">\n    <img libDeferImgLoad [imageSrc]=\"'/assets/images/close-rounded.svg'\" (click)=\"close()\" />\n    <span class=\"header\">Select Bank</span>\n</div>\n\n<div class=\"search-container\">\n    <input\n        type=\"text\"\n        class=\"search-box\"\n        id=\"searchbox\"\n        placeholder=\"Search bank\"\n        (keyup)=\"keyUpData($event.target.value, $event)\" />\n</div>\n<div class=\"bank-wrapper\">\n    @for (bank of displayedBanks; track bank) {\n        <div (click)=\"selectBank(bank, $event)\">\n            <div class=\"bank-container\">\n                <img libDeferImgLoad [imageSrc]=\"bank.image\" alt=\"{{ bank.name }}\" (error)=\"setDefaultPic($event)\" class=\"payment-option-img\" />\n                <div class=\"bank-label\">{{ bank.name }}</div>\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/right_arrow.svg'\" alt=\"proceed\" style=\"padding: 0px 10px\" />\n            </div>\n            <div class=\"divide\"></div>\n        </div>\n    }\n</div>\n", styles: [".header-container{display:flex;width:100%;padding:30px 20px 10px}.header-container .header{font-size:24px;font-weight:600;margin:0 20px}.search-container{width:100%;margin:15px 0;padding:0 20px}.search-container .search-box{background:url(/assets/images/search.svg) 10px no-repeat;width:100%;padding:14px 40px;border-radius:6px;border:solid 1px rgba(0,0,0,.1);box-shadow:0 2px 14px #00000008,0 5px 19px #0000000a}.bank-wrapper{padding-left:20px}.bank-container{display:flex;justify-content:space-between;padding:15px 0;align-items:center;font-size:14px}.divide{display:block;height:1px;width:calc(100% - 20px);background-color:#cbcbcb}.payment-option-img{border:1px solid rgba(0,0,0,.1);border-radius:50%;padding:10px;height:45px;width:45px}.bank-label{text-align:left;padding:0 20px;width:100%;line-height:45px}@media screen and (max-width: 991px){.button-wrapper{position:sticky!important}.radio-btn{line-height:45px}}\n"], dependencies: [{ kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: MobileNetbankViewComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-mobile-netbank-view", standalone: true, imports: [DeferImgLoadDirective], template: "<div class=\"header-container\">\n    <img libDeferImgLoad [imageSrc]=\"'/assets/images/close-rounded.svg'\" (click)=\"close()\" />\n    <span class=\"header\">Select Bank</span>\n</div>\n\n<div class=\"search-container\">\n    <input\n        type=\"text\"\n        class=\"search-box\"\n        id=\"searchbox\"\n        placeholder=\"Search bank\"\n        (keyup)=\"keyUpData($event.target.value, $event)\" />\n</div>\n<div class=\"bank-wrapper\">\n    @for (bank of displayedBanks; track bank) {\n        <div (click)=\"selectBank(bank, $event)\">\n            <div class=\"bank-container\">\n                <img libDeferImgLoad [imageSrc]=\"bank.image\" alt=\"{{ bank.name }}\" (error)=\"setDefaultPic($event)\" class=\"payment-option-img\" />\n                <div class=\"bank-label\">{{ bank.name }}</div>\n                <img libDeferImgLoad [imageSrc]=\"'/assets/images/right_arrow.svg'\" alt=\"proceed\" style=\"padding: 0px 10px\" />\n            </div>\n            <div class=\"divide\"></div>\n        </div>\n    }\n</div>\n", styles: [".header-container{display:flex;width:100%;padding:30px 20px 10px}.header-container .header{font-size:24px;font-weight:600;margin:0 20px}.search-container{width:100%;margin:15px 0;padding:0 20px}.search-container .search-box{background:url(/assets/images/search.svg) 10px no-repeat;width:100%;padding:14px 40px;border-radius:6px;border:solid 1px rgba(0,0,0,.1);box-shadow:0 2px 14px #00000008,0 5px 19px #0000000a}.bank-wrapper{padding-left:20px}.bank-container{display:flex;justify-content:space-between;padding:15px 0;align-items:center;font-size:14px}.divide{display:block;height:1px;width:calc(100% - 20px);background-color:#cbcbcb}.payment-option-img{border:1px solid rgba(0,0,0,.1);border-radius:50%;padding:10px;height:45px;width:45px}.bank-label{text-align:left;padding:0 20px;width:100%;line-height:45px}@media screen and (max-width: 991px){.button-wrapper{position:sticky!important}.radio-btn{line-height:45px}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }], propDecorators: { onSuccess: [{
                type: Output
            }] } });

class NetbankingComponent {
    dialog;
    gtmService;
    deviceService;
    selected;
    selectedPaymentOption = new EventEmitter();
    onSuccess = new EventEmitter();
    filteredBanks;
    isMobile;
    selectedOption;
    constructor(dialog, gtmService, deviceService) {
        this.dialog = dialog;
        this.gtmService = gtmService;
        this.deviceService = deviceService;
        this.isMobile = this.deviceService.isMobile();
    }
    ngOnInit() {
        this.filteredBanks = this.selected.method.payment_options.slice(4);
    }
    selectPaymentOption(option) {
        this.selectedOption = option;
        this.gtmService.logPaymentEvent("netbanking_dropdown", option.code);
        this.selectedPaymentOption.emit(option);
    }
    isPaymentOptionSelected(paymentOption) {
        return (paymentOption &&
            this.selected.option &&
            paymentOption.code === this.selected.option.code);
    }
    openBankPopup() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = "netbanking-full-screen";
        dialogConfig.data = { banks: this.selected.method.payment_options };
        this.dialog
            .open(MobileNetbankViewComponent, dialogConfig)
            .afterClosed()
            .subscribe((data) => {
            if (data) {
                this.selectedPaymentOption.emit(data);
                this.onSuccess.emit();
            }
        });
    }
    setDefaultPic(event) {
        const element = event.target;
        element.src = "/assets/images/default_payment_icon.png";
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NetbankingComponent, deps: [{ token: i1$1.MatDialog }, { token: "app-gtm-service" }, { token: "app-device-service" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: NetbankingComponent, isStandalone: true, selector: "lib-netbanking", inputs: { selected: "selected" }, outputs: { selectedPaymentOption: "selectedPaymentOption", onSuccess: "onSuccess" }, ngImport: i0, template: "<span class=\"popular-brands\"> MOST POPULAR BANKS </span>\n\n<ul class=\"payment-option\">\n    @for (payment_option of selected.method?.payment_options | slice: 0 : 5; track payment_option) {\n        <li (click)=\"selectPaymentOption(payment_option)\">\n            <span class=\"payment-option-item\">\n                @if (isPaymentOptionSelected(payment_option)) {\n                    <span class=\"option-selected\"></span>\n                }\n                <img\n                    [imageSrc]=\"payment_option.image\"\n                    libDeferImgLoad\n                    [style.border]=\"\n                        !isMobile && isPaymentOptionSelected(payment_option) ? '2px solid var(--primary-color) !important' : ''\n                    \"\n                    alt=\"{{ payment_option.name }}\"\n                    (error)=\"setDefaultPic($event)\"\n                    class=\"payment-option-img\" />\n                <div class=\"clearfix\"></div>\n                <label class=\"netbank-label\">\n                    {{ payment_option.name }}\n                </label>\n            </span>\n        </li>\n    }\n</ul>\n\n<!--drop down for desktop-->\n@if (selected.method?.payment_options?.length > 5 && !isMobile) {\n    <div class=\"netbank-form-wrapper\">\n        <form class=\"form-fields-theme\" style=\"float: right; width: 150px\">\n            <mat-form-field appearance=\"outline\">\n                <mat-label>Select other bank</mat-label>\n                <mat-select\n                    placeholder=\"Select other bank\"\n                    matNativeControl\n                    [(value)]=\"selectedOption\"\n                    (selectionChange)=\"selectPaymentOption($event.value)\"\n                    disableOptionCentering\n                    panelClass=\"mat-dropdown\">\n                    @for (payment_option of filteredBanks; track payment_option) {\n                        <mat-option [value]=\"payment_option\">\n                            {{ payment_option.name }}\n                        </mat-option>\n                    }\n                </mat-select>\n            </mat-form-field>\n        </form>\n    </div>\n}\n\n<!--payment popup for mobile-->\n@if (isMobile) {\n    <span (click)=\"openBankPopup()\" class=\"netbank-label view-all-banks\">\n        View All Banks\n        <mat-icon>keyboard_arrow_right</mat-icon>\n    </span>\n}\n", styles: [".payment-option{padding:0}.payment-option li{display:inline;overflow:hidden}.payment-option-item{display:inline-block;margin-bottom:16px;text-align:center;vertical-align:top;width:20%;cursor:pointer;padding:0}.netbank-label{color:#707786;font-size:10px;font-weight:700;line-height:13px;text-transform:capitalize}.option-selected{position:absolute;width:20px;z-index:2;height:20px;background-image:url(/assets/images/tick-green.png);background-size:contain;transform:translate(50px,-5px)}.payment-option-description{font-size:11px;color:#666;line-height:20px!important}.netbank-form-wrapper{margin:38px 25px;cursor:pointer}.netbank-drop-icon{font-size:24px;position:absolute;top:40%;right:0;transform:translateY(-50%);color:#777}.payment-option-img{padding:9px;height:60px;width:60px;border-radius:6px;border:1px solid #c4c4c4}.popular-brands{color:#4c566b;font-size:14px;font-weight:800;line-height:18.2px;letter-spacing:.56px;margin:20px;display:block}@media screen and (max-width: 991px){.popular-brands{display:none}.payment-option-item{cursor:none}.payment-option-img{padding:6px;max-width:40px;height:40px;border-radius:4px;object-fit:contain}.payment-option{padding:20px 0}.view-all-banks{margin-bottom:16px;float:right;text-decoration:underline;display:flex;align-items:center;font-size:14px}.option-selected{transform:translate(30px,-5px)}}\n"], dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i3.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "component", type: MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: MatLabel, selector: "mat-label" }, { kind: "component", type: MatSelect, selector: "mat-select", inputs: ["aria-describedby", "panelClass", "disabled", "disableRipple", "tabIndex", "hideSingleSelectionIndicator", "placeholder", "required", "multiple", "disableOptionCentering", "compareWith", "value", "aria-label", "aria-labelledby", "errorStateMatcher", "typeaheadDebounceInterval", "sortComparator", "id", "panelWidth"], outputs: ["openedChange", "opened", "closed", "selectionChange", "valueChange"], exportAs: ["matSelect"] }, { kind: "component", type: MatOption, selector: "mat-option", inputs: ["value", "id", "disabled"], outputs: ["onSelectionChange"], exportAs: ["matOption"] }, { kind: "component", type: MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "pipe", type: SlicePipe, name: "slice" }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: NetbankingComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-netbanking", standalone: true, imports: [FormsModule, MatFormField, MatLabel, MatSelect, MatOption, MatIcon, SlicePipe, DeferImgLoadDirective], template: "<span class=\"popular-brands\"> MOST POPULAR BANKS </span>\n\n<ul class=\"payment-option\">\n    @for (payment_option of selected.method?.payment_options | slice: 0 : 5; track payment_option) {\n        <li (click)=\"selectPaymentOption(payment_option)\">\n            <span class=\"payment-option-item\">\n                @if (isPaymentOptionSelected(payment_option)) {\n                    <span class=\"option-selected\"></span>\n                }\n                <img\n                    [imageSrc]=\"payment_option.image\"\n                    libDeferImgLoad\n                    [style.border]=\"\n                        !isMobile && isPaymentOptionSelected(payment_option) ? '2px solid var(--primary-color) !important' : ''\n                    \"\n                    alt=\"{{ payment_option.name }}\"\n                    (error)=\"setDefaultPic($event)\"\n                    class=\"payment-option-img\" />\n                <div class=\"clearfix\"></div>\n                <label class=\"netbank-label\">\n                    {{ payment_option.name }}\n                </label>\n            </span>\n        </li>\n    }\n</ul>\n\n<!--drop down for desktop-->\n@if (selected.method?.payment_options?.length > 5 && !isMobile) {\n    <div class=\"netbank-form-wrapper\">\n        <form class=\"form-fields-theme\" style=\"float: right; width: 150px\">\n            <mat-form-field appearance=\"outline\">\n                <mat-label>Select other bank</mat-label>\n                <mat-select\n                    placeholder=\"Select other bank\"\n                    matNativeControl\n                    [(value)]=\"selectedOption\"\n                    (selectionChange)=\"selectPaymentOption($event.value)\"\n                    disableOptionCentering\n                    panelClass=\"mat-dropdown\">\n                    @for (payment_option of filteredBanks; track payment_option) {\n                        <mat-option [value]=\"payment_option\">\n                            {{ payment_option.name }}\n                        </mat-option>\n                    }\n                </mat-select>\n            </mat-form-field>\n        </form>\n    </div>\n}\n\n<!--payment popup for mobile-->\n@if (isMobile) {\n    <span (click)=\"openBankPopup()\" class=\"netbank-label view-all-banks\">\n        View All Banks\n        <mat-icon>keyboard_arrow_right</mat-icon>\n    </span>\n}\n", styles: [".payment-option{padding:0}.payment-option li{display:inline;overflow:hidden}.payment-option-item{display:inline-block;margin-bottom:16px;text-align:center;vertical-align:top;width:20%;cursor:pointer;padding:0}.netbank-label{color:#707786;font-size:10px;font-weight:700;line-height:13px;text-transform:capitalize}.option-selected{position:absolute;width:20px;z-index:2;height:20px;background-image:url(/assets/images/tick-green.png);background-size:contain;transform:translate(50px,-5px)}.payment-option-description{font-size:11px;color:#666;line-height:20px!important}.netbank-form-wrapper{margin:38px 25px;cursor:pointer}.netbank-drop-icon{font-size:24px;position:absolute;top:40%;right:0;transform:translateY(-50%);color:#777}.payment-option-img{padding:9px;height:60px;width:60px;border-radius:6px;border:1px solid #c4c4c4}.popular-brands{color:#4c566b;font-size:14px;font-weight:800;line-height:18.2px;letter-spacing:.56px;margin:20px;display:block}@media screen and (max-width: 991px){.popular-brands{display:none}.payment-option-item{cursor:none}.payment-option-img{padding:6px;max-width:40px;height:40px;border-radius:4px;object-fit:contain}.payment-option{padding:20px 0}.view-all-banks{margin-bottom:16px;float:right;text-decoration:underline;display:flex;align-items:center;font-size:14px}.option-selected{transform:translate(30px,-5px)}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.MatDialog }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }], propDecorators: { selected: [{
                type: Input
            }], selectedPaymentOption: [{
                type: Output
            }], onSuccess: [{
                type: Output
            }] } });

class PaymentComponent {
    router;
    dialog;
    cdr;
    bottomSheet;
    pgCardService;
    upiService;
    paymentService;
    credPayService;
    utilityService;
    amazonPayService;
    sodexoCardService;
    handlePaymentService;
    headerNotificationService;
    makePaymentButtonLoaderService;
    appConfig;
    gtmService;
    environment;
    deviceService;
    onPaymentInit;
    onPaymentSelect = new EventEmitter();
    removeCoupon = new EventEmitter();
    afterCartValidate = new EventEmitter();
    onPaymentError = new EventEmitter();
    noOfOptionToShow;
    allOptionShown = {
        [PaymentMethodCode.UPI]: false
    };
    selected = {
        method: null,
        option: null
    };
    paymentMethods;
    prioritisedPaymentMethods = [];
    originalPaymentMethods = [];
    filteredPayment = [];
    initialMethod = "";
    couponCode;
    isZeroOrderPayment = false;
    isMobile = false;
    disableMakePaymentCta;
    cardFiltered;
    isCreditUsed;
    creditDescription;
    finalCartValue;
    paymentOptionCode;
    paymentMethodCode;
    destroySubject$ = new Subject();
    panelExpanded;
    constructor(router, dialog, cdr, bottomSheet, pgCardService, upiService, paymentService, credPayService, utilityService, amazonPayService, sodexoCardService, handlePaymentService, headerNotificationService, makePaymentButtonLoaderService, appConfig, gtmService, environment, deviceService) {
        this.router = router;
        this.dialog = dialog;
        this.cdr = cdr;
        this.bottomSheet = bottomSheet;
        this.pgCardService = pgCardService;
        this.upiService = upiService;
        this.paymentService = paymentService;
        this.credPayService = credPayService;
        this.utilityService = utilityService;
        this.amazonPayService = amazonPayService;
        this.sodexoCardService = sodexoCardService;
        this.handlePaymentService = handlePaymentService;
        this.headerNotificationService = headerNotificationService;
        this.makePaymentButtonLoaderService = makePaymentButtonLoaderService;
        this.appConfig = appConfig;
        this.gtmService = gtmService;
        this.environment = environment;
        this.deviceService = deviceService;
        this.noOfOptionToShow = this.paymentService.noOfOptionToShow;
        this.paymentOptionCode = PaymentOptionCode;
        this.paymentMethodCode = PaymentMethodCode;
        this.paymentService.couponCode$
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((coupon) => {
            this.couponCode = coupon;
        });
        this.paymentService.disableMakePaymentCta$
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((value) => {
            this.disableMakePaymentCta = value;
        });
        this.handlePaymentService.resetSelection$
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((method) => {
            this.resetSelection(method);
        });
        this.paymentService.isZeroOrderPayment
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((val) => {
            const { currency_used, isZeroOrder } = val;
            this.isCreditUsed = currency_used;
            this.isZeroOrderPayment = isZeroOrder;
            this.creditDescription = currency_used ?
                'Your entire order has been covered by your credits. Tap on "Confirm Order" to place your order.' :
                'This one’s on us. Tap on “Confirm Order” to place your order.';
            this.cdr.markForCheck();
        });
        this.credPayService.description
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((description) => {
            if (Object.keys(description).length) {
                // in the case of credpay, use the description, remark and eligible received from the check linking api
                this.paymentService.rehydratePaymentMethod(description, PaymentOptionCode.CREDPAY);
            }
        });
        this.amazonPayService.amazonPayLaterEligible$
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((data) => {
            if (data && Object.keys(data).length) {
                // in the case of credpay, use the description, remark and eligible received from the check linking api
                this.paymentService.rehydratePaymentMethod(data, PaymentOptionCode.AMAZON_PAY_LATER);
            }
        });
        this.paymentService.paymentMethods
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((data) => {
            const { paymentMethods, filteredPaymentMethod } = data;
            this.paymentMethods = paymentMethods || [];
            const filterPaymentsPresent = filteredPaymentMethod && Object.keys(filteredPaymentMethod).length;
            if (filterPaymentsPresent) {
                const { payment_method, is_cards_filtered } = filteredPaymentMethod;
                this.filteredPayment = [payment_method];
                this.updateSelected(payment_method);
                this.initialMethod = this.selected.method.code;
                this.cardFiltered = is_cards_filtered;
                this.gtmService.logPaymentEvent("payment_offers", "Payment Filter View");
            }
            else if (this.paymentMethods.length) {
                this.filteredPayment = [];
                this.updateSelected();
                this.initialMethod = this.selected.method.code;
                this.cardFiltered = false;
                this.reorderPaymentMethods();
            }
        });
        this.paymentService.isPaymentMethodsUpdated
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((changed) => {
            if (changed && this.selected?.method) {
                const [firstOption] = this.selected.method.payment_options;
                this.checkIfFirstOptionIsNotActive(firstOption);
            }
        });
        this.makePaymentButtonLoaderService.inProgress.next(false);
        this.headerNotificationService.notifyMessage
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((message) => {
            this.onPaymentError.emit(message);
        });
        this.isMobile = this.deviceService.isMobile();
        this.paymentService.cartFinalAmount$
            .pipe(takeUntil(this.destroySubject$))
            .subscribe((final_price) => {
            this.finalCartValue = final_price;
        });
    }
    toggleShow(code) {
        this.allOptionShown[code] = true;
    }
    updateSelected(paymentMethod = null) {
        this.selected.method = paymentMethod || this.getDefaultPaymentMethod(this.paymentMethods);
        this.selected.option = this.getDefaultPaymentOption(this.selected.method);
        const { code: paymentOptionCode, is_saved: isSaved } = this.selected.option;
        if (isSaved) {
            const { saved_information } = this.selected.option;
            switch (paymentOptionCode) {
                case PaymentOptionCode.SAVED_ICICI_UPI:
                    const { saved_vpa } = saved_information;
                    this.upiService.customerVpa = saved_vpa;
                    this.upiService.isValid.next(true);
                    break;
                case PaymentOptionCode.PAYTM_CARD:
                    const { card_info } = saved_information;
                    const { saved_card_id, is_card_coft, cvv = "" } = card_info;
                    this.pgCardService.selectedSavedCardDetails = {
                        ...card_info,
                        card_info: `${saved_card_id}||${cvv}|`,
                        user_consent: is_card_coft ? 1 : 0
                    };
                    break;
                case PaymentOptionCode.SODEXO:
                    const { sourceId } = saved_information.card_info;
                    this.sodexoCardService.cardSourceId.next(sourceId);
                    break;
            }
        }
        this.onPaymentSelect.emit(this.selected);
    }
    reorderPaymentMethods() {
        const prioritisedPaymentMethods = [PaymentMethodCode.PREFERRED_PAYMENT];
        const isSavedcard = this.paymentMethods.find((method) => {
            return method.payment_options.some(option => {
                return option.code === PaymentOptionCode.PAYTM_CARD && option.is_saved;
            });
        });
        const isPrioritisedPaymentMethodsPresent = this.paymentMethods
            .find((method) => method.code === PaymentMethodCode.PREFERRED_PAYMENT);
        if (isPrioritisedPaymentMethodsPresent || isSavedcard) {
            prioritisedPaymentMethods.push(PaymentMethodCode.CARD, PaymentMethodCode.UPI);
        }
        else {
            prioritisedPaymentMethods.push(PaymentMethodCode.UPI, PaymentMethodCode.CARD);
        }
        this.prioritisedPaymentMethods = this.paymentMethods
            .filter((method) => prioritisedPaymentMethods.includes(method.code))
            .sort((methodA, methodB) => {
            return prioritisedPaymentMethods.indexOf(methodA.code) - prioritisedPaymentMethods.indexOf(methodB.code);
        });
        this.originalPaymentMethods = this.paymentMethods.filter((method) => !prioritisedPaymentMethods.includes(method.code));
        this.updateSelected(this.prioritisedPaymentMethods[0] || this.originalPaymentMethods[0]);
    }
    getDefaultPaymentMethod(payment_methods) {
        return payment_methods?.[0] || null;
    }
    getDefaultPaymentOption(payment_method) {
        if (!payment_method || !payment_method.payment_options?.length) {
            return null;
        }
        const [firstOption] = payment_method.payment_options;
        if (firstOption.code === PaymentOptionCode.SODEXO && firstOption.is_saved) {
            return firstOption;
        }
        else {
            this.checkIfFirstOptionIsNotActive(firstOption);
            return firstOption;
        }
    }
    checkIfFirstOptionIsNotActive(option) {
        if (this.disableCta(option)) {
            this.paymentService.disableMakePaymentCta$.next(true);
        }
        else {
            this.paymentService.disableMakePaymentCta$.next(false);
        }
    }
    setPanelState(value, methodCode) {
        this.panelExpanded = {
            value,
            methodCode
        };
    }
    disableCta(option) {
        const { active, code, is_saved } = option;
        const isNewCardOrSodexoCard = (code === PaymentOptionCode.PAYTM_CARD ||
            code === PaymentOptionCode.SODEXO) && !is_saved;
        return (!active || isNewCardOrSodexoCard) && !this.isZeroOrderPayment;
    }
    selectPaymentMethod(paymentMethod) {
        this.updateSelected(paymentMethod);
    }
    handleFoodCardPayment(paymentOption, paymentMethod) {
        this.selectPaymentOption(paymentOption, paymentMethod);
        this.handleMakePayment();
    }
    selectPaymentOption(paymentOption, paymentMethod) {
        this.selected = { option: paymentOption, method: paymentMethod };
        this.paymentService.disableMakePaymentCta$.next(false);
        if (this.isMobile) {
            this.onPaymentSelect.emit(this.selected);
        }
        else {
            event && event.stopPropagation();
        }
    }
    trackByIdentifier(_index, option) {
        return option.identifier;
    }
    verifyPayment() {
        const { option } = this.selected;
        if (option.code === PaymentOptionCode.PAYTM_CARD && option.is_saved) {
            // validate the card payments first, only for saved card
            if (this.pgCardService.savedCardValidator()) {
                this.handleMakePayment();
            }
        }
        else {
            this.handleMakePayment();
        }
    }
    async handleMakePayment() {
        let { method, option } = this.selected;
        this.gtmService.logPaymentEvent("make_payment", option.code);
        if (this.onPaymentInit && !this.onPaymentInit()) {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            return false;
        }
        // Validate payment method and option
        if (!this.paymentService.validParameters(method, option)) {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            return false;
        }
        this.makePaymentButtonLoaderService.inProgress.emit(true);
        if (this.paymentService.paymentModel.type === PaymentType.postOrder) {
            this.paymentService.pay(method, option);
            return;
        }
        // Card offer validation (if applicable)
        // let isEligibleCardOffer = true;
        let paymentOffersExtraParams = [];
        // if (method.code === PaymentMethodCode.CARD && this.filteredPayment.length) {
        //     const payload = this.getCardPayload(option);
        //     try {
        //         const response: any = await this.handlePaymentService.validateOffer(option, payload);
        //         paymentOffersExtraParams = response?.data?.payment_offers_applied || [];
        //         if (response && !response.eligible) {
        //             isEligibleCardOffer = false;
        //             this.otherPaymentsModes(option.is_saved ? "saved-card" : "new-card");
        //         }
        //     } catch (error) {
        //         isEligibleCardOffer = false;
        //         this.utilityService.handleResponse(error);
        //     }
        // }
        // if (!isEligibleCardOffer) {
        //     this.makePaymentButtonLoaderService.inProgress.emit(false);
        //     return false;
        // }
        if (this.isZeroOrderPayment) {
            ({ method, option } = CASH_PAYMENT_MODEL);
        }
        lastValueFrom(this.paymentService.orderValidate(method, option))
            .then((response) => {
            if (response?.meta) {
                const { checksum } = response.cart;
                const extraPayload = {
                    checksum,
                    payment_offers_applied: paymentOffersExtraParams
                };
                this.gtmService.addPaymentInfo(option, response.cart);
                const { code, message, response_code } = response.meta;
                switch (code) {
                    case 200:
                        this.paymentService.pay(method, option, extraPayload);
                        break;
                    case 205:
                        this.paymentService.pay(method, option, extraPayload);
                        break;
                    case 405: {
                        if (response_code) {
                            this.handleOrderValidateResponseCode(response_code, message, method, option, extraPayload);
                        }
                        break;
                    }
                    default: {
                        this.makePaymentButtonLoaderService.inProgress.emit(false);
                        this.utilityService.handleResponse(response);
                    }
                }
            }
            this.afterCartValidate.emit(response);
        }).catch(() => {
            this.makePaymentButtonLoaderService.inProgress.emit(false);
            this.utilityService.handleResponse();
        });
    }
    // getCardPayload(option: PaymentOption) {
    //     const cardDetails: any = option.is_saved
    //         ? this.pgCardService.selectedSavedCardDetails
    //         : this.pgCardService.sanitizedNewCardDetais;
    //     const { payment_type, bank_code, card_number, saved_card_id } = cardDetails;
    //     return  {
    //         channel: "web",
    //         flow_type: "s2s",
    //         PAYMENT_TYPE_ID: payment_type,
    //         IS_SAVED_CARD: option.is_saved ? 1 : null,
    //         CARD_NO: option.is_saved ? null : card_number,
    //         SAVED_CARD_ID: option.is_saved ? saved_card_id : "",
    //         BANK_CODE: bank_code
    //     };
    // }
    handleOrderValidateResponseCode(code, message, method, option, extraPayload) {
        this.gtmService.logPaymentEvent("order_validate_err_code", code);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = 350;
        dialogConfig.panelClass = 'info-dialog';
        code = code.toLowerCase();
        const CTAS = {
            "err_1001": ["Track Now"],
            "err_1002": ["Yes", "No"],
            "err_1003": ["Explore Menu"]
        };
        dialogConfig.data = {
            message,
            ctas: CTAS[code]
        };
        const dialogRef = this.dialog.open(InfoDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((message) => {
            if (message) {
                if (code === "err_1001") {
                    this.utilityService.navigateToManageOrders();
                }
                else if (code === "err_1002" && message === "Yes") {
                    this.paymentService.pay(method, option, extraPayload);
                }
                else if (code === "err_1003") {
                    this.utilityService.navigateTo("/");
                }
                else {
                    this.makePaymentButtonLoaderService.inProgress.emit(false);
                }
            }
            else {
                this.makePaymentButtonLoaderService.inProgress.emit(false);
            }
        });
    }
    resetSelection(paymentMethod) {
        if ([PaymentOptionCode.SODEXO, PaymentOptionCode.PAYTM_CARD].indexOf(this.selected.option.code) !== -1) {
            if (this.isMobile) {
                this.updateSelected();
                window.scrollTo(0, 0);
            }
            else {
                this.updateSelected(paymentMethod);
            }
        }
    }
    otherPaymentsModes(type = "") {
        this.gtmService.logPaymentEvent("payment_offers", "Choose Other Methods");
        const data = {
            couponCode: this.couponCode,
            type
        };
        if (this.isMobile) {
            this.bottomSheet.open(RemoveCouponComponent, {
                panelClass: "remove-coupon-bottom-sheet",
                data,
            }).afterDismissed()
                .subscribe((data) => this.afterCloseHandler(data, type));
        }
        else {
            this.dialog.open(RemoveCouponComponent, {
                panelClass: "remove-coupon-desktop-dialog",
                data
            }).afterClosed()
                .subscribe((data) => this.afterCloseHandler(data, type));
        }
    }
    afterCloseHandler(data, type) {
        if (data === "change-code") {
            this.gtmService.logPaymentEvent("payment_offers", "Apply Another Code: " + (type ? type : "Card_Offer"));
            this.removeCoupon.emit();
            if (this.isMobile) {
                this.router.navigateByUrl("/cart");
            }
        }
        else if (data === "proceed-without-code") {
            this.gtmService.logPaymentEvent("payment_offers", "Proceed Without Coupon:" + (type ? type : "Card_Offer"));
            this.removeCoupon.emit();
        }
    }
    ngOnDestroy() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
        this.credPayService.checkLinkingCalled = false;
        this.credPayService.description.next({});
        this.amazonPayService.amazonPayLaterEligible$.next({});
        this.onPaymentSelect.emit(null);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentComponent, deps: [{ token: i1$4.Router }, { token: i1$1.MatDialog }, { token: i0.ChangeDetectorRef }, { token: i1$2.MatBottomSheet }, { token: PgCardService }, { token: UpiPaymentService }, { token: PaymentService }, { token: CredpayService }, { token: UtilityService }, { token: AmazonPayService }, { token: SodexoCardService }, { token: HandlePaymentService }, { token: HeaderNotificationService }, { token: MakePaymentButtonLoaderService }, { token: "app-config" }, { token: "app-gtm-service" }, { token: "app-environment" }, { token: "app-device-service" }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: PaymentComponent, isStandalone: true, selector: "lib-payment", inputs: { onPaymentInit: "onPaymentInit" }, outputs: { onPaymentSelect: "onPaymentSelect", removeCoupon: "removeCoupon", afterCartValidate: "afterCartValidate", onPaymentError: "onPaymentError" }, ngImport: i0, template: "@if (!paymentMethods?.length) {\n    <div class=\"payment-container\">\n        <!-- this should never happen, if you are seeing this, it is the developer's mistake -->\n        Sorry, payment are not enabled as of now, please try again later\n    </div>\n}\n@if (isMobile) {\n    <ng-container *ngTemplateOutlet=\"mobileView\"></ng-container>\n} @else {\n    @if (paymentMethods?.length) {\n        <div class=\"payment-container custom-theme\">\n            @if (isZeroOrderPayment) {\n                <ul style=\"width: 100%\">\n                    <ng-container *ngTemplateOutlet=\"zeroAmountPayment\"></ng-container>\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: true, zeroAmountPayment: true }\">\n                        </ng-container>\n                    }\n                </ul>\n            } @else {\n                <ul style=\"width: 100%\">\n                    @for (\n                        paymentMethod of filteredPayment.length\n                            ? filteredPayment\n                            : prioritisedPaymentMethods.concat(originalPaymentMethods);\n                        track paymentMethod\n                    ) {\n                        <li>\n                            <div\n                                class=\"payment-methods\"\n                                [ngClass]=\"{ 'white-background': selected.method && selected.method.code === paymentMethod.code }\"\n                                (click)=\"selectPaymentMethod(paymentMethod)\">\n                                @if (filteredPayment.length) {\n                                    <span>Applicable </span>\n                                }\n                                {{ paymentMethod.label }}\n                                @if (paymentMethod.offer_count) {\n                                    <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/offers_filled.svg\" style=\"height: 16px;width: 16px\"/>\n                                }\n                            </div>\n                        </li>\n                    }\n                    <div class=\"payment-option-container\">\n                        <ng-container *ngTemplateOutlet=\"couponCodeTemplate\"></ng-container>\n                        @if (selected.method.code === paymentMethodCode.NETBANK) {\n                            <lib-netbanking\n                                [selected]=\"{ method: selected.method, option: selected.option }\"\n                                (onSuccess)=\"handleMakePayment()\"\n                                (selectedPaymentOption)=\"selectPaymentOption($event, selected.method)\">\n                            </lib-netbanking>\n                        } @else {\n                            <ng-container\n                                *ngTemplateOutlet=\"\n                                    paymentOptionsTemplate;\n                                    context: { paymentMethod: selected.method }\n                                \"></ng-container>\n                        }\n                    </div>\n                    <!-- only if there is coupon specific payment mode -->\n                    @if (filteredPayment.length) {\n                        <div class=\"payment-methods\" (click)=\"otherPaymentsModes('change-code')\">Other Payment Options</div>\n                    }\n                    <!-- PAYMENT BUTTON-->\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: false }\">\n                        </ng-container>\n                    }\n                </ul>\n            }\n        </div>\n    }\n}\n\n<ng-template #mobileView>\n    <ng-container *ngTemplateOutlet=\"couponCodeTemplate\"></ng-container>\n    @if (paymentMethods && paymentMethods.length > 0) {\n        <div class=\"payment-container custom-theme\">\n            @if (isZeroOrderPayment) {\n                <div class=\"space-30\"></div>\n                <ng-container *ngTemplateOutlet=\"zeroAmountPayment\"></ng-container>\n                @if (selected.option) {\n                    <ng-container\n                        [ngTemplateOutlet]=\"paymentButton\"\n                        [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: true }\">\n                    </ng-container>\n                }\n            } @else {\n                <ul style=\"width: 100%\">\n                    @if (!filteredPayment.length) {\n                        <div>\n                            @for (paymentMethod of prioritisedPaymentMethods; track paymentMethod) {\n                                <li>\n                                    <div class=\"payment-methods\">{{ paymentMethod.label }}</div>\n                                    <div class=\"payment-option-container\">\n                                        <ng-container\n                                            [ngTemplateOutlet]=\"paymentOptionsTemplate\"\n                                            [ngTemplateOutletContext]=\"{ paymentMethod }\"></ng-container>\n                                    </div>\n                                </li>\n                            }\n                        </div>\n                    }\n                    @if (!filteredPayment.length && originalPaymentMethods.length) {\n                        <div class=\"payment-methods\">Other Payment Methods</div>\n                    }\n                    <div class=\"mat-acc\">\n                        <mat-accordion>\n                            @for (\n                                paymentMethod of filteredPayment.length ? filteredPayment : originalPaymentMethods;\n                                track paymentMethod\n                            ) {\n                                <mat-expansion-panel\n                                    [expanded]=\"initialMethod === paymentMethod.code\"\n                                    (opened)=\"setPanelState(true, paymentMethod.code)\">\n                                    <mat-expansion-panel-header>\n                                        <mat-panel-title>\n                                            <div style=\"display: flex; justify-content: center; align-items: center; gap: 12px\">\n                                                <img\n                                                    libDeferImgLoad\n                                                    [imageSrc]=\"paymentMethod.image\"\n                                                    class=\"payment-option-img\"\n                                                    alt=\"{{ paymentMethod.name }}\"/>\n                                                <div style=\"display: flex; flex-direction: column; gap: 4px\">\n                                                    <span class=\"payment-method-label\">\n                                                        {{ paymentMethod.label }}\n                                                        @if (paymentMethod.offer_count; as offer_count) {\n                                                            <div class=\"payment-offers\">\n                                                                {{ offer_count }} {{ offer_count > 1 ? 'offers' : 'offer' }}\n                                                            </div>\n                                                        }\n                                                    </span>\n                                                    <span class=\"payment-method-description\">\n                                                        {{ paymentMethod.description }}\n                                                    </span>\n                                                </div>\n                                            </div>\n                                        </mat-panel-title>\n                                    </mat-expansion-panel-header>\n                                    @if (paymentMethod.code === paymentMethodCode.NETBANK) {\n                                        <div class=\"payment-option-container no-shadow\">\n                                            <lib-netbanking\n                                                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                                                (onSuccess)=\"handleMakePayment()\"\n                                                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n                                            </lib-netbanking>\n                                        </div>\n                                    } @else {\n                                        <div class=\"payment-option-container no-shadow\">\n                                            <ng-container\n                                                [ngTemplateOutlet]=\"paymentOptionsTemplate\"\n                                                [ngTemplateOutletContext]=\"{ paymentMethod }\"></ng-container>\n                                        </div>\n                                    }\n                                </mat-expansion-panel>\n                            }\n                        </mat-accordion>\n                    </div>\n                    @if (filteredPayment.length) {\n                        <div (click)=\"otherPaymentsModes('change-code')\">\n                            <div class=\"payment-methods\">OTHER PAYMENT MODES</div>\n                            <div class=\"payment-option-container\">\n                                <div class=\"option\">\n                                    <span class=\"payment-label\" style=\"display: flex; justify-content: space-between\">\n                                        Pay using other options\n                                        <mat-icon>keyboard_arrow_right</mat-icon>\n                                    </span>\n                                    <div class=\"other-mode-text\">Coupon code {{ couponCode }} will not be applicable</div>\n                                </div>\n                            </div>\n                        </div>\n                    }\n                    <div class=\"space-50\"></div>\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: false }\">\n                        </ng-container>\n                    }\n                </ul>\n            }\n        </div>\n    }\n</ng-template>\n\n<ng-template #couponCodeTemplate>\n    @if (couponCode && filteredPayment.length) {\n        <div class=\"applied-coupon center\">\n            {{ couponCode }} Coupon Applied!\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/confetti.svg\" style=\"width: 16px; height: 16px\" />\n        </div>\n    }\n</ng-template>\n\n<ng-template #zeroAmountPayment>\n    <div class=\"zero-amount-payment\">\n        @if (!isCreditUsed) {\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/zero-order.svg\" class=\"zero-order\" />\n        } @else {\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/credit-coin-large.svg\" class=\"credit-icon\" />\n        }\n\n        <div style=\"display: flex; flex-direction: column; align-items: center; max-width: 335px\">\n            <span class=\"heading\">Woohoo!</span>\n            <span class=\"description\"> {{ creditDescription }} </span>\n        </div>\n    </div>\n</ng-template>\n\n<ng-template #paymentButton let-show=\"show\" let-zeroAmountPayment=\"zeroAmountPayment\">\n    <div class=\"payment-btn-container\">\n        @if (show) {\n            <lib-payment-button\n                [selectedOption]=\"selected.option\"\n                [gateway]=\"selected.option.gateway\"\n                [finalCartValue]=\"finalCartValue\"\n                [isZeroOrderPayment]=\"isZeroOrderPayment\"\n                (onSuccess)=\"zeroAmountPayment ? handleMakePayment() : verifyPayment()\">\n            </lib-payment-button>\n        }\n    </div>\n</ng-template>\n\n<ng-template #paymentOptionsTemplate let-paymentMethod=\"paymentMethod\">\n    @let methodCode = paymentMethod.code;\n    @let methodName = paymentMethod.name;\n    @let count = noOfOptionToShow[methodCode];\n    @let isAllOptionsShown = allOptionShown[methodCode];\n    @let paymentOptions = paymentMethod.payment_options;\n\n    @for (option of paymentOptions.slice(0, count); track trackByIdentifier($index, option)) {\n        <ng-container\n            [ngTemplateOutlet]=\"individualOptionTemplate\"\n            [ngTemplateOutletContext]=\"{\n                option,\n                paymentMethod\n            }\"></ng-container>\n    }\n\n    @if (paymentOptions.length > count && !isAllOptionsShown) {\n        <div class=\"show-all-options center\" style=\"justify-content: flex-start;\" (click)=\"toggleShow(methodCode)\">\n            View all <span style=\"text-transform: capitalize\"> &nbsp;{{ methodName }}&nbsp; </span> options\n            @if (paymentMethod.collapsed_offer_count; as offer_count) {\n                <div class=\"payment-offers\" style=\"margin-left: 4px;\">\n                    {{ offer_count }} {{ offer_count > 1 ? 'offers' : 'offer' }}\n                </div>\n            }\n\n            <mat-icon class=\"icon\">keyboard_arrow_down</mat-icon>\n        </div>\n    }\n\n    @if (isAllOptionsShown) {\n        @for (option of paymentOptions.slice(count); track trackByIdentifier($index, option)) {\n            <ng-container\n                [ngTemplateOutlet]=\"individualOptionTemplate\"\n                [ngTemplateOutletContext]=\"{\n                    option,\n                    paymentMethod\n                }\"></ng-container>\n        }\n    }\n</ng-template>\n\n<ng-template #individualOptionTemplate let-option=\"option\" let-paymentMethod=\"paymentMethod\">\n    @switch (option.code) {\n        @case (paymentOptionCode.PAYTM_CARD) {\n            <lib-card-view\n                [option]=\"option\"\n                [cardFiltered]=\"cardFiltered\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (onPaymentClick)=\"handleMakePayment()\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-card-view>\n        }\n        @case (paymentOptionCode.SAVED_ICICI_UPI) {\n            <lib-saved-upi\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-saved-upi>\n        }\n        @case (paymentOptionCode.GPAY) {\n            <lib-gpay\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-gpay>\n        }\n        @case (paymentOptionCode.SODEXO) {\n            <lib-sodexo-card-view\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                [finalCartValue]=\"finalCartValue\"\n                (onSuccess)=\"handleFoodCardPayment($event, paymentMethod)\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-sodexo-card-view>\n        }\n        @default {\n            <lib-payment-icon\n                [panelExpanded]=\"panelExpanded\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                [option]=\"option\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-payment-icon>\n        }\n    }\n</ng-template>\n", styles: [".payment-container{display:flex;background:#f8f8f8;position:relative;min-height:500px}.payment-option-container{position:absolute;top:0%;left:25%;width:75%;min-height:294px;height:100%;padding:0 20px 70px;background:#fff;box-shadow:-1px 0 #0000000d;overflow-y:scroll}.zero-amount-payment{display:flex;flex-direction:column;padding:20px!important;align-items:center;gap:12px;height:100%;background:#fff;overflow-y:scroll}.credit-icon{width:80px;height:80px}.heading{font-size:16px;font-weight:800;line-height:1.3}.description{color:#707786;text-align:center;font-size:14px;font-weight:600;line-height:18.2px}.payment-methods{display:inline-block;width:25%;padding:15px 20px;height:auto;border:none;cursor:pointer;border-bottom:1px solid #e2e2e2;font-size:14px;font-weight:800;color:#4c566b;text-transform:capitalize;letter-spacing:.56px}.white-background{border-left:2px solid var(--primary-color);background:#fff;box-shadow:0 0 9px #00000026;color:#000}.payment-method-label{color:#000;font-size:14px;font-weight:600;line-height:18.2px;display:flex;gap:12px;align-items:center;flex-wrap:wrap}.payment-method-description{overflow:hidden;color:#7c8495;text-overflow:ellipsis;font-size:10px;font-weight:400;line-height:13px;letter-spacing:.1px;width:calc(100vw - 180px);display:inline-block;white-space:nowrap}.applied-coupon{width:auto;gap:10px;border-radius:8px;background:#e8f7f7;box-shadow:0 3px 6px #474d601a;font-weight:800;font-size:12px;line-height:15px;color:var(--secondary-color);height:56px;margin:0 0 20px}.payment-offers{display:flex;padding:2px 4px;justify-content:center;align-items:center;color:#1daaaa;font-size:10px;font-weight:700;border-radius:4px;background:#e8f7f7}.show-all-options{height:40px;padding:10px 11px 10px 16px;border-radius:8px;background:#f2f3f4;color:#000;font-size:14px;font-weight:700;line-height:130%;cursor:pointer;position:relative}.icon{position:absolute;right:12px}@media screen and (max-width: 991px){.payment-container{flex-direction:column;overflow:scroll;cursor:initial;min-height:calc(100vh - 150px)}.show-all-options{margin:20px 16px}.payment-methods{width:100%;display:block;white-space:nowrap;overflow:scroll;-webkit-tap-highlight-color:transparent;border-bottom:none;text-transform:uppercase}.payment-methods::-webkit-scrollbar{display:none}.white-background{border-bottom:none;border-left:none;box-shadow:none;text-transform:uppercase;padding:10px 20px;font-weight:800;font-size:14px;color:#7c8495}.payment-option-container{padding:0;position:initial;overflow-y:scroll;min-height:unset;box-shadow:0 3px 6px #474d601a;border-radius:20px;background:#fff;margin:12px 20px;width:auto}.other-mode-text{color:#cf5c63;font-size:12px;font-weight:500;line-height:15.6px;letter-spacing:.12px}.zero-amount-payment{margin:20px;border-radius:20px;box-shadow:0 3px 6px #474d601a}.applied-coupon{margin:12px 20px 0}}.payment-container .mat-expansion-panel-header{padding:34px 20px!important}.mat-acc{box-shadow:0 3px 6px #474d601a;border-radius:20px;background:#fff;margin:12px 20px;width:auto;overflow:hidden}.no-shadow{box-shadow:none;margin:0 12px}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: NetbankingComponent, selector: "lib-netbanking", inputs: ["selected"], outputs: ["selectedPaymentOption", "onSuccess"] }, { kind: "directive", type: MatAccordion, selector: "mat-accordion", inputs: ["hideToggle", "displayMode", "togglePosition"], exportAs: ["matAccordion"] }, { kind: "component", type: MatExpansionPanel, selector: "mat-expansion-panel", inputs: ["hideToggle", "togglePosition"], outputs: ["afterExpand", "afterCollapse"], exportAs: ["matExpansionPanel"] }, { kind: "component", type: MatExpansionPanelHeader, selector: "mat-expansion-panel-header", inputs: ["expandedHeight", "collapsedHeight", "tabIndex"] }, { kind: "directive", type: MatExpansionPanelTitle, selector: "mat-panel-title" }, { kind: "component", type: MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: PaymentButtonComponent, selector: "lib-payment-button", inputs: ["selectedOption", "gateway", "isZeroOrderPayment", "finalCartValue"], outputs: ["onSuccess"] }, { kind: "component", type: CardViewComponent, selector: "lib-card-view", inputs: ["selected", "option", "cardFiltered"], outputs: ["onPaymentClick", "selectedPaymentOption"] }, { kind: "component", type: SavedUpiComponent, selector: "lib-saved-upi", inputs: ["selected", "option"], outputs: ["selectedPaymentOption"] }, { kind: "component", type: GpayComponent, selector: "lib-gpay", inputs: ["selected", "option"], outputs: ["selectedPaymentOption"] }, { kind: "component", type: SodexoCardViewComponent, selector: "lib-sodexo-card-view", inputs: ["selected", "option", "finalCartValue"], outputs: ["onSuccess", "selectedPaymentOption"] }, { kind: "component", type: PaymentIconComponent, selector: "lib-payment-icon", inputs: ["selected", "option", "panelExpanded"], outputs: ["selectedPaymentOption"] }, { kind: "directive", type: DeferImgLoadDirective, selector: "[libDeferImgLoad]", inputs: ["imageSrc", "defaultImage"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentComponent, decorators: [{
            type: Component,
            args: [{ selector: "lib-payment", standalone: true, imports: [NgTemplateOutlet, NgClass, NetbankingComponent, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatIcon, PaymentButtonComponent, CardViewComponent, SavedUpiComponent, GpayComponent, SodexoCardViewComponent, PaymentIconComponent, DeferImgLoadDirective], template: "@if (!paymentMethods?.length) {\n    <div class=\"payment-container\">\n        <!-- this should never happen, if you are seeing this, it is the developer's mistake -->\n        Sorry, payment are not enabled as of now, please try again later\n    </div>\n}\n@if (isMobile) {\n    <ng-container *ngTemplateOutlet=\"mobileView\"></ng-container>\n} @else {\n    @if (paymentMethods?.length) {\n        <div class=\"payment-container custom-theme\">\n            @if (isZeroOrderPayment) {\n                <ul style=\"width: 100%\">\n                    <ng-container *ngTemplateOutlet=\"zeroAmountPayment\"></ng-container>\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: true, zeroAmountPayment: true }\">\n                        </ng-container>\n                    }\n                </ul>\n            } @else {\n                <ul style=\"width: 100%\">\n                    @for (\n                        paymentMethod of filteredPayment.length\n                            ? filteredPayment\n                            : prioritisedPaymentMethods.concat(originalPaymentMethods);\n                        track paymentMethod\n                    ) {\n                        <li>\n                            <div\n                                class=\"payment-methods\"\n                                [ngClass]=\"{ 'white-background': selected.method && selected.method.code === paymentMethod.code }\"\n                                (click)=\"selectPaymentMethod(paymentMethod)\">\n                                @if (filteredPayment.length) {\n                                    <span>Applicable </span>\n                                }\n                                {{ paymentMethod.label }}\n                                @if (paymentMethod.offer_count) {\n                                    <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/offers_filled.svg\" style=\"height: 16px;width: 16px\"/>\n                                }\n                            </div>\n                        </li>\n                    }\n                    <div class=\"payment-option-container\">\n                        <ng-container *ngTemplateOutlet=\"couponCodeTemplate\"></ng-container>\n                        @if (selected.method.code === paymentMethodCode.NETBANK) {\n                            <lib-netbanking\n                                [selected]=\"{ method: selected.method, option: selected.option }\"\n                                (onSuccess)=\"handleMakePayment()\"\n                                (selectedPaymentOption)=\"selectPaymentOption($event, selected.method)\">\n                            </lib-netbanking>\n                        } @else {\n                            <ng-container\n                                *ngTemplateOutlet=\"\n                                    paymentOptionsTemplate;\n                                    context: { paymentMethod: selected.method }\n                                \"></ng-container>\n                        }\n                    </div>\n                    <!-- only if there is coupon specific payment mode -->\n                    @if (filteredPayment.length) {\n                        <div class=\"payment-methods\" (click)=\"otherPaymentsModes('change-code')\">Other Payment Options</div>\n                    }\n                    <!-- PAYMENT BUTTON-->\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: false }\">\n                        </ng-container>\n                    }\n                </ul>\n            }\n        </div>\n    }\n}\n\n<ng-template #mobileView>\n    <ng-container *ngTemplateOutlet=\"couponCodeTemplate\"></ng-container>\n    @if (paymentMethods && paymentMethods.length > 0) {\n        <div class=\"payment-container custom-theme\">\n            @if (isZeroOrderPayment) {\n                <div class=\"space-30\"></div>\n                <ng-container *ngTemplateOutlet=\"zeroAmountPayment\"></ng-container>\n                @if (selected.option) {\n                    <ng-container\n                        [ngTemplateOutlet]=\"paymentButton\"\n                        [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: true }\">\n                    </ng-container>\n                }\n            } @else {\n                <ul style=\"width: 100%\">\n                    @if (!filteredPayment.length) {\n                        <div>\n                            @for (paymentMethod of prioritisedPaymentMethods; track paymentMethod) {\n                                <li>\n                                    <div class=\"payment-methods\">{{ paymentMethod.label }}</div>\n                                    <div class=\"payment-option-container\">\n                                        <ng-container\n                                            [ngTemplateOutlet]=\"paymentOptionsTemplate\"\n                                            [ngTemplateOutletContext]=\"{ paymentMethod }\"></ng-container>\n                                    </div>\n                                </li>\n                            }\n                        </div>\n                    }\n                    @if (!filteredPayment.length && originalPaymentMethods.length) {\n                        <div class=\"payment-methods\">Other Payment Methods</div>\n                    }\n                    <div class=\"mat-acc\">\n                        <mat-accordion>\n                            @for (\n                                paymentMethod of filteredPayment.length ? filteredPayment : originalPaymentMethods;\n                                track paymentMethod\n                            ) {\n                                <mat-expansion-panel\n                                    [expanded]=\"initialMethod === paymentMethod.code\"\n                                    (opened)=\"setPanelState(true, paymentMethod.code)\">\n                                    <mat-expansion-panel-header>\n                                        <mat-panel-title>\n                                            <div style=\"display: flex; justify-content: center; align-items: center; gap: 12px\">\n                                                <img\n                                                    libDeferImgLoad\n                                                    [imageSrc]=\"paymentMethod.image\"\n                                                    class=\"payment-option-img\"\n                                                    alt=\"{{ paymentMethod.name }}\"/>\n                                                <div style=\"display: flex; flex-direction: column; gap: 4px\">\n                                                    <span class=\"payment-method-label\">\n                                                        {{ paymentMethod.label }}\n                                                        @if (paymentMethod.offer_count; as offer_count) {\n                                                            <div class=\"payment-offers\">\n                                                                {{ offer_count }} {{ offer_count > 1 ? 'offers' : 'offer' }}\n                                                            </div>\n                                                        }\n                                                    </span>\n                                                    <span class=\"payment-method-description\">\n                                                        {{ paymentMethod.description }}\n                                                    </span>\n                                                </div>\n                                            </div>\n                                        </mat-panel-title>\n                                    </mat-expansion-panel-header>\n                                    @if (paymentMethod.code === paymentMethodCode.NETBANK) {\n                                        <div class=\"payment-option-container no-shadow\">\n                                            <lib-netbanking\n                                                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                                                (onSuccess)=\"handleMakePayment()\"\n                                                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n                                            </lib-netbanking>\n                                        </div>\n                                    } @else {\n                                        <div class=\"payment-option-container no-shadow\">\n                                            <ng-container\n                                                [ngTemplateOutlet]=\"paymentOptionsTemplate\"\n                                                [ngTemplateOutletContext]=\"{ paymentMethod }\"></ng-container>\n                                        </div>\n                                    }\n                                </mat-expansion-panel>\n                            }\n                        </mat-accordion>\n                    </div>\n                    @if (filteredPayment.length) {\n                        <div (click)=\"otherPaymentsModes('change-code')\">\n                            <div class=\"payment-methods\">OTHER PAYMENT MODES</div>\n                            <div class=\"payment-option-container\">\n                                <div class=\"option\">\n                                    <span class=\"payment-label\" style=\"display: flex; justify-content: space-between\">\n                                        Pay using other options\n                                        <mat-icon>keyboard_arrow_right</mat-icon>\n                                    </span>\n                                    <div class=\"other-mode-text\">Coupon code {{ couponCode }} will not be applicable</div>\n                                </div>\n                            </div>\n                        </div>\n                    }\n                    <div class=\"space-50\"></div>\n                    @if (selected.option) {\n                        <ng-container\n                            [ngTemplateOutlet]=\"paymentButton\"\n                            [ngTemplateOutletContext]=\"{ show: !disableMakePaymentCta, zeroAmountPayment: false }\">\n                        </ng-container>\n                    }\n                </ul>\n            }\n        </div>\n    }\n</ng-template>\n\n<ng-template #couponCodeTemplate>\n    @if (couponCode && filteredPayment.length) {\n        <div class=\"applied-coupon center\">\n            {{ couponCode }} Coupon Applied!\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/confetti.svg\" style=\"width: 16px; height: 16px\" />\n        </div>\n    }\n</ng-template>\n\n<ng-template #zeroAmountPayment>\n    <div class=\"zero-amount-payment\">\n        @if (!isCreditUsed) {\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/zero-order.svg\" class=\"zero-order\" />\n        } @else {\n            <img libDeferImgLoad imageSrc=\"{{ environment.DEPLOY_URL }}/assets/images/credit-coin-large.svg\" class=\"credit-icon\" />\n        }\n\n        <div style=\"display: flex; flex-direction: column; align-items: center; max-width: 335px\">\n            <span class=\"heading\">Woohoo!</span>\n            <span class=\"description\"> {{ creditDescription }} </span>\n        </div>\n    </div>\n</ng-template>\n\n<ng-template #paymentButton let-show=\"show\" let-zeroAmountPayment=\"zeroAmountPayment\">\n    <div class=\"payment-btn-container\">\n        @if (show) {\n            <lib-payment-button\n                [selectedOption]=\"selected.option\"\n                [gateway]=\"selected.option.gateway\"\n                [finalCartValue]=\"finalCartValue\"\n                [isZeroOrderPayment]=\"isZeroOrderPayment\"\n                (onSuccess)=\"zeroAmountPayment ? handleMakePayment() : verifyPayment()\">\n            </lib-payment-button>\n        }\n    </div>\n</ng-template>\n\n<ng-template #paymentOptionsTemplate let-paymentMethod=\"paymentMethod\">\n    @let methodCode = paymentMethod.code;\n    @let methodName = paymentMethod.name;\n    @let count = noOfOptionToShow[methodCode];\n    @let isAllOptionsShown = allOptionShown[methodCode];\n    @let paymentOptions = paymentMethod.payment_options;\n\n    @for (option of paymentOptions.slice(0, count); track trackByIdentifier($index, option)) {\n        <ng-container\n            [ngTemplateOutlet]=\"individualOptionTemplate\"\n            [ngTemplateOutletContext]=\"{\n                option,\n                paymentMethod\n            }\"></ng-container>\n    }\n\n    @if (paymentOptions.length > count && !isAllOptionsShown) {\n        <div class=\"show-all-options center\" style=\"justify-content: flex-start;\" (click)=\"toggleShow(methodCode)\">\n            View all <span style=\"text-transform: capitalize\"> &nbsp;{{ methodName }}&nbsp; </span> options\n            @if (paymentMethod.collapsed_offer_count; as offer_count) {\n                <div class=\"payment-offers\" style=\"margin-left: 4px;\">\n                    {{ offer_count }} {{ offer_count > 1 ? 'offers' : 'offer' }}\n                </div>\n            }\n\n            <mat-icon class=\"icon\">keyboard_arrow_down</mat-icon>\n        </div>\n    }\n\n    @if (isAllOptionsShown) {\n        @for (option of paymentOptions.slice(count); track trackByIdentifier($index, option)) {\n            <ng-container\n                [ngTemplateOutlet]=\"individualOptionTemplate\"\n                [ngTemplateOutletContext]=\"{\n                    option,\n                    paymentMethod\n                }\"></ng-container>\n        }\n    }\n</ng-template>\n\n<ng-template #individualOptionTemplate let-option=\"option\" let-paymentMethod=\"paymentMethod\">\n    @switch (option.code) {\n        @case (paymentOptionCode.PAYTM_CARD) {\n            <lib-card-view\n                [option]=\"option\"\n                [cardFiltered]=\"cardFiltered\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (onPaymentClick)=\"handleMakePayment()\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-card-view>\n        }\n        @case (paymentOptionCode.SAVED_ICICI_UPI) {\n            <lib-saved-upi\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-saved-upi>\n        }\n        @case (paymentOptionCode.GPAY) {\n            <lib-gpay\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-gpay>\n        }\n        @case (paymentOptionCode.SODEXO) {\n            <lib-sodexo-card-view\n                [option]=\"option\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                [finalCartValue]=\"finalCartValue\"\n                (onSuccess)=\"handleFoodCardPayment($event, paymentMethod)\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-sodexo-card-view>\n        }\n        @default {\n            <lib-payment-icon\n                [panelExpanded]=\"panelExpanded\"\n                [selected]=\"{ method: paymentMethod, option: selected.option }\"\n                [option]=\"option\"\n                (selectedPaymentOption)=\"selectPaymentOption($event, paymentMethod)\">\n            </lib-payment-icon>\n        }\n    }\n</ng-template>\n", styles: [".payment-container{display:flex;background:#f8f8f8;position:relative;min-height:500px}.payment-option-container{position:absolute;top:0%;left:25%;width:75%;min-height:294px;height:100%;padding:0 20px 70px;background:#fff;box-shadow:-1px 0 #0000000d;overflow-y:scroll}.zero-amount-payment{display:flex;flex-direction:column;padding:20px!important;align-items:center;gap:12px;height:100%;background:#fff;overflow-y:scroll}.credit-icon{width:80px;height:80px}.heading{font-size:16px;font-weight:800;line-height:1.3}.description{color:#707786;text-align:center;font-size:14px;font-weight:600;line-height:18.2px}.payment-methods{display:inline-block;width:25%;padding:15px 20px;height:auto;border:none;cursor:pointer;border-bottom:1px solid #e2e2e2;font-size:14px;font-weight:800;color:#4c566b;text-transform:capitalize;letter-spacing:.56px}.white-background{border-left:2px solid var(--primary-color);background:#fff;box-shadow:0 0 9px #00000026;color:#000}.payment-method-label{color:#000;font-size:14px;font-weight:600;line-height:18.2px;display:flex;gap:12px;align-items:center;flex-wrap:wrap}.payment-method-description{overflow:hidden;color:#7c8495;text-overflow:ellipsis;font-size:10px;font-weight:400;line-height:13px;letter-spacing:.1px;width:calc(100vw - 180px);display:inline-block;white-space:nowrap}.applied-coupon{width:auto;gap:10px;border-radius:8px;background:#e8f7f7;box-shadow:0 3px 6px #474d601a;font-weight:800;font-size:12px;line-height:15px;color:var(--secondary-color);height:56px;margin:0 0 20px}.payment-offers{display:flex;padding:2px 4px;justify-content:center;align-items:center;color:#1daaaa;font-size:10px;font-weight:700;border-radius:4px;background:#e8f7f7}.show-all-options{height:40px;padding:10px 11px 10px 16px;border-radius:8px;background:#f2f3f4;color:#000;font-size:14px;font-weight:700;line-height:130%;cursor:pointer;position:relative}.icon{position:absolute;right:12px}@media screen and (max-width: 991px){.payment-container{flex-direction:column;overflow:scroll;cursor:initial;min-height:calc(100vh - 150px)}.show-all-options{margin:20px 16px}.payment-methods{width:100%;display:block;white-space:nowrap;overflow:scroll;-webkit-tap-highlight-color:transparent;border-bottom:none;text-transform:uppercase}.payment-methods::-webkit-scrollbar{display:none}.white-background{border-bottom:none;border-left:none;box-shadow:none;text-transform:uppercase;padding:10px 20px;font-weight:800;font-size:14px;color:#7c8495}.payment-option-container{padding:0;position:initial;overflow-y:scroll;min-height:unset;box-shadow:0 3px 6px #474d601a;border-radius:20px;background:#fff;margin:12px 20px;width:auto}.other-mode-text{color:#cf5c63;font-size:12px;font-weight:500;line-height:15.6px;letter-spacing:.12px}.zero-amount-payment{margin:20px;border-radius:20px;box-shadow:0 3px 6px #474d601a}.applied-coupon{margin:12px 20px 0}}.payment-container .mat-expansion-panel-header{padding:34px 20px!important}.mat-acc{box-shadow:0 3px 6px #474d601a;border-radius:20px;background:#fff;margin:12px 20px;width:auto;overflow:hidden}.no-shadow{box-shadow:none;margin:0 12px}\n"] }]
        }], ctorParameters: () => [{ type: i1$4.Router }, { type: i1$1.MatDialog }, { type: i0.ChangeDetectorRef }, { type: i1$2.MatBottomSheet }, { type: PgCardService }, { type: UpiPaymentService }, { type: PaymentService }, { type: CredpayService }, { type: UtilityService }, { type: AmazonPayService }, { type: SodexoCardService }, { type: HandlePaymentService }, { type: HeaderNotificationService }, { type: MakePaymentButtonLoaderService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-config"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-gtm-service"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-environment"]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ["app-device-service"]
                }] }], propDecorators: { onPaymentInit: [{
                type: Input
            }], onPaymentSelect: [{
                type: Output
            }], removeCoupon: [{
                type: Output
            }], afterCartValidate: [{
                type: Output
            }], onPaymentError: [{
                type: Output
            }] } });

class SharedModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: SharedModule, imports: [CommonModule,
            MatCheckboxModule,
            MatButtonModule,
            MatInputModule,
            MatAutocompleteModule,
            MatFormFieldModule,
            MatRadioModule,
            MatSelectModule,
            MatDividerModule,
            MatExpansionModule,
            MatIconModule,
            MatProgressSpinnerModule,
            MatProgressBarModule,
            MatDialogModule,
            MatSnackBarModule,
            FormsModule,
            ReactiveFormsModule], exports: [MatCheckboxModule,
            MatButtonModule,
            MatInputModule,
            MatAutocompleteModule,
            MatFormFieldModule,
            MatRadioModule,
            MatSelectModule,
            MatDividerModule,
            MatExpansionModule,
            MatIconModule,
            MatProgressSpinnerModule,
            MatProgressBarModule,
            MatDialogModule,
            MatSnackBarModule,
            FormsModule,
            ReactiveFormsModule] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SharedModule, providers: [
            { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true, autoFocus: true } }
        ], imports: [CommonModule,
            MatCheckboxModule,
            MatButtonModule,
            MatInputModule,
            MatAutocompleteModule,
            MatFormFieldModule,
            MatRadioModule,
            MatSelectModule,
            MatDividerModule,
            MatExpansionModule,
            MatIconModule,
            MatProgressSpinnerModule,
            MatProgressBarModule,
            MatDialogModule,
            MatSnackBarModule,
            FormsModule,
            ReactiveFormsModule, MatCheckboxModule,
            MatButtonModule,
            MatInputModule,
            MatAutocompleteModule,
            MatFormFieldModule,
            MatRadioModule,
            MatSelectModule,
            MatDividerModule,
            MatExpansionModule,
            MatIconModule,
            MatProgressSpinnerModule,
            MatProgressBarModule,
            MatDialogModule,
            MatSnackBarModule,
            FormsModule,
            ReactiveFormsModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: SharedModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatCheckboxModule,
                        MatButtonModule,
                        MatInputModule,
                        MatAutocompleteModule,
                        MatFormFieldModule,
                        MatRadioModule,
                        MatSelectModule,
                        MatDividerModule,
                        MatExpansionModule,
                        MatIconModule,
                        MatProgressSpinnerModule,
                        MatProgressBarModule,
                        MatDialogModule,
                        MatSnackBarModule,
                        FormsModule,
                        ReactiveFormsModule
                    ],
                    declarations: [],
                    exports: [
                        MatCheckboxModule,
                        MatButtonModule,
                        MatInputModule,
                        MatAutocompleteModule,
                        MatFormFieldModule,
                        MatRadioModule,
                        MatSelectModule,
                        MatDividerModule,
                        MatExpansionModule,
                        MatIconModule,
                        MatProgressSpinnerModule,
                        MatProgressBarModule,
                        MatDialogModule,
                        MatSnackBarModule,
                        FormsModule,
                        ReactiveFormsModule,
                    ],
                    providers: [
                        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true, autoFocus: true } }
                    ]
                }]
        }] });

class OtpValidateComponent {
    dialogRef;
    toastrService;
    data;
    otpTimer;
    customerPhoneNo;
    otpValidate;
    paymentStrategy = null;
    constructor(dialogRef, toastrService, data) {
        this.dialogRef = dialogRef;
        this.toastrService = toastrService;
        this.data = data;
        this.otpValidate = new FormGroup({
            otp: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(4)
            ]))
        });
        this.paymentStrategy = this.data.payment_strategy;
        this.customerPhoneNo = this.data.phone_no;
        this.paymentStrategy.response.subscribe(response => {
            this.calculateResponse(response);
        });
    }
    sendOtp() {
        try {
            this.paymentStrategy.sendOtp(this.otpTimer);
        }
        catch (e) {
            console.log('Payment strategy not implemented!');
            this.close();
        }
    }
    validateOtp(data) {
        try {
            this.paymentStrategy.validateOtp(data);
        }
        catch (e) {
            console.log('Payment strategy not implemented!');
            this.close();
        }
    }
    calculateResponse(response) {
        if (!response)
            return;
        if (response.message) {
            this.toastrService.openSnackBar(response.message, 'OKAY');
        }
        if (response.linkedWallet && response.linkedWallet.length > 0) {
            this.close(true);
        }
        else if (response.closeWindow) {
            this.close();
        }
    }
    close(isWalletLinked = false) {
        this.dialogRef.close(isWalletLinked);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OtpValidateComponent, deps: [{ token: i1$1.MatDialogRef }, { token: ToastrService }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: OtpValidateComponent, isStandalone: true, selector: "lib-otp-validate", viewQueries: [{ propertyName: "otpTimer", first: true, predicate: ["otpTimer"], descendants: true }], ngImport: i0, template: "<div mat-dialog-title>\n    OTP VERIFICATION\n    <div class=\"pull-right close\" (click)=\"close()\"> &#10005; </div>\n</div>\n<div mat-dialog-content>\n    <div class=\"text-center\">\n        OTP has been sent to <span class=\"highlight\">{{customerPhoneNo}}</span>\n    </div>\n    <div class=\"space-5\"></div>\n    <form name=\"otpValidate\" [formGroup]=\"otpValidate\">\n        <mat-form-field>\n            <input matInput formControlName=\"otp\" inputmode=\"numeric\" placeholder=\"ENTER OTP\" maxlength=\"6\">\n        </mat-form-field>\n    </form>\n</div>\n<div mat-dialog-actions>\n    <button type=\"submit\" class=\"custom-auth-btn full-width radius-3\" [disabled]=\"!otpValidate.valid\"\n        (click)=\"validateOtp(otpValidate.value)\">\n        VERIFY OTP\n    </button>\n    <div class=\"space-5\"></div>\n    <div class=\"text-center\" style=\"width: 100%; margin-bottom: 15px;\">\n        <lib-timer #otpTimer timeInSeconds=\"30\" message=\"You will receive message within\">\n            <div style=\"font-size: 12px; color: #6b6b6b;\">\n                <span style=\"font-style: italic;\">Didn't get OTP?</span>&nbsp;\n                <a href=\"JavaScript:void(0);\" (click)=\"sendOtp()\" style=\"color: #2493da;\">Resend OTP</a>\n            </div>\n        </lib-timer>\n    </div>\n</div>", styles: [""], dependencies: [{ kind: "directive", type: MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { kind: "directive", type: MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i3.MaxLengthValidator, selector: "[maxlength][formControlName],[maxlength][formControl],[maxlength][ngModel]", inputs: ["maxlength"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "directive", type: MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]", inputs: ["align"] }, { kind: "component", type: TimerComponent, selector: "lib-timer", inputs: ["timeInSeconds", "message"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: OtpValidateComponent, decorators: [{
            type: Component,
            args: [{ selector: 'lib-otp-validate', standalone: true, imports: [MatDialogTitle, MatDialogContent, FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatDialogActions, TimerComponent], template: "<div mat-dialog-title>\n    OTP VERIFICATION\n    <div class=\"pull-right close\" (click)=\"close()\"> &#10005; </div>\n</div>\n<div mat-dialog-content>\n    <div class=\"text-center\">\n        OTP has been sent to <span class=\"highlight\">{{customerPhoneNo}}</span>\n    </div>\n    <div class=\"space-5\"></div>\n    <form name=\"otpValidate\" [formGroup]=\"otpValidate\">\n        <mat-form-field>\n            <input matInput formControlName=\"otp\" inputmode=\"numeric\" placeholder=\"ENTER OTP\" maxlength=\"6\">\n        </mat-form-field>\n    </form>\n</div>\n<div mat-dialog-actions>\n    <button type=\"submit\" class=\"custom-auth-btn full-width radius-3\" [disabled]=\"!otpValidate.valid\"\n        (click)=\"validateOtp(otpValidate.value)\">\n        VERIFY OTP\n    </button>\n    <div class=\"space-5\"></div>\n    <div class=\"text-center\" style=\"width: 100%; margin-bottom: 15px;\">\n        <lib-timer #otpTimer timeInSeconds=\"30\" message=\"You will receive message within\">\n            <div style=\"font-size: 12px; color: #6b6b6b;\">\n                <span style=\"font-style: italic;\">Didn't get OTP?</span>&nbsp;\n                <a href=\"JavaScript:void(0);\" (click)=\"sendOtp()\" style=\"color: #2493da;\">Resend OTP</a>\n            </div>\n        </lib-timer>\n    </div>\n</div>" }]
        }], ctorParameters: () => [{ type: i1$1.MatDialogRef }, { type: ToastrService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }], propDecorators: { otpTimer: [{
                type: ViewChild,
                args: ['otpTimer', { static: false }]
            }] } });

class PaymentModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: PaymentModule, imports: [CommonModule,
            SharedModule,
            PaymentComponent,
            CardViewComponent,
            SodexoCardViewComponent,
            LazypayPaymentComponent,
            UpiPaymentComponent,
            TimerComponent,
            GooglePayComponent,
            UpiPaymentTimerModalComponent,
            NetbankingComponent,
            PaymentButtonComponent,
            PaymentIconComponent,
            SavedCardComponent,
            OtpValidateComponent,
            MobileNetbankViewComponent,
            SecureCardBottomSheetComponent,
            SecureCardDialogComponent,
            SodexoSavedCardComponent,
            PaymentPendingComponent,
            TransactionInterruptedBottomSheetComponent,
            RemoveCouponComponent,
            InfoDialogComponent,
            SavedUpiComponent,
            GpayComponent], exports: [PaymentComponent,
            SodexoCardViewComponent,
            SodexoSavedCardComponent,
            SavedCardComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentModule, providers: [
            CashService,
            PaytmPgNetbankService,
            PaytmPgService,
            LazypayS2SService
        ], imports: [CommonModule,
            SharedModule,
            PaymentComponent,
            CardViewComponent,
            SodexoCardViewComponent,
            LazypayPaymentComponent,
            UpiPaymentComponent,
            GooglePayComponent,
            UpiPaymentTimerModalComponent,
            NetbankingComponent,
            PaymentButtonComponent,
            PaymentIconComponent,
            SavedCardComponent,
            OtpValidateComponent,
            SodexoSavedCardComponent,
            RemoveCouponComponent,
            SavedUpiComponent,
            GpayComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaymentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SharedModule,
                        PaymentComponent,
                        CardViewComponent,
                        SodexoCardViewComponent,
                        LazypayPaymentComponent,
                        UpiPaymentComponent,
                        TimerComponent,
                        GooglePayComponent,
                        UpiPaymentTimerModalComponent,
                        NetbankingComponent,
                        PaymentButtonComponent,
                        PaymentIconComponent,
                        SavedCardComponent,
                        OtpValidateComponent,
                        MobileNetbankViewComponent,
                        SecureCardBottomSheetComponent,
                        SecureCardDialogComponent,
                        SodexoSavedCardComponent,
                        PaymentPendingComponent,
                        TransactionInterruptedBottomSheetComponent,
                        RemoveCouponComponent,
                        InfoDialogComponent,
                        SavedUpiComponent,
                        GpayComponent
                    ],
                    exports: [
                        PaymentComponent,
                        SodexoCardViewComponent,
                        SodexoSavedCardComponent,
                        SavedCardComponent
                    ],
                    providers: [
                        CashService,
                        PaytmPgNetbankService,
                        PaytmPgService,
                        LazypayS2SService
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/*
 * Public API Surface of payment
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AmazonPayService, HandlePaymentService, OtpValidateComponent, PaymentComponent, PaymentMethodCode, PaymentModule, PaymentOptionCode, PaymentService, PgCardService, SavedCardComponent, SimplService, SodexoCardService, SodexoCardViewComponent, SodexoSavedCardComponent, UpiPaymentService };
